import { UIMetadataComplete } from '../../../types/UIMetadata';

/**
 * Silverado Configurator (linkId 2371).
 *
 * UI half of the 2.0 metadata pair — every key mirrors a configurationId in
 * ./graphics-metadata.ts. Converted from the 1.0 repo's
 * `src/metadata/data/metadata-2371.ts`; shared UI boilerplate
 * (staticTranslations / saveFormConfig) follows the 2378 project.
 *
 * Conversion notes:
 *  - 1.0 `removeAccessories` + `defaultAccessory` become a shared graphics
 *    `groupId` + `isDefault` (2.0's single-select mechanism), namespaced per
 *    trim because the SDK resolves groupIds model-wide.
 *  - 1.0 `forColor` (allow-list of colour indices) is inverted into 2.0's
 *    `notAvailableForColor` deny-list of colour ids.
 *  - `interactionApplyMessage`/`applyInteractions` become
 *    `affectedInteractionOnApply`; `includedPackages` become
 *    `dependencyPackages`; `brandItemCode` becomes `autoIntelRPOCode`.
 *  - Dropped, with no 2.0 equivalent: `video` Welcome Animation interactions,
 *    feature `tempSkuToLoad`/`replaceAsset`, `interactionRemoveMessage`,
 *    interaction `meshAnimation`/`glowMesh`.
 *  - Broken references in the 1.0 source (dangling ids, duplicate accessory
 *    ids, identifiers naming assets the variant never loads) were repaired —
 *    see conversion-report-2371.txt.
 *
 * Assets live in public/assets/{images,videos}/2371/.
 */
// Cast through unknown to allow additional model-level UI fields (e.g., name, specifications)
export const uimetadata = {
  models: {
    Silverado: {
      variants: {
        'high-country-2025': {
          configurationId: 'd5tbkkfd7k97',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Regular Cab',
          name: {
            en: '2025 High Country',
            ar: '2025 High Country'
          },
          description: {
            en: '',
            ar: ''
          },
          drivetrain: '',
          trim: 'High Country',
          modelyear: '2025',
          image: 'images/2371/variants/HC_Radiant-Red-Tintcoat.png',
          price: {
            KSA: 265353,
            UAE: 260400,
            OMR: 27597,
            BHD: 26889,
            QAR: 257570,
            JOD: 50240,
            KW: 21936,
            IQD: 70761,
            LBP: 70761,
            default: 260400,
          },
          basePrice: {
            UAE: 260400,
            KSA: 294300,
            KW: 20400,
            OMR: 25700,
            JOD: 82600,
            QAR: 243000,
            BHD: 26740,
            IQD: 92908844,
            LBP: 6348711976,
            default: 260400
          },
          index: 0,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '3LZ',
          brandItemCode: 'CHSL09',
        },
        'zr2-2025': {
          configurationId: 'nfy6bsvabnu2',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Regular Cab',
          name: {
            en: '2025 ZR2',
            ar: '2025 ZR2'
          },
          description: {
            en: '',
            ar: ''
          },
          drivetrain: '',
          trim: 'ZR2',
          modelyear: '2025',
          image: 'images/2371/variants/ZR2_Cypress-Gray.png',
          price: {
            KSA: 255673,
            UAE: 250900,
            OMR: 26590,
            BHD: 25908,
            QAR: 248173,
            JOD: 48407,
            KW: 21136,
            IQD: 68179,
            LBP: 68179,
            default: 250900,
          },
          basePrice: {
            UAE: 250900,
            KSA: 284000,
            KW: 19600,
            OMR: 24800,
            JOD: 79400,
            QAR: 234000,
            BHD: 25764,
            IQD: 89519313,
            LBP: 6117096140,
            default: 250900
          },
          index: 1,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '3LT',
          brandItemCode: 'CHSL08',
        },
        'trail-boss-crew-cab-2025': {
          configurationId: 'sx6g1pcz1wtu',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Crew Cab',
          name: {
            en: '2025 Trail Boss Crew Cab',
            ar: '2025 Trail Boss Crew Cab'
          },
          description: {
            en: '',
            ar: ''
          },
          drivetrain: '',
          trim: 'Trail Boss Crew Cab',
          modelyear: '2025',
          image: 'images/2371/variants/TRAILBOSS_CC_Riptide-Blue-Metallic.png',
          price: {
            KSA: 211753,
            UAE: 207800,
            OMR: 22022,
            BHD: 21458,
            QAR: 205541,
            JOD: 40092,
            KW: 17505,
            IQD: 56467,
            LBP: 56467,
            default: 207800,
          },
          basePrice: {
            UAE: 207800,
            KSA: 235300,
            KW: 16200,
            OMR: 18500,
            JOD: 58600,
            QAR: 243000,
            BHD: 21338,
            IQD: 74141544,
            LBP: 5066291662,
            default: 207800
          },
          index: 2,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '2LT',
          brandItemCode: 'CHSL07',
        },
        'trail-boss-2025': {
          configurationId: '40idzo3up99n',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Regular Cab',
          // No second-row hotspot: this is a Regular Cab configuration (1.0's
          // 3dhotspots for this variant carries only 'First Row Seat', unlike its
          // Crew Cab sibling which has both) - 2.0's hotspots are model-level, not
          // per-variant, so the Interior tab's seat dropdown is filtered against
          // this list in Viewport.tsx instead.
          hiddenHotspots: ['interiorCamera_1'],
          name: {
            en: '2025 Trail Boss',
            ar: '2025 Trail Boss'
          },
          description: {
            en: '',
            ar: ''
          },
          drivetrain: '',
          trim: 'Trail Boss',
          modelyear: '2025',
          image: 'images/2371/variants/TRAILBOSS_RC_Iridescent-Pearl-Tricoat.png',
          price: {
            KSA: 193614,
            UAE: 190000,
            OMR: 20136,
            BHD: 19620,
            QAR: 187935,
            JOD: 36658,
            KW: 16005,
            IQD: 51630,
            LBP: 51630,
            default: 190000,
          },
          basePrice: {
            UAE: 190000,
            KSA: 212400,
            KW: 14600,
            JOD: 58600,
            QAR: 243000,
            OMR: 18500,
            BHD: 19510,
            IQD: 67790632,
            LBP: 4632316726,
            default: 190000
          },
          index: 3,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '2LT',
          brandItemCode: 'CHSL06',
        },
        'rst-crew-cab-2025': {
          configurationId: '0w61ysfcblqg',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Crew Cab',
          name: {
            en: '2025 RST Crew Cab',
            ar: '2025 RST Crew Cab'
          },
          description: {
            en: '',
            ar: ''
          },
          drivetrain: '',
          trim: 'RST Crew Cab',
          modelyear: '2025',
          image: 'images/2371/variants/RST_CC_Slate-Gray-Metallic.png',
          price: {
            KSA: 206861,
            UAE: 203000,
            OMR: 21514,
            BHD: 20962,
            QAR: 200793,
            JOD: 39166,
            KW: 17101,
            IQD: 55163,
            LBP: 55163,
            default: 203000,
          },
          basePrice: {
            UAE: 203000,
            KSA: 230400,
            KW: 15800,
            OMR: 17200,
            JOD: 54300,
            QAR: 161900,
            BHD: 20845,
            IQD: 72428938,
            LBP: 4949264713,
            default: 203000
          },
          index: 4,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1SP',
          brandItemCode: 'CHSL05',
        },
        'rst-2025': {
          configurationId: 'n4m6265ils6k',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Regular Cab',
          // No second-row hotspot: this is a Regular Cab configuration (1.0's
          // 3dhotspots for this variant carries only 'First Row Seat', unlike its
          // Crew Cab sibling which has both) - 2.0's hotspots are model-level, not
          // per-variant, so the Interior tab's seat dropdown is filtered against
          // this list in Viewport.tsx instead.
          hiddenHotspots: ['interiorCamera_1'],
          name: {
            en: '2025 RST',
            ar: '2025 RST'
          },
          description: {
            en: '',
            ar: ''
          },
          drivetrain: '',
          trim: 'RST',
          modelyear: '2025',
          image: 'images/2371/variants/RST_RC_Summit-White.png',
          price: {
            KSA: 177615,
            UAE: 174300,
            OMR: 18472,
            BHD: 17998,
            QAR: 172405,
            JOD: 33629,
            KW: 14683,
            IQD: 47364,
            LBP: 47364,
            default: 174300,
          },
          basePrice: {
            UAE: 174300,
            KSA: 197400,
            KW: 13600,
            JOD: 54300,
            OMR: 17200,
            QAR: 161900,
            BHD: 17898,
            IQD: 62188985,
            LBP: 4249541081,
            default: 174300
          },
          index: 5,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1SP',
          brandItemCode: 'CHSL04',
        },
        'lt-crew-cab-4wd-2025': {
          configurationId: 'atjkqi6ciumj',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Crew Cab',
          name: {
            en: '2025 LT Crew Cab',
            ar: '2025 LT Crew Cab'
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي'
          },
          drivetrain: '4WD',
          trim: 'LT Crew Cab',
          modelyear: '2025',
          image: 'images/2371/variants/LT_CC_Red-Hot.png',
          price: {
            KSA: 205129,
            UAE: 201300,
            OMR: 21333,
            BHD: 20786,
            QAR: 199112,
            JOD: 38838,
            KW: 16957,
            JID: 50500,
            IQD: 54701,
            LBP: 54701,
            default: 201300,
          },
          basePrice: {
            UAE: 201300,
            KSA: 228600,
            KW: 15700,
            JOD: 50500,
            OMR: 16000,
            QAR: 151000,
            BHD: 20670,
            IQD: 71822390,
            LBP: 4907817669,
            default: 201300
          },
          index: 6,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LT',
          brandItemCode: 'CHSL03',
        },
        'lt-4wd-2025': {
          configurationId: 'u5qm0qtkgk60',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Regular Cab',
          // No second-row hotspot: this is a Regular Cab configuration (1.0's
          // 3dhotspots for this variant carries only 'First Row Seat', unlike its
          // Crew Cab sibling which has both) - 2.0's hotspots are model-level, not
          // per-variant, so the Interior tab's seat dropdown is filtered against
          // this list in Viewport.tsx instead.
          hiddenHotspots: ['interiorCamera_1'],
          name: {
            en: '2025 LT',
            ar: '2025 LT'
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي'
          },
          drivetrain: '4WD',
          trim: 'LT',
          modelyear: '2025',
          image: 'images/2371/variants/LT_RC_Black.png',
          price: {
            KSA: 165795,
            UAE: 162700,
            OMR: 17243,
            BHD: 16801,
            QAR: 160932,
            JOD: 31390,
            KW: 13706,
            IQD: 44212,
            LBP: 44212,
            default: 162700,
          },
          basePrice: {
            UAE: 162700,
            KSA: 184200,
            KW: 12300,
            OMR: 16000,
            JOD: 50500,
            QAR: 151000,
            BHD: 16707,
            IQD: 58050188,
            LBP: 3966725955,
            default: 162700
          },
          index: 7,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LT',
          brandItemCode: 'CHSL02',
        },
        'lt-rwd-2025': {
          configurationId: '2ls0cq83nlcs',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Regular Cab',
          // No second-row hotspot: this is a Regular Cab configuration (1.0's
          // 3dhotspots for this variant carries only 'First Row Seat', unlike its
          // Crew Cab sibling which has both) - 2.0's hotspots are model-level, not
          // per-variant, so the Interior tab's seat dropdown is filtered against
          // this list in Viewport.tsx instead.
          hiddenHotspots: ['interiorCamera_1'],
          name: {
            en: '2025 LT',
            ar: '2025 LT'
          },
          description: {
            en: 'RWD',
            ar: 'دفع خلفي'
          },
          drivetrain: 'RWD',
          trim: 'LT',
          modelyear: '2025',
          image: 'images/2371/variants/LT_RC_Black.png',
          price: {
            KSA: 179857,
            UAE: 176500,
            OMR: 18705,
            BHD: 18226,
            QAR: 174582,
            JOD: 34053,
            KW: 14868,
            IQD: 47962,
            LBP: 47962,
            default: 176500,
          },
          basePrice: {
            UAE: 176500,
            KSA: 199900,
            KW: 13400,
            JOD: 50500,
            OMR: 16000,
            QAR: 151000,
            BHD: 18124,
            IQD: 62973929,
            LBP: 4303178433,
            default: 176500
          },
          index: 8,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LT',
          brandItemCode: 'CHSL01',
        },
        'high-country-2026': {
          configurationId: 'd5dtbkkfd7k97',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Regular Cab',
          name: {
            en: '2026 High Country',
            ar: '2026 High Country'
          },
          description: {
            en: '',
            ar: ''
          },
          drivetrain: '',
          trim: 'High Country',
          modelyear: '2026',
          image: 'images/2371/variants/HC_Radiant-Red-Tintcoat.png',
          price: {
            KSA: 265353,
            UAE: 260400,
            OMR: 27597,
            BHD: 26889,
            QAR: 257570,
            JOD: 50240,
            KW: 21936,
            IQD: 70761,
            LBP: 70761,
            default: 260400,
          },
          basePrice: {
            UAE: 260400,
            KSA: 294300,
            KW: 20400,
            OMR: 25700,
            JOD: 82600,
            QAR: 243000,
            BHD: 26740,
            IQD: 92908844,
            LBP: 6348711976,
            default: 260400
          },
          index: 9,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '3LZ',
          brandItemCode: 'CHSL09',
        },
        'zr2-2026': {
          configurationId: 'nfdy6bsvabnu2',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Regular Cab',
          name: {
            en: '2026 ZR2',
            ar: '2026 ZR2'
          },
          description: {
            en: '',
            ar: ''
          },
          drivetrain: '',
          trim: 'ZR2',
          modelyear: '2026',
          image: 'images/2371/variants/ZR2_Cypress-Gray.png',
          price: {
            KSA: 255673,
            UAE: 250900,
            OMR: 26590,
            BHD: 25908,
            QAR: 248173,
            JOD: 48407,
            KW: 21136,
            IQD: 68179,
            LBP: 68179,
            default: 250900,
          },
          basePrice: {
            UAE: 250900,
            KSA: 284000,
            KW: 19600,
            OMR: 24800,
            JOD: 79400,
            QAR: 234000,
            BHD: 25764,
            IQD: 89519313,
            LBP: 6117096140,
            default: 250900
          },
          index: 10,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '3LT',
          brandItemCode: 'CHSL08',
        },
        'trail-boss-crew-cab-2026': {
          configurationId: 'sx6g1pcz1wtu12',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Crew Cab',
          name: {
            en: '2026 Trail Boss Crew Cab',
            ar: '2026 Trail Boss Crew Cab'
          },
          description: {
            en: '',
            ar: ''
          },
          drivetrain: '',
          trim: 'Trail Boss Crew Cab',
          modelyear: '2026',
          image: 'images/2371/variants/TRAILBOSS_CC_Riptide-Blue-Metallic.png',
          price: {
            KSA: 211753,
            UAE: 207800,
            OMR: 22022,
            BHD: 21458,
            QAR: 205541,
            JOD: 40092,
            KW: 17505,
            IQD: 56467,
            LBP: 56467,
            default: 207800,
          },
          basePrice: {
            UAE: 207800,
            KSA: 235300,
            KW: 16200,
            JOD: 58600,
            QAR: 243000,
            OMR: 18500,
            BHD: 21338,
            IQD: 74141544,
            LBP: 5066291662,
            default: 207800
          },
          index: 11,
          isDefault: true,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '2LT',
          brandItemCode: 'CHSL07',
        },
        'trail-boss-2026': {
          configurationId: '40ildzo3up99n',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Regular Cab',
          // No second-row hotspot: this is a Regular Cab configuration (1.0's
          // 3dhotspots for this variant carries only 'First Row Seat', unlike its
          // Crew Cab sibling which has both) - 2.0's hotspots are model-level, not
          // per-variant, so the Interior tab's seat dropdown is filtered against
          // this list in Viewport.tsx instead.
          hiddenHotspots: ['interiorCamera_1'],
          name: {
            en: '2026 Trail Boss',
            ar: '2026 Trail Boss'
          },
          description: {
            en: '',
            ar: ''
          },
          drivetrain: '',
          trim: 'Trail Boss',
          modelyear: '2026',
          image: 'images/2371/variants/TRAILBOSS_RC_Iridescent-Pearl-Tricoat.png',
          price: {
            KSA: 193614,
            UAE: 190000,
            OMR: 20136,
            BHD: 19620,
            QAR: 187935,
            JOD: 36658,
            KW: 16005,
            IQD: 51630,
            LBP: 51630,
            default: 190000,
          },
          basePrice: {
            UAE: 190000,
            KSA: 212400,
            KW: 14600,
            JOD: 58600,
            QAR: 243000,
            OMR: 18500,
            BHD: 19510,
            IQD: 67790632,
            LBP: 4632316726,
            default: 190000
          },
          index: 12,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '2LT',
          brandItemCode: 'CHSL06',
        },
        'rst-crew-cab-2026': {
          configurationId: '0w61ysggfcblqg',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Crew Cab',
          name: {
            en: '2026 RST Crew Cab',
            ar: '2026 RST Crew Cab'
          },
          description: {
            en: '',
            ar: ''
          },
          drivetrain: '',
          trim: 'RST Crew Cab',
          modelyear: '2026',
          image: 'images/2371/variants/RST_CC_Slate-Gray-Metallic.png',
          price: {
            KSA: 206861,
            UAE: 203000,
            OMR: 21514,
            BHD: 20962,
            QAR: 200793,
            JOD: 39166,
            KW: 17101,
            IQD: 55163,
            LBP: 55163,
            default: 203000,
          },
          basePrice: {
            UAE: 203000,
            KSA: 230400,
            KW: 15800,
            JOD: 54300,
            QAR: 161900,
            BHD: 20845,
            OMR: 17200,
            IQD: 72428938,
            LBP: 4949264713,
            default: 203000
          },
          index: 13,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1SP',
          brandItemCode: 'CHSL05',
        },
        'rst-2026': {
          configurationId: 'n4m6mk265ils6k',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Regular Cab',
          // No second-row hotspot: this is a Regular Cab configuration (1.0's
          // 3dhotspots for this variant carries only 'First Row Seat', unlike its
          // Crew Cab sibling which has both) - 2.0's hotspots are model-level, not
          // per-variant, so the Interior tab's seat dropdown is filtered against
          // this list in Viewport.tsx instead.
          hiddenHotspots: ['interiorCamera_1'],
          name: {
            en: '2026 RST',
            ar: '2026 RST'
          },
          description: {
            en: '',
            ar: ''
          },
          drivetrain: '',
          trim: 'RST',
          modelyear: '2026',
          image: 'images/2371/variants/RST_RC_Summit-White.png',
          price: {
            KSA: 177615,
            UAE: 174300,
            OMR: 18472,
            BHD: 17998,
            QAR: 172405,
            JOD: 33629,
            KW: 14683,
            IQD: 47364,
            LBP: 47364,
            default: 174300,
          },
          basePrice: {
            UAE: 174300,
            KSA: 197400,
            KW: 13600,
            JOD: 54300,
            QAR: 161900,
            OMR: 17200,
            BHD: 17898,
            IQD: 62188985,
            LBP: 4249541081,
            default: 174300
          },
          index: 14,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1SP',
          brandItemCode: 'CHSL04',
        },
        'lt-crew-cab-4wd-2026': {
          configurationId: 'atjkqi6cium1',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Crew Cab',
          name: {
            en: '2026 LT Crew Cab',
            ar: '2026 LT Crew Cab'
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي'
          },
          drivetrain: '4WD',
          trim: 'LT Crew Cab',
          modelyear: '2026',
          image: 'images/2371/variants/LT_CC_Red-Hot.png',
          price: {
            KSA: 205129,
            UAE: 201300,
            OMR: 21333,
            BHD: 20786,
            QAR: 199112,
            JOD: 38838,
            KW: 16957,
            IQD: 54701,
            LBP: 54701,
            default: 201300,
          },
          basePrice: {
            UAE: 201300,
            KSA: 228600,
            KW: 15700,
            JOD: 50500,
            OMR: 16000,
            QAR: 151000,
            BHD: 20670,
            IQD: 71822390,
            LBP: 4907817669,
            default: 201300
          },
          index: 15,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LT',
          brandItemCode: 'CHSL03',
        },
        'lt-4wd-2026': {
          configurationId: 'u5qm0qrrtkgk60',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Regular Cab',
          // No second-row hotspot: this is a Regular Cab configuration (1.0's
          // 3dhotspots for this variant carries only 'First Row Seat', unlike its
          // Crew Cab sibling which has both) - 2.0's hotspots are model-level, not
          // per-variant, so the Interior tab's seat dropdown is filtered against
          // this list in Viewport.tsx instead.
          hiddenHotspots: ['interiorCamera_1'],
          name: {
            en: '2026 LT',
            ar: '2026 LT'
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي'
          },
          drivetrain: '4WD',
          trim: 'LT',
          modelyear: '2026',
          image: 'images/2371/variants/LT_RC_Black.png',
          price: {
            KSA: 165795,
            UAE: 162700,
            OMR: 17243,
            BHD: 16801,
            QAR: 160932,
            JOD: 31390,
            KW: 13706,
            IQD: 44212,
            LBP: 44212,
            default: 162700,
          },
          basePrice: {
            UAE: 162700,
            KSA: 184200,
            KW: 12300,
            OMR: 16000,
            JOD: 50500,
            QAR: 151000,
            BHD: 16707,
            IQD: 58050188,
            LBP: 3966725955,
            default: 162700
          },
          index: 16,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LT',
          brandItemCode: 'CHSL02',
        },
        'lt-rwd-2026': {
          configurationId: '2ls0cllq83nlcs',
          // Matches 1.0's per-option `parent: 'engine'` on this same Cab Type
          // filter (metadata-2371.ts ~line 73065) - 1.0's filter keys off `engine`,
          // not the top-level `key: 'variant'` the filter object itself carries.
          engine: 'Regular Cab',
          // No second-row hotspot: this is a Regular Cab configuration (1.0's
          // 3dhotspots for this variant carries only 'First Row Seat', unlike its
          // Crew Cab sibling which has both) - 2.0's hotspots are model-level, not
          // per-variant, so the Interior tab's seat dropdown is filtered against
          // this list in Viewport.tsx instead.
          hiddenHotspots: ['interiorCamera_1'],
          name: {
            en: '2026 LT',
            ar: '2026 LT'
          },
          description: {
            en: 'RWD',
            ar: 'دفع خلفي'
          },
          drivetrain: 'RWD',
          trim: 'LT',
          modelyear: '2026',
          image: 'images/2371/variants/LT_RC_Black.png',
          price: {
            KSA: 179857,
            UAE: 176500,
            OMR: 18705,
            BHD: 18226,
            QAR: 174582,
            JOD: 34053,
            KW: 14868,
            omr: 16000,
            IQD: 47962,
            LBP: 47962,
            default: 176500,
          },
          basePrice: {
            UAE: 176500,
            KSA: 199900,
            KW: 13400,
            omr: 16000,
            JOD: 50500,
            QAR: 151000,
            BHD: 18124,
            IQD: 62973929,
            LBP: 4303178433,
            default: 176500
          },
          index: 17,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LT',
          brandItemCode: 'CHSL01',
        }
      },
      backgrounds: {
        g0ZA968_RpUd: {
          image: 'images/2371/HDRI/desert_1.png',
          title: {
            en: 'Desert Landscape 1',
            ar: 'المناظر الطبيعية الصحراوية 1'
          }
        },
        g0ZA968_RpUe: {
          image: 'images/2371/HDRI/desert_2.png',
          title: {
            en: 'Desert Landscape 2',
            ar: 'المناظر الطبيعية الصحراوية 2'
          }
        }
      },
      ambientLighting: {},
      colors: {
        interior: {
          tarm9hwgcy55: {
            label: {
              en: 'Jet Black - Leather',
              ar: 'جلد أسود'
            },
            image: 'images/2371/interiors/HC_int_color.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2371/interiors/HC_int_color.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'HXC',
            brandItemCode: 'IN16',
          },
          wolmccxt5hjd: {
            label: {
              en: 'Jet Black/ Greystone Leather',
              ar: 'جلد أسود/رمادي'
            },
            image: 'images/2371/interiors/ZR2_int_color.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2371/interiors/ZR2_int_color.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'H37',
            brandItemCode: 'IN19',
          },
          vinl6onp0hdu: {
            label: {
              en: 'Gideon Cloth',
              ar: 'قماش باللون البني'
            },
            image: 'images/2371/interiors/official/Gideon-Cloth.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2371/interiors/official/Gideon-Cloth.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'HV5',
            brandItemCode: 'IN20',
          },
          he9ofys2sliq: {
            label: {
              en: 'Gideon Leather',
              ar: 'جلد باللون البني'
            },
            image: 'images/2371/interiors/official/Gideon-Leather.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2371/interiors/official/Gideon-Leather.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'HVE',
            brandItemCode: 'IN21',
          },
          nhbc3d6y68kz: {
            label: {
              en: 'Gideon Cloth',
              ar: 'قماش باللون البني'
            },
            image: 'images/2371/interiors/official/Gideon-Cloth.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2371/interiors/official/Gideon-Cloth.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'HV5',
            brandItemCode: 'IN20',
          },
          wsycdik891e5: {
            label: {
              en: 'Gideon Leather',
              ar: 'جلد باللون البني'
            },
            image: 'images/2371/interiors/official/Gideon-Leather.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2371/interiors/official/Gideon-Leather.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'HVE',
            brandItemCode: 'IN21',
          },
          '73c9e3m5u13v': {
            label: {
              en: 'Gideon Cloth',
              ar: 'قماش باللون البني'
            },
            image: 'images/2371/interiors/official/Gideon-Cloth.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2371/interiors/official/Gideon-Cloth.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'HV5',
            brandItemCode: 'IN20',
          },
          '8f1wuj26aml3': {
            label: {
              en: 'Gideon Leather',
              ar: 'جلد باللون البني'
            },
            image: 'images/2371/interiors/official/Gideon-Leather.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2371/interiors/official/Gideon-Leather.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'HVE',
            brandItemCode: 'IN21',
          },
          '5ntegqogzttd': {
            label: {
              en: 'Gideon Cloth',
              ar: 'قماش باللون البني'
            },
            image: 'images/2371/interiors/official/Gideon-Cloth.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2371/interiors/official/Gideon-Cloth.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'HV5',
            brandItemCode: 'IN20',
          },
          ghaqazjmli4q: {
            label: {
              en: 'Gideon Leather',
              ar: 'جلد باللون البني'
            },
            image: 'images/2371/interiors/official/Gideon-Leather.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2371/interiors/official/Gideon-Leather.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'HVE',
            brandItemCode: 'IN21',
          },
          '1z0i3csgxouu': {
            label: {
              en: 'Gideon Cloth',
              ar: 'قماش باللون البني'
            },
            image: 'images/2371/interiors/official/Gideon-Cloth.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2371/interiors/official/Gideon-Cloth.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'HV5',
            brandItemCode: 'IN20',
          },
          '93wy3qilqcvm': {
            label: {
              en: 'Gideon Leather',
              ar: 'جلد باللون البني'
            },
            image: 'images/2371/interiors/official/Gideon-Leather.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2371/interiors/official/Gideon-Leather.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'HVE',
            brandItemCode: 'IN21',
          },
          a08fde58dlwj: {
            label: {
              en: 'Gideon Cloth',
              ar: 'قماش باللون البني'
            },
            image: 'images/2371/interiors/official/Gideon-Cloth.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2371/interiors/official/Gideon-Cloth.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'HV5',
            brandItemCode: 'IN20',
          },
          f6cooxj7w8ba: {
            label: {
              en: 'Gideon Cloth',
              ar: 'قماش باللون البني'
            },
            image: 'images/2371/interiors/official/Gideon-Cloth.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2371/interiors/official/Gideon-Cloth.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'HV5',
            brandItemCode: 'IN20',
          }
        },
        exterior: {
          '5c8ep1iwg2jn': {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2371/ext_colors/official/Black.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          sn7yhhtw36la: {
            label: {
              en: 'Iridescent Pearl Tricoat',
              ar: 'لؤلؤي ثلاثي الطبقات'
            },
            image: 'images/2371/ext_colors/official/Iridescent-Pearl-Tricoat.jpg',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Iridescent Pearl Tricoat',
                  ar: 'لؤلؤي ثلاثي الطبقات'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Iridescent-Pearl-Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G1W',
            brandItemCode: 'EX10',
          },
          'sn7yhhtw36la-my26': {
            label: {
              en: 'Polar White Tricoat',
              ar: 'أبيض ثلاثي الطبقات'
            },
            image: 'images/2371/ext_colors/official/Polar-White-Tricoat.jpg',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Polar White Tricoat',
                  ar: 'أبيض ثلاثي الطبقات'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Polar-White-Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX29',
          },
          fa1g6h4tgwqj: {
            label: {
              en: 'Sterling Gray Metallic',
              ar: 'رمادي معدني'
            },
            image: 'images/2371/ext_colors/official/Sterling-Gray-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray Metallic',
                  ar: 'رمادي معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Sterling-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX05',
          },
          nwljddrqlbg2: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر داكن'
            },
            image: 'images/2371/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red Tintcoat',
                  ar: 'أحمر داكن'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          '1vsvpj5pgkpw': {
            label: {
              en: 'Dark Ash Metallic',
              ar: 'رمادي داكن معدني'
            },
            image: 'images/2371/ext_colors/official/Dark-Ash-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Dark Ash Metallic',
                  ar: 'رمادي داكن معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 7,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Dark-Ash-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX11',
          },
          g6y3sjg30waa: {
            label: {
              en: 'Red Hot',
              ar: 'أحمر'
            },
            image: 'images/2371/ext_colors/official/Red-Hot.jpg',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Red Hot',
                  ar: 'أحمر'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Red-Hot.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G7C',
            brandItemCode: 'EX31',
          },
          isfjjq0k49rf: {
            label: {
              en: 'Summit White',
              ar: 'أبيض'
            },
            image: 'images/2371/ext_colors/official/Summit-White.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'أبيض'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Summit-White.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          '9i25yo15msgi': {
            label: {
              en: 'Slate Gray Metallic',
              ar: 'رمادي أردوازي معدني'
            },
            image: 'images/2371/ext_colors/official/Slate-Gray-Metallic.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Slate Gray Metallic',
                  ar: 'رمادي أردوازي معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Slate-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNO',
            brandItemCode: 'EX30',
          },
          '9i25yo15msgi-my26': {
            label: {
              en: 'White Sands',
              ar: 'أبيض رملي'
            },
            image: 'images/2371/ext_colors/official/White-Sands.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'White Sands',
                  ar: 'أبيض رملي'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/White-Sands.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G42',
            brandItemCode: 'EX32',
          },
          '8keam23mnq98': {
            label: {
              en: 'Riptide Blue Metallic',
              ar: 'أزرق معدني'
            },
            image: 'images/2371/ext_colors/official/Riptide-Blue-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Riptide Blue Metallic',
                  ar: 'أزرق معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Riptide-Blue-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJV',
            brandItemCode: 'EX04',
          },
          t5iz0qtjiu66: {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2371/ext_colors/official/Black.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          kj3h63dvah5v: {
            label: {
              en: 'Cypress Gray',
              ar: 'رمادي'
            },
            image: 'images/2371/ext_colors/official/Cypress-Gray.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Cypress Gray',
                  ar: 'رمادي'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 6,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Cypress-Gray.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBD',
            brandItemCode: 'EX12',
          },
          v6j91ri0v3fw: {
            label: {
              en: 'Summit White',
              ar: 'أبيض'
            },
            image: 'images/2371/ext_colors/official/Summit-White.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'أبيض'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Summit-White.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          '5t6ycv9d6u2a': {
            label: {
              en: 'Sterling Gray Metallic',
              ar: 'رمادي معدني'
            },
            image: 'images/2371/ext_colors/official/Sterling-Gray-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray Metallic',
                  ar: 'رمادي معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Sterling-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX05',
          },
          pu8ndfz3yuww: {
            label: {
              en: 'Riptide Blue Metallic',
              ar: 'أزرق معدني'
            },
            image: 'images/2371/ext_colors/official/Riptide-Blue-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Riptide Blue Metallic',
                  ar: 'أزرق معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Riptide-Blue-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJV',
            brandItemCode: 'EX04',
          },
          anuclcfy65hu: {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2371/ext_colors/official/Black.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          hvd0le9te38d: {
            label: {
              en: 'Cypress Gray',
              ar: 'رمادي'
            },
            image: 'images/2371/ext_colors/official/Cypress-Gray.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Cypress Gray',
                  ar: 'رمادي'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Cypress-Gray.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBD',
            brandItemCode: 'EX12',
          },
          b1u0be790w3j: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر داكن'
            },
            image: 'images/2371/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red Tintcoat',
                  ar: 'أحمر داكن'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 6,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          uwetia14dlwx: {
            label: {
              en: 'Summit White',
              ar: 'أبيض'
            },
            image: 'images/2371/ext_colors/official/Summit-White.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'أبيض'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Summit-White.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          cyxr36xkp8lj: {
            label: {
              en: 'Sterling Gray Metallic',
              ar: 'رمادي معدني'
            },
            image: 'images/2371/ext_colors/official/Sterling-Gray-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray Metallic',
                  ar: 'رمادي معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Sterling-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX05',
          },
          ss2t8loc0zuj: {
            label: {
              en: 'Riptide Blue Metallic',
              ar: 'أزرق معدني'
            },
            image: 'images/2371/ext_colors/official/Riptide-Blue-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Riptide Blue Metallic',
                  ar: 'أزرق معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Riptide-Blue-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJV',
            brandItemCode: 'EX04',
          },
          ewtu4bd3e2lk: {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2371/ext_colors/official/Black.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          ew6nnknehod2: {
            label: {
              en: 'Cypress Gray',
              ar: 'رمادي'
            },
            image: 'images/2371/ext_colors/official/Cypress-Gray.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Cypress Gray',
                  ar: 'رمادي'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Cypress-Gray.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBD',
            brandItemCode: 'EX12',
          },
          '6dyaosyo71jo': {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر داكن'
            },
            image: 'images/2371/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red Tintcoat',
                  ar: 'أحمر داكن'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 6,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          '42unzt5oris8': {
            label: {
              en: 'Red Hot',
              ar: 'أحمر'
            },
            image: 'images/2371/ext_colors/official/Red-Hot.jpg',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Red Hot',
                  ar: 'أحمر'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Red-Hot.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G7C',
            brandItemCode: 'EX31',
          },
          '42unzt5oris8-my26': {
            label: {
              en: 'White Sands',
              ar: 'أبيض رملي'
            },
            image: 'images/2371/ext_colors/official/White-Sands.jpg',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'White Sands',
                  ar: 'أبيض رملي'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/White-Sands.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G42',
            brandItemCode: 'EX32',
          },
          vm7p16mew3v8: {
            label: {
              en: 'Summit White',
              ar: 'أبيض'
            },
            image: 'images/2371/ext_colors/official/Summit-White.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'أبيض'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Summit-White.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          '28kpjqnxlj1g': {
            label: {
              en: 'Sterling Gray Metallic',
              ar: 'رمادي معدني'
            },
            image: 'images/2371/ext_colors/official/Sterling-Gray-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray Metallic',
                  ar: 'رمادي معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Sterling-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX05',
          },
          '6yngtuuz7ba5': {
            label: {
              en: 'Riptide Blue Metallic',
              ar: 'أزرق معدني'
            },
            image: 'images/2371/ext_colors/official/Riptide-Blue-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Riptide Blue Metallic',
                  ar: 'أزرق معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Riptide-Blue-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJV',
            brandItemCode: 'EX04',
          },
          tikeuy98bjmx: {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2371/ext_colors/official/Black.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          g59lqemyqzko: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر داكن'
            },
            image: 'images/2371/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red Tintcoat',
                  ar: 'أحمر داكن'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          g7u1kp5phj7t: {
            label: {
              en: 'Summit White',
              ar: 'أبيض'
            },
            image: 'images/2371/ext_colors/official/Summit-White.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'أبيض'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Summit-White.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          vpmrwig8eaal: {
            label: {
              en: 'Red Hot',
              ar: 'أحمر'
            },
            image: 'images/2371/ext_colors/official/Red-Hot.jpg',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Red Hot',
                  ar: 'أحمر'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Red-Hot.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G7C',
            brandItemCode: 'EX31',
          },
          'vpmrwig8eaal-my26': {
            label: {
              en: 'White Sands',
              ar: 'أبيض رملي'
            },
            image: 'images/2371/ext_colors/official/White-Sands.jpg',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'White Sands',
                  ar: 'أبيض رملي'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/White-Sands.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G42',
            brandItemCode: 'EX32',
          },
          triu9xb1d3yc: {
            label: {
              en: 'Sterling Gray Metallic',
              ar: 'رمادي معدني'
            },
            image: 'images/2371/ext_colors/official/Sterling-Gray-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray Metallic',
                  ar: 'رمادي معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Sterling-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX05',
          },
          '70r7ym4u3hle': {
            label: {
              en: 'Riptide Blue Metallic',
              ar: 'أزرق معدني'
            },
            image: 'images/2371/ext_colors/official/Riptide-Blue-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Riptide Blue Metallic',
                  ar: 'أزرق معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Riptide-Blue-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJV',
            brandItemCode: 'EX04',
          },
          '8007q43snr2r': {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2371/ext_colors/official/Black.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          qwbx50kclek6: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر داكن'
            },
            image: 'images/2371/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red Tintcoat',
                  ar: 'أحمر داكن'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          g8bcl2qhhvar: {
            label: {
              en: 'Red Hot',
              ar: 'أحمر'
            },
            image: 'images/2371/ext_colors/official/Red-Hot.jpg',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Red Hot',
                  ar: 'أحمر'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Red-Hot.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G7C',
            brandItemCode: 'EX31',
          },
          '2d29kh3p8f8j': {
            label: {
              en: 'Summit White',
              ar: 'أبيض'
            },
            image: 'images/2371/ext_colors/official/Summit-White.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'أبيض'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Summit-White.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          '7h2h723sv74i': {
            label: {
              en: 'Sterling Gray Metallic',
              ar: 'رمادي معدني'
            },
            image: 'images/2371/ext_colors/official/Sterling-Gray-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray Metallic',
                  ar: 'رمادي معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Sterling-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX05',
          },
          ecwytnxf0hxy: {
            label: {
              en: 'Riptide Blue Metallic',
              ar: 'أزرق معدني'
            },
            image: 'images/2371/ext_colors/official/Riptide-Blue-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Riptide Blue Metallic',
                  ar: 'أزرق معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Riptide-Blue-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJV',
            brandItemCode: 'EX04',
          },
          sekelomxqqwv: {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2371/ext_colors/official/Black.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          w5asg0j1keaw: {
            label: {
              en: 'Slate Gray Metallic',
              ar: 'رمادي أردوازي معدني'
            },
            image: 'images/2371/ext_colors/official/Slate-Gray-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Slate Gray Metallic',
                  ar: 'رمادي أردوازي معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 6,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Slate-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNO',
            brandItemCode: 'EX30',
          },
          'w5asg0j1keaw-my26': {
            label: {
              en: 'Cypress Gray',
              ar: 'رمادي'
            },
            image: 'images/2371/ext_colors/official/Cypress-Gray.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Cypress Gray',
                  ar: 'رمادي'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 6,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Cypress-Gray.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBD',
            brandItemCode: 'EX12',
          },
          k550ufl2z63j: {
            label: {
              en: 'Red Hot',
              ar: 'أحمر'
            },
            image: 'images/2371/ext_colors/official/Red-Hot.jpg',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Red Hot',
                  ar: 'أحمر'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Red-Hot.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G7C',
            brandItemCode: 'EX31',
          },
          gxqxsxnaljfj: {
            label: {
              en: 'Summit White',
              ar: 'أبيض'
            },
            image: 'images/2371/ext_colors/official/Summit-White.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'أبيض'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Summit-White.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          l415icrduyfe: {
            label: {
              en: 'Sterling Gray Metallic',
              ar: 'رمادي معدني'
            },
            image: 'images/2371/ext_colors/official/Sterling-Gray-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray Metallic',
                  ar: 'رمادي معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Sterling-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX05',
          },
          t0ah6d0ze4xe: {
            label: {
              en: 'Riptide Blue Metallic',
              ar: 'أزرق معدني'
            },
            image: 'images/2371/ext_colors/official/Riptide-Blue-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Riptide Blue Metallic',
                  ar: 'أزرق معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Riptide-Blue-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJV',
            brandItemCode: 'EX04',
          },
          ew6nnknehod1: {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2371/ext_colors/official/Black.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          ew6nnknehod8: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر داكن'
            },
            image: 'images/2371/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red Tintcoat',
                  ar: 'أحمر داكن'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          'ew6nnknehod8-my26': {
            label: {
              en: 'Cypress Gray',
              ar: 'رمادي'
            },
            image: 'images/2371/ext_colors/official/Cypress-Gray.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Cypress Gray',
                  ar: 'رمادي'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Cypress-Gray.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBD',
            brandItemCode: 'EX12',
          },
          fkyrlr7clfu8: {
            label: {
              en: 'Red Hot',
              ar: 'أحمر'
            },
            image: 'images/2371/ext_colors/official/Red-Hot.jpg',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Red Hot',
                  ar: 'أحمر'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Red-Hot.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G7C',
            brandItemCode: 'EX31',
          },
          g6w0v7586nep: {
            label: {
              en: 'Summit White',
              ar: 'أبيض'
            },
            image: 'images/2371/ext_colors/official/Summit-White.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'أبيض'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Summit-White.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          frv70rwooeqs: {
            label: {
              en: 'Sterling Gray Metallic',
              ar: 'رمادي معدني'
            },
            image: 'images/2371/ext_colors/official/Sterling-Gray-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray Metallic',
                  ar: 'رمادي معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Sterling-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX05',
          },
          hx1107tranuw: {
            label: {
              en: 'Riptide Blue Metallic',
              ar: 'أزرق معدني'
            },
            image: 'images/2371/ext_colors/official/Riptide-Blue-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Riptide Blue Metallic',
                  ar: 'أزرق معدني'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Riptide-Blue-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJV',
            brandItemCode: 'EX04',
          },
          xa5qgss7of58: {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2371/ext_colors/official/Black.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          '6dlgbe69b79c': {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر داكن'
            },
            image: 'images/2371/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red Tintcoat',
                  ar: 'أحمر داكن'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          '6dlgbe69b79c-my26': {
            label: {
              en: 'Cypress Gray',
              ar: 'رمادي'
            },
            image: 'images/2371/ext_colors/official/Cypress-Gray.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Cypress Gray',
                  ar: 'رمادي'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2371/ext_colors/official/Cypress-Gray.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBD',
            brandItemCode: 'EX12',
          },
        }
      },
      features: {
        qlpcncqd64b7: {
          label: {
            en: 'Front Grill',
            ar: 'الشبك الأمامي'
          },
          image: 'images/2371/features/HC/HC_Front Grill.jpg',
          title: {
            en: 'Exclusive Galvano grille with Godric accents',
            ar: 'شبك Galvano الحصري مع لمسات Godric'
          },
          desc: {
            en: 'Exclusive Galvano grille with Godric accents',
            ar: 'شبك Galvano الحصري مع لمسات Godric'
          },
          type: 'exterior'
        },
        kh1xk6tsachr: {
          label: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          image: 'images/2371/features/HC/HC_Headlamp.jpg',
          title: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          desc: {
            en: 'High-intensity LED reflector headlamps with LED curtain DRLs and White tracer animation',
            ar: 'مصابيح أمامية عاكسة LED عالية الكثافة مع أضواء نهارية ستائرية LED ورسوم متحركة بيضاء'
          },
          type: 'exterior'
        },
        '2uhaenqtv8gj': {
          label: {
            en: 'Wheels',
            ar: 'عجلات'
          },
          image: 'images/2371/features/HC/HC_Wheels.jpg',
          title: {
            en: '20-inch machined-aluminum',
            ar: 'عجلات ألومنيوم مشغولة قياس 20 إنش'
          },
          desc: {
            en: '20-inch machined-aluminum',
            ar: 'عجلات ألومنيوم مشغولة قياس 20 إنش'
          },
          type: 'exterior'
        },
        '1slkxz7syxvp': {
          label: {
            en: 'Bedliner',
            ar: 'بطانة الصندوق'
          },
          image: 'images/2371/features/HC/HC_Bed.jpg',
          title: {
            en: 'Bedliner',
            ar: 'بطانة الصندوق'
          },
          desc: {
            en: 'Chevytec Sprayon Bedliner',
            ar: 'بطانة صندوق Chevytec Sprayon'
          },
          type: 'exterior'
        },
        kbns5f4ez323: {
          label: {
            en: 'Side Step',
            ar: 'درجات المساعدة على الصعود والنزول'
          },
          image: 'images/2371/features/HC/HC_Side Step.jpg',
          title: {
            en: 'Side Step',
            ar: 'درجات المساعدة على الصعود والنزول'
          },
          desc: {
            en: 'Power-retractable assist steps',
            ar: 'درجات المساعدة على الصعود والنزول قابلة للسحب كهربائياً'
          },
          type: 'exterior'
        },
        okpcdfyxu7lo: {
          label: {
            en: 'Power Tail gate',
            ar: 'بوابة خلفية تعمل كهربائياً'
          },
          image: 'images/2371/features/HC/HC_Tailgate.jpg',
          title: {
            en: 'Power Tail gate',
            ar: 'بوابة خلفية تعمل كهربائياً'
          },
          desc: {
            en: 'Power up/down tailgate',
            ar: 'بوابة خلفية قابلة للرفع والخفض كهربائياً'
          },
          type: 'exterior'
        },
        wh2pdteldd7b: {
          label: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          image: 'images/2371/features/HC/HC_Info Screen.jpg',
          title: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          desc: {
            en: '13.4-inch diagonal color touchscreen display and Google built-in§ powered by OnStar§, AM/FM stereo, Bluetooth® audio streaming, wireless Apple CarPlay®, and Android Auto™ compatibility, advance voice recognition, In-vehicle apps, personalized profiles for infotainment and vehicle settings',
            ar: 'شاشة لمس ملونة قياس 13.4 إنش مع خدمات جوجل المدمجة§ والمدعومة من اونستار§، ونظام ستيريو AM/FM، وبث صوتي عبر ®Bluetooth، وتوافق لاسلكي مع ®Apple CarPlay و™Android Auto، وتقنية تعرف صوتي متقدمة، وتطبيقات داخل المركبة، وملفات تعرّف شخصية لإعدادات النظام المعلوماتي الترفيهي والمركبة'
          },
          type: 'interior'
        },
        sulp9ugcljsd: {
          label: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          image: 'images/2371/features/HC/HC_Speedometer.jpg',
          title: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          desc: {
            en: 'Enhanced 12.3" diagonal reconfigurable multicolour digital system',
            ar: 'مركز معلومات القيادة المعزز بشاشة عرض متعددة الألوان قابلة للتخصيص قياس 12.3 إنش'
          },
          type: 'interior'
        },
        '14ojjvbzhg1q': {
          label: {
            en: 'Sound System',
            ar: 'نظام الصوت'
          },
          image: 'images/2371/features/HC/HC_Speaker.jpg',
          title: {
            en: 'Sound System',
            ar: 'نظام الصوت'
          },
          desc: {
            en: 'BOSE® premium 7 Seater sound system with Richbass woofer',
            ar: 'نظام ®Bose الصوتي الفاخر بـ7 مكبرات صوت مع مكبر صوت Richbass'
          },
          type: 'interior'
        },
        '1i87rvwcyk2a': {
          label: {
            en: 'Jet Black Interior',
            ar: 'مقصورة باللون الأسود'
          },
          image: 'images/2371/features/HC/HC_Seats.jpg',
          title: {
            en: 'Jet Black Interior',
            ar: 'مقصورة باللون الأسود'
          },
          desc: {
            en: 'Jet Black Interior Color with leather seating surfaces with exclusive custom perforation and stitching pattern',
            ar: 'مقصورة باللون الأسود مع أسطح مقاعد من الجلد المثقب بشكل حصري وخياطة مميّزة'
          },
          type: 'interior'
        },
        xje8k6tk6c08: {
          label: {
            en: 'Wireless Charging',
            ar: 'الشحن اللاسلكي'
          },
          image: 'images/2371/features/HC/HC_Wireless Charging.jpg',
          title: {
            en: 'Wireless Charging',
            ar: 'الشحن اللاسلكي'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        xsdac8nojc1s: {
          label: {
            en: 'Headrest',
            ar: 'مسند الرأس'
          },
          image: 'images/2371/features/HC/HC_Seats.jpg',
          title: {
            en: 'Headrest',
            ar: 'مسند الرأس'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        ddlwz0n6pzn8: {
          label: {
            en: 'Door Sill Badging',
            ar: 'شارة عتبة الباب'
          },
          image: 'images/2371/features/HC/image.png',
          title: {
            en: 'Door Sill Badging',
            ar: 'شارة عتبة الباب'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        '9cffzj69hili': {
          label: {
            en: 'Authentic Wood Trim Console',
            ar: 'كونسول بزخارف خشبية أصلية'
          },
          image: 'images/2371/features/HC/HC_Console.jpg',
          title: {
            en: 'Authentic Wood Trim Console',
            ar: 'كونسول بزخارف خشبية أصلية'
          },
          desc: {
            en: 'Authentic open-pore wood trim on the console, upper glove box and doors',
            ar: 'زخارف خشبية أصلية ذات مسام مفتوحة على الكونسول وصندوق القفازات العلوي والأبواب'
          },
          type: 'interior'
        },
        aseriz822crx: {
          label: {
            en: 'Black Chrome Grill',
            ar: 'شبك كروم أسود'
          },
          image: 'images/2371/features/ZR2/ZR2_Grill.jpg',
          title: {
            en: 'Black Chrome Grill',
            ar: 'شبك كروم أسود'
          },
          desc: {
            en: 'Black Chrome Grill with flowtie for increased airflow to engine',
            ar: 'شبك أمامي من الكروم الأسود مع شعار Flowtie لزيادة تدفق الهواء إلى المحرك'
          },
          type: 'exterior'
        },
        '9ram7z0rq7yd': {
          label: {
            en: 'Wheels',
            ar: 'عجلات'
          },
          image: 'images/2371/features/ZR2/ZR2_Wheels.jpg',
          title: {
            en: 'Wheels',
            ar: 'عجلات'
          },
          desc: {
            en: '33-inch Goodyear Wrangler Territory® mud-terrain tires with ZR2-specific 18-inch aluminum wheels',
            ar: 'إطارات ®Goodyear Wrangler Territory مقاس 33 إنش للطرق الوعرة مع عجلات من الألومنيوم قياس 18 إنش خاصة بـ ZR2'
          },
          type: 'exterior'
        },
        ztt94zgq6tv2: {
          label: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          image: 'images/2371/features/ZR2/ZR2_Headlamp.jpg',
          title: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          desc: {
            en: 'High-intensity LED reflector headlamps with Black bezels, LED curtain DRLs and White tracer animation',
            ar: 'مصابيح أمامية عاكسة LED عالية الكثافة مع حواف سوداء، ومصابيح نهارية LED ستائرية ورسوم متحركة بيضاء'
          },
          type: 'exterior'
        },
        x5bxbjca4a53: {
          label: {
            en: 'ZR2 Badging',
            ar: 'شارات ZR2'
          },
          image: 'images/2371/features/ZR2/ZR2_Fender Logo.jpg',
          title: {
            en: 'ZR2 Badging',
            ar: 'شارات ZR2'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        '0bpm6r7t120n': {
          label: {
            en: 'Rear Bumper',
            ar: 'المصد الخلفي'
          },
          image: 'images/2371/features/ZR2/ZR2_Rear Bumper.jpg',
          title: {
            en: 'Rear Bumper',
            ar: 'المصد الخلفي'
          },
          desc: {
            en: 'Modified Rear Bumper with hidden dual exhaust esigned for off-road durability d clearance',
            ar: 'تم تعديل المصد الخلفي بعادم مزدوج مخفي، مصمم لتحسين المتانة والخلوص الأرضي في الطرق الوعرة'
          },
          type: 'exterior'
        },
        v2eva244szp6: {
          label: {
            en: 'Interiors',
            ar: 'المقصورة الداخلية'
          },
          image: 'images/2371/features/ZR2/ZR2_Seats.jpg',
          title: {
            en: 'Interiors',
            ar: 'المقصورة الداخلية'
          },
          desc: {
            en: 'Exclusive Graystone/Jet Black interior trim with Strike Yellow accent stitching',
            ar: 'لمسات داخلية حصرية باللون الرمادي الداكن/الأسود مع خياطة بارزة باللون الأصفر'
          },
          type: 'interior'
        },
        '4jf6vmtzqseb': {
          label: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          image: 'images/2371/features/ZR2/ZR2_Info Screen.jpg',
          title: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          desc: {
            en: '13.4-inch diagonal color touchscreen display and Google built-in§ powered by OnStar§, AM/FM stereo, Bluetooth® audio streaming, wireless Apple CarPlay®, and Android Auto™ compatibility, advance voice recognition, In-vehicle apps, personalized profiles for infotainment and vehicle settings',
            ar: 'شاشة لمس ملونة قياس 13.4 إنش مع خدمات جوجل المدمجة§ والمدعومة من اونستار§، ونظام ستيريو AM/FM، وبث صوتي عبر ®Bluetooth، وتوافق لاسلكي مع ®Apple CarPlay و™Android Auto، وتقنية تعرف صوتي متقدمة، وتطبيقات داخل المركبة، وملفات تعرّف شخصية لإعدادات النظام المعلوماتي الترفيهي والمركبة'
          },
          type: 'interior'
        },
        '7h7ahk43t9t6': {
          label: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          image: 'images/2371/features/ZR2/ZR2_Speedometer.jpg',
          title: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          desc: {
            en: 'Enhanced 12.3" diagonal reconfigurable multicolour digital system',
            ar: 'مركز معلومات القيادة المعزز بشاشة عرض متعددة الألوان قابلة للتخصيص قياس 12.3 إنش'
          },
          type: 'interior'
        },
        '5b5qyf2dkc3d': {
          label: {
            en: 'Sound System',
            ar: 'نظام الصوت'
          },
          image: 'images/2371/features/ZR2/ZR2_Speaker.jpg',
          title: {
            en: 'Sound System',
            ar: 'نظام الصوت'
          },
          desc: {
            en: 'BOSE® premium 7 Seater sound system with Richbass woofer',
            ar: 'نظام ®Bose الصوتي الفاخر بـ7 مكبرات صوت مع مكبر صوت Richbass'
          },
          type: 'interior'
        },
        r8p8s0tba94a: {
          label: {
            en: 'Wheels',
            ar: 'عجلات'
          },
          image: 'images/2371/features/TB/TB_Wheels.jpg',
          title: {
            en: 'Wheels',
            ar: 'عجلات'
          },
          desc: {
            en: '18-inch high-gloss Black-painted wheels with Goodyear Wrangler DuraTrac® mud-terrain tires',
            ar: 'عجلات قياس 18 إنش مطلية باللون الأسود اللامع مع إطارات ®Goodyear Wrangler DuraTrac للطرق الوعرة'
          },
          type: 'exterior'
        },
        '67hwcwhf1291': {
          label: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          image: 'images/2371/features/TB/TB_Headlamp.jpg',
          title: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          desc: {
            en: 'High-intensity LED reflector headlamps with LED curtain DRLs and White tracer animation',
            ar: 'مصابيح أمامية عاكسة LED عالية الكثافة مع أضواء نهارية ستائرية LED ورسوم متحركة بيضاء'
          },
          type: 'exterior'
        },
        vkxy3mgo24us: {
          label: {
            en: 'Black Chevrolet Bowtie',
            ar: 'شعار شفروليه باللون الأسود'
          },
          image: 'images/2371/features/TB/TB_Front Logo.jpg',
          title: {
            en: 'Black Chevrolet Bowtie',
            ar: 'شعار شفروليه باللون الأسود'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        ik1a0znz2yvr: {
          label: {
            en: 'Z71 badge',
            ar: 'شارة Z71'
          },
          image: 'images/2371/features/TB/TB_Fender_Logo.jpg',
          title: {
            en: 'Z71 badge',
            ar: 'شارة Z71'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        '6kfn70dpldgt': {
          label: {
            en: 'Dual Outlet Exhausts',
            ar: 'عوادم ذات مخرج مزدوج'
          },
          image: 'images/2371/features/TB/TB_Exhausts.jpg',
          title: {
            en: 'Dual Outlet Exhausts',
            ar: 'عوادم ذات مخرج مزدوج'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        xwcjbwuemmg9: {
          label: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          image: 'images/2371/features/TB/TB_Info_Screen.jpg',
          title: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          desc: {
            en: '13.4-inch diagonal color touchscreen display and Google built-in§ powered by OnStar§, AM/FM stereo, Bluetooth® audio streaming, wireless Apple CarPlay®, and Android Auto™ compatibility, advance voice recognition, In-vehicle apps, personalized profiles for infotainment and vehicle settings',
            ar: 'شاشة لمس ملونة قياس 13.4 إنش مع خدمات جوجل المدمجة§ والمدعومة من اونستار§، ونظام ستيريو AM/FM، وبث صوتي عبر ®Bluetooth، وتوافق لاسلكي مع ®Apple CarPlay و™Android Auto، وتقنية تعرف صوتي متقدمة، وتطبيقات داخل المركبة، وملفات تعرّف شخصية لإعدادات النظام المعلوماتي الترفيهي والمركبة'
          },
          type: 'interior'
        },
        w8k27psfeex9: {
          label: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          image: 'images/2371/features/TB/TB_Speedometer.jpg',
          title: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          desc: {
            en: 'Enhanced 12.3" diagonal reconfigurable multicolour digital system',
            ar: 'مركز معلومات القيادة المعزز بشاشة عرض متعددة الألوان قابلة للتخصيص قياس 12.3 إنش'
          },
          type: 'interior'
        },
        '7alln8x64kl2': {
          label: {
            en: 'Sound System',
            ar: 'نظام الصوت'
          },
          image: 'images/2371/features/TB/TB_Speaker.jpg',
          title: {
            en: 'Sound System',
            ar: 'نظام الصوت'
          },
          desc: {
            en: 'BOSE® premium 7 Seater sound system with Richbass woofer',
            ar: 'نظام ®Bose الصوتي الفاخر بـ7 مكبرات صوت مع مكبر صوت Richbass'
          },
          type: 'interior'
        },
        gok6ybzvijl3: {
          label: {
            en: 'Wheels',
            ar: 'عجلات'
          },
          image: 'images/2371/features/TB/TB_Wheels.jpg',
          title: {
            en: 'Wheels',
            ar: 'عجلات'
          },
          desc: {
            en: '18-inch high-gloss Black-painted wheels with Goodyear Wrangler DuraTrac® mud-terrain tires',
            ar: 'عجلات قياس 18 إنش مطلية باللون الأسود اللامع مع إطارات ®Goodyear Wrangler DuraTrac للطرق الوعرة'
          },
          type: 'exterior'
        },
        aor43nna2081: {
          label: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          image: 'images/2371/features/TB/TB_Headlamp.jpg',
          title: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          desc: {
            en: 'High-intensity LED reflector headlamps with LED curtain DRLs and White tracer animation',
            ar: 'مصابيح أمامية عاكسة LED عالية الكثافة مع أضواء نهارية ستائرية LED ورسوم متحركة بيضاء'
          },
          type: 'exterior'
        },
        bghvqv1oxwq3: {
          label: {
            en: 'Black Chevrolet Bowtie',
            ar: 'شعار شفروليه باللون الأسود'
          },
          image: 'images/2371/features/TB/TB_Front Logo.jpg',
          title: {
            en: 'Black Chevrolet Bowtie',
            ar: 'شعار شفروليه باللون الأسود'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        kh1xk6tsachv: {
          label: {
            en: 'Dual Outlet Exhausts',
            ar: 'عوادم ذات مخرج مزدوج'
          },
          image: 'images/2371/features/TB/TB_Exhausts.jpg',
          title: {
            en: 'Dual Outlet Exhausts',
            ar: 'عوادم ذات مخرج مزدوج'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        b3wyhh9ssmp5: {
          label: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          image: 'images/2371/features/TB/TB_Info_Screen.jpg',
          title: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          desc: {
            en: '13.4-inch diagonal color touchscreen display and Google built-in§ powered by OnStar§, AM/FM stereo, Bluetooth® audio streaming, wireless Apple CarPlay®, and Android Auto™ compatibility, advance voice recognition, In-vehicle apps, personalized profiles for infotainment and vehicle settings',
            ar: 'شاشة لمس ملونة قياس 13.4 إنش مع خدمات جوجل المدمجة§ والمدعومة من اونستار§، ونظام ستيريو AM/FM، وبث صوتي عبر ®Bluetooth، وتوافق لاسلكي مع ®Apple CarPlay و™Android Auto، وتقنية تعرف صوتي متقدمة، وتطبيقات داخل المركبة، وملفات تعرّف شخصية لإعدادات النظام المعلوماتي الترفيهي والمركبة'
          },
          type: 'interior'
        },
        tpor5yrdiomu: {
          label: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          image: 'images/2371/features/TB/TB_Speedometer.jpg',
          title: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          desc: {
            en: 'Enhanced 12.3" diagonal reconfigurable multicolour digital system',
            ar: 'مركز معلومات القيادة المعزز بشاشة عرض متعددة الألوان قابلة للتخصيص قياس 12.3 إنش'
          },
          type: 'interior'
        },
        zc3f877ep5fv: {
          label: {
            en: 'Sound System',
            ar: 'نظام الصوت'
          },
          image: 'images/2371/features/TB/TB_Speaker.jpg',
          title: {
            en: 'Sound System',
            ar: 'نظام الصوت'
          },
          desc: {
            en: 'BOSE® premium 7 Seater sound system with Richbass woofer',
            ar: 'نظام ®Bose الصوتي الفاخر بـ7 مكبرات صوت مع مكبر صوت Richbass'
          },
          type: 'interior'
        },
        kjzeywh6bhmc: {
          label: {
            en: 'Front Bumpers',
            ar: 'المصدات الأمامية'
          },
          image: 'images/2371/features/RST/RST_Front_Bumper.jpg',
          title: {
            en: 'Front Bumpers',
            ar: 'المصدات الأمامية'
          },
          desc: {
            en: 'Chrome front bumpers',
            ar: 'مصدات أمامية من الكروم'
          },
          type: 'exterior'
        },
        hbjabs5a19ly: {
          label: {
            en: 'Black Chevrolet bowtie emblems',
            ar: 'شعارات شفروليه باللون الأسود'
          },
          image: 'images/2371/features/RST/RST_Front_Logo.jpg',
          title: {
            en: 'Black Chevrolet bowtie emblems',
            ar: 'شعارات شفروليه باللون الأسود'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        tw7ymfa9bxbe: {
          label: {
            en: 'LED DRL',
            ar: 'مصابيح LED للقيادة النهارية'
          },
          image: 'images/2371/features/RST/RST_DRL.jpg',
          title: {
            en: 'LED DRL',
            ar: 'مصابيح LED للقيادة النهارية'
          },
          desc: {
            en: 'High-intensity LED daytime running lamps with white tracer animation',
            ar: 'مصابيح LED عالية الشدة للقيادة النهارية مع رسوم متحركة بيضاء'
          },
          type: 'exterior'
        },
        cj51eb7h876w: {
          label: {
            en: 'Durabed',
            ar: 'Durabed'
          },
          image: 'images/2371/features/RST/RST_Bed.jpg',
          title: {
            en: 'Durabed',
            ar: 'Durabed'
          },
          desc: {
            en: 'Durabed featuring class-leading cargo volume and 12 standard tie-downs',
            ar: 'تتميز Durabed بحجم حمولة رائد في فئتها و12 أداة تثبيت قياسية ضمن الصندوق'
          },
          type: 'exterior'
        },
        '3ahtspa15nlz': {
          label: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          image: 'images/2371/features/RST/RST_Info_Screen.jpg',
          title: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          desc: {
            en: '13.4-inch diagonal color touchscreen display and Google built-in§ powered by OnStar§, AM/FM stereo, Bluetooth® audio streaming, wireless Apple CarPlay®, and Android Auto™ compatibility, advance voice recognition, In-vehicle apps, personalized profiles for infotainment and vehicle settings',
            ar: 'شاشة لمس ملونة قياس 13.4 إنش مع خدمات جوجل المدمجة§ والمدعومة من اونستار§، ونظام ستيريو AM/FM، وبث صوتي عبر ®Bluetooth، وتوافق لاسلكي مع ®Apple CarPlay و™Android Auto، وتقنية تعرف صوتي متقدمة، وتطبيقات داخل المركبة، وملفات تعرّف شخصية لإعدادات النظام المعلوماتي الترفيهي والمركبة'
          },
          type: 'interior'
        },
        '5s66ctsekqb9': {
          label: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          image: 'images/2371/features/RST/RST_Speedometer.jpg',
          title: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          desc: {
            en: 'Enhanced 12.3" diagonal reconfigurable multicolour digital system',
            ar: 'مركز معلومات القيادة المعزز بشاشة عرض متعددة الألوان قابلة للتخصيص قياس 12.3 إنش'
          },
          type: 'interior'
        },
        '9011oiubpuej': {
          label: {
            en: 'Sound System',
            ar: 'نظام الصوت'
          },
          image: 'images/2371/features/RST/RST_Speaker.jpg',
          title: {
            en: 'Sound System',
            ar: 'نظام الصوت'
          },
          desc: {
            en: 'BOSE® premium 7 Seater sound system with Richbass woofer',
            ar: 'نظام ®Bose الصوتي الفاخر بـ7 مكبرات صوت مع مكبر صوت Richbass'
          },
          type: 'interior'
        },
        '48s1xhz7jk96': {
          label: {
            en: 'Black Chevrolet bowtie emblems',
            ar: 'شعارات شفروليه باللون الأسود'
          },
          image: 'images/2371/features/RST/RST_Steering _Wheel_Logo.jpg',
          title: {
            en: 'Black Chevrolet bowtie emblems',
            ar: 'شعارات شفروليه باللون الأسود'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        '2ostbx6tot3q': {
          label: {
            en: 'Front Bumpers',
            ar: 'المصدات الأمامية'
          },
          image: 'images/2371/features/RST/RST_Front_Bumper.jpg',
          title: {
            en: 'Front Bumpers',
            ar: 'المصدات الأمامية'
          },
          desc: {
            en: 'Chrome front bumpers',
            ar: 'مصدات أمامية من الكروم'
          },
          type: 'exterior'
        },
        '989gromic7l7': {
          label: {
            en: 'Black Chevrolet bowtie emblems',
            ar: 'شعارات شفروليه باللون الأسود'
          },
          image: 'images/2371/features/RST/RST_Front_Logo.jpg',
          title: {
            en: 'Black Chevrolet bowtie emblems',
            ar: 'شعارات شفروليه باللون الأسود'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        '80pysz4ealuh': {
          label: {
            en: 'LED DRL',
            ar: 'مصابيح LED للقيادة النهارية'
          },
          image: 'images/2371/features/RST/RST_DRL.jpg',
          title: {
            en: 'LED DRL',
            ar: 'مصابيح LED للقيادة النهارية'
          },
          desc: {
            en: 'High-intensity LED daytime running lamps with white tracer animation',
            ar: 'مصابيح LED عالية الشدة للقيادة النهارية مع رسوم متحركة بيضاء'
          },
          type: 'exterior'
        },
        uxjy77kpsykc: {
          label: {
            en: 'Durabed',
            ar: 'Durabed'
          },
          image: 'images/2371/features/RST/RST_Bed.jpg',
          title: {
            en: 'Durabed',
            ar: 'Durabed'
          },
          desc: {
            en: 'Durabed featuring class-leading cargo volume and 12 standard tie-downs',
            ar: 'تتميز Durabed بحجم حمولة رائد في فئتها و12 أداة تثبيت قياسية ضمن الصندوق'
          },
          type: 'exterior'
        },
        qqai4ydjh6oy: {
          label: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          image: 'images/2371/features/RST/RST_Info_Screen.jpg',
          title: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          desc: {
            en: '13.4-inch diagonal color touchscreen display and Google built-in§ powered by OnStar§, AM/FM stereo, Bluetooth® audio streaming, wireless Apple CarPlay®, and Android Auto™ compatibility, advance voice recognition, In-vehicle apps, personalized profiles for infotainment and vehicle settings',
            ar: 'شاشة لمس ملونة قياس 13.4 إنش مع خدمات جوجل المدمجة§ والمدعومة من اونستار§، ونظام ستيريو AM/FM، وبث صوتي عبر ®Bluetooth، وتوافق لاسلكي مع ®Apple CarPlay و™Android Auto، وتقنية تعرف صوتي متقدمة، وتطبيقات داخل المركبة، وملفات تعرّف شخصية لإعدادات النظام المعلوماتي الترفيهي والمركبة'
          },
          type: 'interior'
        },
        zlpprqne1dyg: {
          label: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          image: 'images/2371/features/RST/RST_Speedometer.jpg',
          title: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          desc: {
            en: 'Enhanced 12.3" diagonal reconfigurable multicolour digital system',
            ar: 'مركز معلومات القيادة المعزز بشاشة عرض متعددة الألوان قابلة للتخصيص قياس 12.3 إنش'
          },
          type: 'interior'
        },
        y6zcqp9r8fln: {
          label: {
            en: 'Sound System',
            ar: 'نظام الصوت'
          },
          image: 'images/2371/features/RST/RST_Speaker.jpg',
          title: {
            en: 'Sound System',
            ar: 'نظام الصوت'
          },
          desc: {
            en: 'BOSE® premium 7 Seater sound system with Richbass woofer',
            ar: 'نظام ®Bose الصوتي الفاخر بـ7 مكبرات صوت مع مكبر صوت Richbass'
          },
          type: 'interior'
        },
        '3ahd3ud1cxvf': {
          label: {
            en: 'Black Chevrolet bowtie emblems',
            ar: 'شعارات شفروليه باللون الأسود'
          },
          image: 'images/2371/features/RST/RST_Steering _Wheel_Logo.jpg',
          title: {
            en: 'Black Chevrolet bowtie emblems',
            ar: 'شعارات شفروليه باللون الأسود'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        '1kmk7x0zr7yd': {
          label: {
            en: 'Chrome Bumpers',
            ar: 'مصدات من الكروم'
          },
          image: 'images/2371/features/LT_CC/LT_CC_Front_Bumper.jpg',
          title: {
            en: 'Chrome Bumpers',
            ar: 'مصدات من الكروم'
          },
          desc: {
            en: 'Chrome Bars with high-gloss black mesh inserts grille',
            ar: 'شبك أمامي بقضبان من الكروم وتطعيمات شبكية سوداء شديدة اللمعان'
          },
          type: 'exterior'
        },
        j09x4tjlksiq: {
          label: {
            en: 'Wheels',
            ar: 'عجلات'
          },
          image: 'images/2371/features/LT_CC/LT_CC_Wheels.jpg',
          title: {
            en: 'Wheels',
            ar: 'عجلات'
          },
          desc: {
            en: '20-inch machined-face painted-aluminum wheels with Grazen-painted pockets',
            ar: 'عجلات من الألومنيوم المطلي قياس 20 إنش مع جيوب مطلية بطبقة من Grazen'
          },
          type: 'exterior'
        },
        rwj9cz2t4kgs: {
          label: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          image: 'images/2371/features/LT_CC/LT_CC_Headlamp.jpg',
          title: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          desc: {
            en: 'High-intensity LED reflector headlamps with Black bezels, LED curtain DRLs and White tracer animation',
            ar: 'مصابيح أمامية عاكسة LED عالية الكثافة مع حواف سوداء، ومصابيح نهارية LED ستائرية ورسوم متحركة بيضاء'
          },
          type: 'exterior'
        },
        f0rf1nsdn3mr: {
          label: {
            en: 'Gold Chevrolet Bowtie',
            ar: 'شعار شفروليه باللون الذهبي'
          },
          image: 'images/2371/features/LT_CC/LT_CC_Front_Logo.jpg',
          title: {
            en: 'Gold Chevrolet Bowtie',
            ar: 'شعار شفروليه باللون الذهبي'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        vvi10af2yeqo: {
          label: {
            en: 'Durabed',
            ar: 'Durabed'
          },
          image: 'images/2371/features/LT_CC/LT_CC_Bed.jpg',
          title: {
            en: 'Durabed',
            ar: 'Durabed'
          },
          desc: {
            en: 'Durabed featuring class-leading cargo volume and 12 standard tie-downs',
            ar: 'تتميز Durabed بحجم حمولة رائد في فئتها و12 أداة تثبيت قياسية ضمن الصندوق'
          },
          type: 'exterior'
        },
        tauk6dp0vcf3: {
          label: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          image: 'images/2371/features/LT_CC/LT_CC_Info_Screen.jpg',
          title: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          desc: {
            en: '13.4-inch diagonal color touchscreen display and Google built-in§ powered by OnStar§, AM/FM stereo, Bluetooth® audio streaming, wireless Apple CarPlay®, and Android Auto™ compatibility, advance voice recognition, In-vehicle apps, personalized profiles for infotainment and vehicle settings',
            ar: 'شاشة لمس ملونة قياس 13.4 إنش مع خدمات جوجل المدمجة§ والمدعومة من اونستار§، ونظام ستيريو AM/FM، وبث صوتي عبر ®Bluetooth، وتوافق لاسلكي مع ®Apple CarPlay و™Android Auto، وتقنية تعرف صوتي متقدمة، وتطبيقات داخل المركبة، وملفات تعرّف شخصية لإعدادات النظام المعلوماتي الترفيهي والمركبة'
          },
          type: 'interior'
        },
        u9pqnc3jhbhx: {
          label: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          image: 'images/2371/features/LT_CC/LT_CC_Speedometer.jpg',
          title: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          desc: {
            en: 'Enhanced 12.3" diagonal reconfigurable multicolour digital system',
            ar: 'مركز معلومات القيادة المعزز بشاشة عرض متعددة الألوان قابلة للتخصيص قياس 12.3 إنش'
          },
          type: 'interior'
        },
        twsbc3te9m0w: {
          label: {
            en: 'Leather-wrapped steering wheel',
            ar: 'عجلة قيادة مغطاة بالجلد'
          },
          image: 'images/2371/features/LT_CC/LT_CC_Steering_Wheel.jpg',
          title: {
            en: 'Leather-wrapped steering wheel',
            ar: 'عجلة قيادة مغطاة بالجلد'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        m1m4h0l2cqeo: {
          label: {
            en: 'Chrome Bumpers',
            ar: 'مصدات من الكروم'
          },
          image: 'images/2371/features/LT_RC/LT_RC_Front_Bumper.jpg',
          title: {
            en: 'Chrome Bumpers',
            ar: 'مصدات من الكروم'
          },
          desc: {
            en: 'Chrome Bars with high-gloss black mesh inserts grille',
            ar: 'شبك أمامي بقضبان من الكروم وتطعيمات شبكية سوداء شديدة اللمعان'
          },
          type: 'exterior'
        },
        ylam62d7djz9: {
          label: {
            en: 'Wheels',
            ar: 'عجلات'
          },
          image: 'images/2371/features/LT_RC/LT_RC_Wheels.jpg',
          title: {
            en: 'Wheels',
            ar: 'عجلات'
          },
          desc: {
            en: '20-inch machined-face painted-aluminum wheels with Grazen-painted pockets',
            ar: 'عجلات من الألومنيوم المطلي قياس 20 إنش مع جيوب مطلية بطبقة من Grazen'
          },
          type: 'exterior'
        },
        h7hoq4zvkx1d: {
          label: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          image: 'images/2371/features/LT_RC/LT_RC_Headlamp.jpg',
          title: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          desc: {
            en: 'High-intensity LED reflector headlamps with Black bezels, LED curtain DRLs and White tracer animation',
            ar: 'مصابيح أمامية عاكسة LED عالية الكثافة مع حواف سوداء، ومصابيح نهارية LED ستائرية ورسوم متحركة بيضاء'
          },
          type: 'exterior'
        },
        h8b41kjneur6: {
          label: {
            en: 'Gold Chevrolet Bowtie',
            ar: 'شعار شفروليه باللون الذهبي'
          },
          image: 'images/2371/features/LT_RC/LT_RC_Front_Logo.jpg',
          title: {
            en: 'Gold Chevrolet Bowtie',
            ar: 'شعار شفروليه باللون الذهبي'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        wog0ubyk7gkm: {
          label: {
            en: 'Durabed',
            ar: 'Durabed'
          },
          image: 'images/2371/features/LT_RC/LT_RC_Bed.jpg',
          title: {
            en: 'Durabed',
            ar: 'Durabed'
          },
          desc: {
            en: 'Durabed featuring class-leading cargo volume and 12 standard tie-downs',
            ar: 'تتميز Durabed بحجم حمولة رائد في فئتها و12 أداة تثبيت قياسية ضمن الصندوق'
          },
          type: 'exterior'
        },
        '1dozmck8mjut': {
          label: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          image: 'images/2371/features/LT_RC/LT_RC_Info_Screen.jpg',
          title: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          desc: {
            en: '13.4-inch diagonal color touchscreen display and Google built-in§ powered by OnStar§, AM/FM stereo, Bluetooth® audio streaming, wireless Apple CarPlay®, and Android Auto™ compatibility, advance voice recognition, In-vehicle apps, personalized profiles for infotainment and vehicle settings',
            ar: 'شاشة لمس ملونة قياس 13.4 إنش مع خدمات جوجل المدمجة§ والمدعومة من اونستار§، ونظام ستيريو AM/FM، وبث صوتي عبر ®Bluetooth، وتوافق لاسلكي مع ®Apple CarPlay و™Android Auto، وتقنية تعرف صوتي متقدمة، وتطبيقات داخل المركبة، وملفات تعرّف شخصية لإعدادات النظام المعلوماتي الترفيهي والمركبة'
          },
          type: 'interior'
        },
        y2e6haahw9vd: {
          label: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          image: 'images/2371/features/LT_RC/LT_RC_Speedometer.jpg',
          title: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          desc: {
            en: 'Enhanced 12.3" diagonal reconfigurable multicolour digital system',
            ar: 'مركز معلومات القيادة المعزز بشاشة عرض متعددة الألوان قابلة للتخصيص قياس 12.3 إنش'
          },
          type: 'interior'
        },
        y6zcqp9r8rfln: {
          label: {
            en: 'Leather-wrapped steering wheel',
            ar: 'عجلة قيادة مغطاة بالجلد'
          },
          image: 'images/2371/features/LT_RC/LT_RC_Steering_Wheel.jpg',
          title: {
            en: 'Leather-wrapped steering wheel',
            ar: 'عجلة قيادة مغطاة بالجلد'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        o0007fa1ki84: {
          label: {
            en: 'Chrome Bumpers',
            ar: 'مصدات من الكروم'
          },
          image: 'images/2371/features/LT_RC/LT_RC_Front_Bumper.jpg',
          title: {
            en: 'Chrome Bumpers',
            ar: 'مصدات من الكروم'
          },
          desc: {
            en: 'Chrome Bars with high-gloss black mesh inserts grille',
            ar: 'شبك أمامي بقضبان من الكروم وتطعيمات شبكية سوداء شديدة اللمعان'
          },
          type: 'exterior'
        },
        '9j2b1he3cyuz': {
          label: {
            en: 'Wheels',
            ar: 'عجلات'
          },
          image: 'images/2371/features/LT_RC/LT_RC_Wheels.jpg',
          title: {
            en: 'Wheels',
            ar: 'عجلات'
          },
          desc: {
            en: '20-inch machined-face painted-aluminum wheels with Grazen-painted pockets',
            ar: 'عجلات من الألومنيوم المطلي قياس 20 إنش مع جيوب مطلية بطبقة من Grazen'
          },
          type: 'exterior'
        },
        zxhw2adigsjb: {
          label: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          image: 'images/2371/features/LT_RC/LT_RC_Headlamp.jpg',
          title: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          desc: {
            en: 'High-intensity LED reflector headlamps with Black bezels, LED curtain DRLs and White tracer animation',
            ar: 'مصابيح أمامية عاكسة LED عالية الكثافة مع حواف سوداء، ومصابيح نهارية LED ستائرية ورسوم متحركة بيضاء'
          },
          type: 'exterior'
        },
        a39z5tj2p9mu: {
          label: {
            en: 'Gold Chevrolet Bowtie',
            ar: 'شعار شفروليه باللون الذهبي'
          },
          image: 'images/2371/features/LT_RC/LT_RC_Front_Logo.jpg',
          title: {
            en: 'Gold Chevrolet Bowtie',
            ar: 'شعار شفروليه باللون الذهبي'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        '43e5j3wwg3h3': {
          label: {
            en: 'Durabed',
            ar: 'Durabed'
          },
          image: 'images/2371/features/LT_RC/LT_RC_Bed.jpg',
          title: {
            en: 'Durabed',
            ar: 'Durabed'
          },
          desc: {
            en: 'Durabed featuring class-leading cargo volume and 12 standard tie-downs',
            ar: 'تتميز Durabed بحجم حمولة رائد في فئتها و12 أداة تثبيت قياسية ضمن الصندوق'
          },
          type: 'exterior'
        },
        gkyn7z7f6x6e: {
          label: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          image: 'images/2371/features/LT_RC/LT_RC_Info_Screen.jpg',
          title: {
            en: 'Info Screen',
            ar: 'شاشة المعلومات'
          },
          desc: {
            en: '13.4-inch diagonal color touchscreen display and Google built-in§ powered by OnStar§, AM/FM stereo, Bluetooth® audio streaming, wireless Apple CarPlay®, and Android Auto™ compatibility, advance voice recognition, In-vehicle apps, personalized profiles for infotainment and vehicle settings',
            ar: 'شاشة لمس ملونة قياس 13.4 إنش مع خدمات جوجل المدمجة§ والمدعومة من اونستار§، ونظام ستيريو AM/FM، وبث صوتي عبر ®Bluetooth، وتوافق لاسلكي مع ®Apple CarPlay و™Android Auto، وتقنية تعرف صوتي متقدمة، وتطبيقات داخل المركبة، وملفات تعرّف شخصية لإعدادات النظام المعلوماتي الترفيهي والمركبة'
          },
          type: 'interior'
        },
        '0lcgclpnp6ew': {
          label: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          image: 'images/2371/features/LT_RC/LT_RC_Speedometer.jpg',
          title: {
            en: 'Driver Information Center',
            ar: 'مركز معلومات القيادة'
          },
          desc: {
            en: 'Enhanced 12.3" diagonal reconfigurable multicolour digital system',
            ar: 'مركز معلومات القيادة المعزز بشاشة عرض متعددة الألوان قابلة للتخصيص قياس 12.3 إنش'
          },
          type: 'interior'
        },
        ecjxcwk9ba81: {
          label: {
            en: 'Leather-wrapped steering wheel',
            ar: 'عجلة قيادة مغطاة بالجلد'
          },
          image: 'images/2371/features/LT_RC/LT_RC_Steering_Wheel.jpg',
          title: {
            en: 'Leather-wrapped steering wheel',
            ar: 'عجلة قيادة مغطاة بالجلد'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        }
      },
      accessories: {
        w9hpmzuxt0zw: {
          label: {
            en: '6.2L',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '69tpimc80m8d': {
          label: {
            en: 'Tow hook',
            ar: 'خطاف السحب'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        u20m3d5j71vd: {
          label: {
            en: 'Fender badge',
            ar: 'شارة الرفرف'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '0agqewqxtxza': {
          label: {
            en: 'Hood Logo',
            ar: 'شعار غطاء المحرك'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '3nc1olw2alfg': {
          label: {
            en: 'Redline Decal',
            ar: 'ملصق Redline'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        vsr3vozntdek: {
          label: {
            en: 'Spotter Mirror',
            ar: 'مرآة كشف النقطة العمياء'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        b6tg7mm7ujey: {
          label: {
            en: 'FLOOR_MATS',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        j173afsvfoxm: {
          label: {
            en: 'WO Bedliner',
            ar: 'بدون بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        shr9jmwd4mm7: {
          label: {
            en: ' Default Multiflex tailgate',
            ar: 'البوابة الخلفية Multi-Flex القياسية'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '8hjk03msw523': {
          label: {
            en: 'Multiflex gate',
            ar: 'البوابة الخلفية Multi-Flex'
          },
          image: 'images/2371/Acc/LTZ/SHORT_MULTI_FLEX_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'QK2',
          brandItemCode: 'AC66',
        },
        '5ea062y4dxnj': {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        dto6if7ng76e: {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '47fmyjws74cp': {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        hmlzbxlv36y9: {
          label: {
            en: 'Suspension_package_acc',
            ar: 'Suspension_package_acc'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        h7l2djmpt06c: {
          label: {
            en: 'HEAD UP DISPLAY1',
            ar: 'نظام العرض على الزجاج الأمامي1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
        },
        hi5kmxzzzq9u: {
          label: {
            en: 'HEAD UP DISPLAY',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'UV6'
        },
        '0tocr9fb6kax': {
          label: {
            en: 'IRVM1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        j8kuo5g8mvzt: {
          label: {
            en: 'IRVM2',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        rybgoukmmqhe: {
          label: {
            en: 'Power-retractable Assist Steps',
            ar: 'درجات المساعدة على الصعود والنزول قابلة للسحب كهربائياً'
          },
          image: 'images/2371/Power_Retractable _HC.png',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'BRS',
          brandItemCode: 'AC67',
        },
        drbil9s8a41l: {
          label: {
            en: 'CHROME_WHEEL_TO_WHEEL',
            ar: 'درجات مساعدة قابلة للسحب كهربائيًا'
          },
          image: 'images/2371/Acc/HC/assist step.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        mtzheryp1crc: {
          label: {
            en: 'ROOF LIGHT1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '54nn9olfhq0x': {
          label: {
            en: 'ROOF LIGHT3',
            ar: 'ضوء السقف 3'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        jhtdb4dgs4ag: {
          label: {
            en: 'SHORT BED TAILAGE2',
            ar: 'SHORT BED TAILAGE2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        dhp5omqgvvcg: {
          label: {
            en: 'Assist Steps 4 Inch Round Black',
            ar: 'درجات المساعدة على الصعود والنزول باللون الأسود ومستديرة قياس 4 إنش'
          },
          image: 'images/2371/Acc/HC/assist step.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        '7hbnhyetgkro': {
          label: {
            en: 'Assist Steps 4 Inch Round Black',
            ar: 'درجات المساعدة على الصعود والنزول باللون الأسود ومستديرة قياس 4 إنش'
          },
          image: 'images/2371/Acc/HC/assist step.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        uqjfhy5t45zy: {
          label: {
            en: 'Bowtie Logo Puddle Light',
            ar: 'إنارة أرضية أسفل الأبواب بشعار شفروليه'
          },
          image: 'images/2371/Acc/HC/ACC_Bowtie_Logo_Puddle_Light.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'S3I',
          excludeFromRecommendation: true,
          brandItemCode: 'AC69',
        },
        pdqc9e5ntf2r: {
          label: {
            en: 'Bowtie Graphic',
            ar: 'رسم شعار شفروليه'
          },
          image: 'images/2371/Acc/HC/ACC_Bowtie_Graphic_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SFZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC70',
        },
        a6vg80c1um80: {
          label: {
            en: 'Illuiminated Front Bowtie',
            ar: 'شعار شفروليه أمامي مضيء'
          },
          image: 'images/2371/Acc/HC/ACC_Illuminated_Front_Bowtie_Except_ZR2.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          excludeFromRecommendation: true,
          brandItemCode: 'AC71',
        },
        kzb69p4za0o5: {
          label: {
            en: 'Bed Mat Black',
            ar: 'حصيرة الصندوق باللون الأسود'
          },
          image: 'images/2371/Acc/HC/ACC_Bed Mat_Black_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VBR',
          excludeFromRecommendation: true,
          brandItemCode: 'AC72',
        },
        jw0a0kwm303b: {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صلب قابل للطي لصندوق الحمولة'
          },
          image: 'images/2371/Acc/HC/ACC_Hard_Folding_Tonneau_Cover_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VOZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC73',
        },
        lsxhwegkkd4y: {
          label: {
            en: 'Sports Bar Bed Mounted',
            ar: 'بار رياضي مثبت على الصندوق'
          },
          image: 'images/2371/Acc/HC/ACC_Sports_Bar_Bed_Mounted_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        rq9m3rn89rf4: {
          label: {
            en: '20in Machined Aluminium with Charcoal Pockets Standard',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        rq9m3rn89rf904: {
          label: {
            en: 'Alloy_20in_High_Gloss_Short_bed',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        dk8i90myygvo: {
          label: {
            en: 'Midnight Edition HC',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '6waediiz4m74': {
          label: {
            en: '22" Painted Aluminium with Chrome inserts',
            ar: 'عجلات ألومنيوم مطلية قياس 22 إنش مع لمسات من الكروم'
          },
          image: 'images/2371/Acc/HC/22inc_alloy_HC.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RPT',
          brandItemCode: 'AC75',
        },
        '876c3pibsjov': {
          label: {
            en: 'hood logo',
            ar: 'شعار غطاء المحرك'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '66huqa03m4o7': {
          label: {
            en: '6.2L',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        u3557rhx8d1m: {
          label: {
            en: 'Tow hook',
            ar: 'خطاف السحب'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '2qjengswijpp': {
          label: {
            en: 'Tow hook',
            ar: 'خطاف السحب'
          },
          image: 'images/2371/Acc/ZR2/BLACK_HOOK.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        jzph1dgkv4md: {
          label: {
            en: 'Roof Light',
            ar: 'مصباح السقف'
          },
          image: 'images/2371/Acc/ZR2/ROOF_LIGHT_3.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        kb0slo52pbnd: {
          label: {
            en: 'Redline Decal',
            ar: 'ملصق Redline'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '6uxaabsyqpx3': {
          label: {
            en: 'Spotter Mirror',
            ar: 'مرآة كشف النقطة العمياء'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        lp03od01mlyo: {
          label: {
            en: 'FLOOR_MATS',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        wh68ti5fisga: {
          label: {
            en: 'Adaptive Cruise Control',
            ar: 'نظام تثبيت السرعة التكيفي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        d45yq1c8nd0s: {
          label: {
            en: 'Hitch View',
            ar: 'عرض موضع القطر'
          },
          image: 'images/2371/Acc/ZR2/ADAPTIVE_CRUISE_CONTROL.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'PZ8',
          brandItemCode: 'AC83',
        },
        avkty5mam3dk: {
          label: {
            en: 'WO Bedliner',
            ar: 'بدون بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        lrmm67s11zi3: {
          label: {
            en: 'Bedliner',
            ar: 'بطانة الصندوق'
          },
          image: 'images/2371/Acc/LTZ/SHORT_DEFAULT_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC78',
        },
        hu6r3sxwhcqv: {
          label: {
            en: ' Default Multiflex tailgate',
            ar: 'البوابة الخلفية Multi-Flex القياسية'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        s87repgh8l42: {
          label: {
            en: 'Multiflex gate',
            ar: 'البوابة الخلفية Multi-Flex'
          },
          image: 'images/2371/Acc/LTZ/SHORT_MULTI_FLEX_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'QK2',
          brandItemCode: 'AC66',
        },
        '5jrmudijeqpm': {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        zgrj1lgvwg7j: {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        h3gqpm1v53bx: {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '38b1zt9m9l7h': {
          label: {
            en: 'Suspension_package_acc',
            ar: 'Suspension_package_acc'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        z4tn2qlwdxdq: {
          label: {
            en: 'HEAD UP DISPLAY1',
            ar: 'نظام العرض على الزجاج الأمامي1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
        },
        u3zpsiu1n3nb: {
          label: {
            en: 'HEAD UP DISPLAY',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'UV6'
        },
        '29zj0gvuwp98': {
          label: {
            en: 'IRVM1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '15fp8hgbcwyv': {
          label: {
            en: 'IRVM2',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '5bgopnoc8isd': {
          label: {
            en: 'Assist steps, Chrome, wheel to wheel',
            ar: 'درجات المساعدة على الصعود والنزول من الكروم تمتد من عجلة إلى عجلة'
          },
          image: 'images/2371/Acc/ZR2/ACC_Assist_Steps_Chrome_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'BVT',
          brandItemCode: 'AC76',
        },
        m27tcb602fql: {
          label: {
            en: 'ROOF LIGHT1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        h5au9myl063y: {
          label: {
            en: 'Roof Light 2',
            ar: 'ضوء السقف 2'
          },
          image: 'images/2371/Acc/ZR2/ROOF_LIGHT_2.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        tbpzcy915xx3: {
          label: {
            en: 'ROOF LIGHT3',
            ar: 'ضوء السقف 3'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        qz2iomvtp5mv: {
          label: {
            en: 'SHORT BED TAILAGE2',
            ar: 'SHORT BED TAILAGE2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        oel878rj2o2x: {
          label: {
            en: 'Assist Steps 4 Inch Round Black',
            ar: 'درجات المساعدة على الصعود والنزول باللون الأسود ومستديرة قياس 4 إنش'
          },
          image: 'images/2371/Acc/ZR2/ACC_Assist_Steps_Black_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        h5lkbdk5et66: {
          label: {
            en: 'Assist steps chrome',
            ar: 'درجات المساعدة على الصعود والنزول من الكروم'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        oubehplj30lk: {
          label: {
            en: 'Padel Covers',
            ar: 'أغطية مبدلات السرعة'
          },
          image: 'images/2371/Acc/ZR2/ACC_Pedal Covers_Sport_Stainless_Steel.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        c5f6o8lpildp: {
          label: {
            en: 'Bowtie Logo Puddle Light',
            ar: 'إنارة أرضية أسفل الأبواب بشعار شفروليه'
          },
          image: 'images/2371/Acc/ZR2/ACC_Bowtie_Logo_Puddle_Light.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'S3I',
          excludeFromRecommendation: true,
          brandItemCode: 'AC69',
        },
        '8bmp5qdr4umi': {
          label: {
            en: 'Bowtie Graphic',
            ar: 'رسم شعار شفروليه'
          },
          image: 'images/2371/Acc/ZR2/ACC_Bowtie_Graphic_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SFZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC70',
        },
        '6yep0op98rgw': {
          label: {
            en: 'Illuiminated Front Bowtie',
            ar: 'شعار شفروليه أمامي مضيء'
          },
          image: 'images/2371/Acc/ZR2/ACC_Illuminated_Front_Bowtie_ZR2.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          excludeFromRecommendation: true,
          brandItemCode: 'AC71',
        },
        '5speucfhfr48': {
          label: {
            en: 'Bed Mat Black',
            ar: 'حصيرة الصندوق باللون الأسود'
          },
          image: 'images/2371/Acc/ZR2/ACC_Bed Mat_Black_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VBR',
          excludeFromRecommendation: true,
          brandItemCode: 'AC72',
        },
        '4naxdvnyupg8': {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صلب قابل للطي لصندوق الحمولة'
          },
          image: 'images/2371/Acc/ZR2/ACC_Hard_Folding_Tonneau_Cover_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VOZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC73',
        },
        rypmhhonl0tg: {
          label: {
            en: 'Sports Bar Bed Mounted',
            ar: 'بار رياضي مثبت على الصندوق'
          },
          image: 'images/2371/Acc/ZR2/ACC_Sports_Bar_Bed_Mounted_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        yy0hy78t9fvl: {
          label: {
            en: '18in Aluminium Machine Face with Black painted spokes and Gold Accents',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        n1g3jd1stqd5: {
          label: {
            en: '18" Gloss Black',
            ar: 'عجلات سوداء شديدة اللمعان قياس 18 إنش'
          },
          image: 'images/2371/Acc/ZR2/Alloy_18in_Gloss_Black_ZR2.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        n1g3jd1stqd905: {
          label: {
            en: 'Alloy_18in_High_Gloss_Short_bed',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        pyw2gvfy8z9g: {
          label: {
            en: 'Steering System',
            ar: 'نظام التوجيه'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        h58thpxpqmpc: {
          label: {
            en: 'Steering System2',
            ar: 'نظام التوجيه 2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        zh0uvtjvnqfa: {
          label: {
            en: 'Task lamp1',
            ar: 'مصباح المهام 1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        drdn0mg8sb78: {
          label: {
            en: 'Task lamp2',
            ar: 'مصباح المهام 2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        th8zueo3valx: {
          label: {
            en: 'SHORT BED TAILAGE SYSTEM1',
            ar: 'نظام الباب الخلفي للصندوق القصير1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        cdrpwn9dos9r: {
          label: {
            en: 'Short Bed Tailgate System',
            ar: 'نظام الباب الخلفي للصندوق القصير'
          },
          image: 'images/2371/Acc/ZR2/SHORT_TAILGATE_POWER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        aww7oooc9won: {
          label: {
            en: 'Dark_Appearance_Package',
            ar: 'Dark_Appearance_Package'
          },
          image: 'images/2371/Acc/ZR2/SHORT_TAILGATE_POWER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '2bo0gswfrfcz': {
          label: {
            en: '20" High Gloss Black-Painted Aluminum',
            ar: 'عجلات ألومنيوم مطلية باللون الأسود شديد اللمعان قياس 20 إنش'
          },
          image: 'images/2371/Acc/TB CC/Alloy_20in_High_Gloss_Std_bed.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RD5',
          brandItemCode: 'AC77',
        },
        c6u4n5p3dp39: {
          label: {
            en: 'Fog lamp',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        zl5dd3sw2bt1: {
          label: {
            en: 'hood logo',
            ar: 'شعار غطاء المحرك'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '3jyopz3oorhe': {
          label: {
            en: 'Engine 2.7L TurboMax',
            ar: 'محرك TurboMax سعة 2.7 لتر'
          },
          image: 'images/2371/Acc/TB CC/engine.png',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        jnybh05tlkfs: {
          label: {
            en: 'Tow hook',
            ar: 'خطاف السحب'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        u02qdok8qgoi: {
          label: {
            en: 'Tow hook',
            ar: 'خطاف السحب'
          },
          image: 'images/2371/Acc/TB CC/BLACK_HOOK.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        oz0z7dsfp8bd: {
          label: {
            en: 'Silverdao logo',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        m03wg71zplza: {
          label: {
            en: 'Silverdao logo1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        un7e58s023em: {
          label: {
            en: 'Redline Decal',
            ar: 'ملصق Redline'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        mli9be6yh6pr: {
          label: {
            en: 'Spotter Mirror',
            ar: 'مرآة كشف النقطة العمياء'
          },
          image: 'images/2371/Acc/TB CC/SPOTTER_MIRROR.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '0pk9phb573rt': {
          label: {
            en: 'Floor Liners- Front and Rear',
            ar: 'بطانات الأرضية - أمامية وخلفية'
          },
          image: 'images/2371/Acc/TB CC/FLOOR_LINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AAK',
          brandItemCode: 'AC87',
        },
        '9l7a7vvp5buq': {
          label: {
            en: 'ADAPTIVE CRUISE CONTROL',
            ar: 'نظام تثبيت السرعة التكيفي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '2ybsfpcpw041': {
          label: {
            en: 'WO Bedliner',
            ar: 'بدون بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        j2wfqejl5fxk: {
          label: {
            en: 'Bedliner',
            ar: 'بطانة الصندوق'
          },
          image: 'images/2371/features/TB/TB_Exhausts.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC78',
        },
        sn2lalj1ic7j: {
          label: {
            en: ' Without Multiflex',
            ar: 'بدون بوابة Multiflex'
          },
          image: 'images/2371/features/TB/TB_Exhausts.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '34z00lryq9uk': {
          label: {
            en: 'Multiflex gate',
            ar: 'البوابة الخلفية Multi-Flex'
          },
          image: 'images/2371/features/TB/TB_Exhausts.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'QK2',
          brandItemCode: 'AC66',
        },
        i2m85llrw0bu: {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '7gsoxqirzwj4': {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        hbv59yodwomm: {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '6kmb8jxn2g9t': {
          label: {
            en: 'Suspension_package_acc',
            ar: 'Suspension_package_acc'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ltmgrevl229k: {
          label: {
            en: 'HEAD UP DISPLAY1',
            ar: 'نظام العرض على الزجاج الأمامي1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
        },
        yjbw6quzffew: {
          label: {
            en: 'HEAD UP DISPLAY',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'UV6'
        },
        '37nnp2r86h97': {
          label: {
            en: 'IRVM1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        tg93h5y76f2f: {
          label: {
            en: 'IRVM2',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        akfa3p311thw: {
          label: {
            en: 'Power-retractable Assist Steps',
            ar: 'درجات المساعدة على الصعود والنزول قابلة للسحب كهربائياً'
          },
          image: 'images/2371/Acc/TB CC/ACC_Assist_Steps_Chrome_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'BRS',
          brandItemCode: 'AC67',
        },
        s2lulhvnbsid: {
          label: {
            en: 'ROOF LIGHT1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '5coix9shmd1o': {
          label: {
            en: 'Roof Light',
            ar: 'مصباح السقف'
          },
          image: 'images/2371/Acc/TB CC/ROOF_LIGHT_2.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        v2ej8yk6v14e: {
          label: {
            en: 'ROOF LIGHT3',
            ar: 'ضوء السقف 3'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        t128kzfbtn1f: {
          label: {
            en: 'SHORT BED TAILAGE2',
            ar: 'SHORT BED TAILAGE2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        oxiuvot0twab: {
          label: {
            en: 'Assist Steps 4 Inch Round Black',
            ar: 'درجات المساعدة على الصعود والنزول باللون الأسود ومستديرة قياس 4 إنش'
          },
          image: 'images/2371/Acc/TB CC/ACC_Assist_Steps_Black_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        j8ufoo4scfer: {
          label: {
            en: 'Assist steps',
            ar: 'درجات المساعدة على الصعود والنزول'
          },
          image: 'images/2371/Acc/TB CC/ACC_Assist_Steps_Black_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'B3L',
          brandItemCode: 'AC56',
        },
        xaqcampnqq5z: {
          label: {
            en: 'Padel Covers',
            ar: 'أغطية مبدلات السرعة'
          },
          image: 'images/2371/Acc/TB CC/ACC_Pedal Covers_Sport_Stainless_Steel.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        '9nvo6s76ajus': {
          label: {
            en: 'Bowtie Logo Puddle Light',
            ar: 'إنارة أرضية أسفل الأبواب بشعار شفروليه'
          },
          image: 'images/2371/Acc/TB CC/ACC_Bowtie_Logo_Puddle_Light.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'S3I',
          excludeFromRecommendation: true,
          brandItemCode: 'AC69',
        },
        '38k3iyysbq2z': {
          label: {
            en: 'Bowtie Graphic',
            ar: 'رسم شعار شفروليه'
          },
          image: 'images/2371/Acc/TB CC/ACC_Bowtie_Graphic_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SFZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC70',
        },
        fd6bpuickxcp: {
          label: {
            en: 'Illuiminated Front Bowtie',
            ar: 'شعار شفروليه أمامي مضيء'
          },
          image: 'images/2371/Acc/TB CC/ACC_Illuminated_Front_Bowtie_Except_ZR2.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          excludeFromRecommendation: true,
          brandItemCode: 'AC71',
        },
        n530gwunez4g: {
          label: {
            en: 'Bed Mat Black',
            ar: 'حصيرة الصندوق باللون الأسود'
          },
          image: 'images/2371/Acc/TB CC/ACC_Bed Mat_Black_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VBR',
          excludeFromRecommendation: true,
          brandItemCode: 'AC72',
        },
        '735wgq63ug0k': {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صلب قابل للطي لصندوق الحمولة'
          },
          image: 'images/2371/Acc/TB CC/ACC_Hard_Folding_Tonneau_Cover_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VOZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC73',
        },
        lp5918siuusp: {
          label: {
            en: 'Sports Bar Bed Mounted',
            ar: 'بار رياضي مثبت على الصندوق'
          },
          image: 'images/2371/Acc/TB CC/ACC_Sports_Bar_Bed_Mounted_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        o1czcsnfg64j: {
          label: {
            en: '18in High Gloss Black-Painted Aluminum Standard_High',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '603xlrj84z06': {
          label: {
            en: 'Steering System',
            ar: 'نظام التوجيه'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '0mdferymt8kz': {
          label: {
            en: 'Steering System2',
            ar: 'نظام التوجيه 2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '3gcejyte0k2w': {
          label: {
            en: 'Task lamp1',
            ar: 'مصباح المهام 1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ivmhdr8aj6iv: {
          label: {
            en: 'Task lamp2',
            ar: 'مصباح المهام 2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        npsyote0o3kj: {
          label: {
            en: 'SHORT BED TAILAGE SYSTEM1',
            ar: 'نظام الباب الخلفي للصندوق القصير1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        c1l34m40cnhz: {
          label: {
            en: 'Short Bed Tailgate System',
            ar: 'نظام الباب الخلفي للصندوق القصير'
          },
          image: 'images/2371/features/TB/TB_Exhausts.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '73ymcufp080z': {
          label: {
            en: 'ORVM',
            ar: 'ORVM'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ont3on5c7qax: {
          label: {
            en: 'Seat_Storage__Armrest',
            ar: 'مساحة تخزين المقعد_مسند الذراع'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          brandItemCode: 'AC89',
        },
        e84uvam0sehp: {
          label: {
            en: 'Seat Storage',
            ar: 'مساحة تخزين المقعد'
          },
          image: 'images/2371/Acc/TB CC/W_STORAGE_CLOTH.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VBJ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC79',
        },
        '3g4a6gwcxysh': {
          label: {
            en: 'Door Speaker - BOSE',
            ar: 'مكبر صوت الباب - BOSE'
          },
          image: 'images/2371/Acc/TB CC/DOOR_BOSE_SPEAKER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'UQA',
          brandItemCode: 'AC80',
        },
        r8ypn17xe1ao: {
          label: {
            en: 'BENCH CONSOLE',
            ar: 'كونسول بمقعد صفي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        la6n9w1lw34y: {
          label: {
            en: 'Front Bucker Seats & Console',
            ar: 'مقاعد أمامية حاضنة وكونسول'
          },
          image: 'images/2371/Acc/TB CC/BUCKET_CLOTH.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'A95',
          brandItemCode: 'AC81',
        },
        '37vy4x17zma1': {
          label: {
            en: 'Heated Front Seats & Steering Wheel',
            ar: 'مقاعد أمامية قابلة للتدفئة وعجلة قيادة قابلة للتدفئة'
          },
          image: 'images/2371/Acc/TB CC/HEATED_STEERING_SEATS.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'PCL',
          brandItemCode: 'AC82',
        },
        qzroidq5dh2h: {
          label: {
            en: 'PILLAR SPEAKER',
            ar: 'مكبر صوت العمود'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        '6zvsql40dfeu': {
          label: {
            en: 'PILLAR SPEAKER',
            ar: 'مكبر صوت العمود'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        r1of6om2fww7: {
          label: {
            en: 'Exhaust1',
            ar: '22 بوصة من الألومنيوم المطلي مع لمسات من الكروم'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '4ai8by09rqvh': {
          label: {
            en: 'Exhaust2',
            ar: '22 بوصة من الألومنيوم المطلي مع لمسات من الكروم'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '8fipb3d7mw06': {
          label: {
            en: 'Midnight Edition LTTB CC',
            ar: 'إصدار Midnight Edition LTTB CC'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ey2n7o70uqp2: {
          label: {
            en: 'Dark Appearance LTTB CC',
            ar: 'المظهر الداكن LTTB CC'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        e38raezqc0yr: {
          label: {
            en: 'Hitch View',
            ar: 'عرض موضع القطر'
          },
          image: 'images/2371/TB_Hitch_view.png',
          category: {
            en: 'Performance',
            ar: 'أداء'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'PZ8',
          brandItemCode: 'AC83',
        },
        '8bavf3nhxttl': {
          label: {
            en: '20" High Gloss Black-Painted Aluminum',
            ar: 'عجلات ألومنيوم مطلية باللون الأسود شديد اللمعان قياس 20 إنش'
          },
          image: 'images/2371/Acc/TB CC/Alloy_20in_High_Gloss_Std_bed.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RD5',
          brandItemCode: 'AC77',
        },
        rshtgcsxc87m: {
          label: {
            en: 'Fog lamp',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        jik37nn77ze2: {
          label: {
            en: 'hood logo',
            ar: 'شعار غطاء المحرك'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        i7nci40itn8y: {
          label: {
            en: 'Engine 2.7L TurboMax',
            ar: 'محرك TurboMax سعة 2.7 لتر'
          },
          image: 'images/2371/Acc/TB CC/engine.png',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        gp1ercwlphfc: {
          label: {
            en: 'Tow hook',
            ar: 'خطاف السحب'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        a3rwg17sz7f3: {
          label: {
            en: 'Tow hook',
            ar: 'خطاف السحب'
          },
          image: 'images/2371/Acc/TB CC/BLACK_HOOK.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '9b6tnvf421wy': {
          label: {
            en: 'Silverdao logo',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '1bfrm3gx9mcw': {
          label: {
            en: 'Silverdao logo1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        wx09nefcniov: {
          label: {
            en: 'Redline Decal',
            ar: 'ملصق Redline'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '8whocyzz2mpv': {
          label: {
            en: 'Spotter Mirror',
            ar: 'مرآة كشف النقطة العمياء'
          },
          image: 'images/2371/Acc/TB CC/SPOTTER_MIRROR.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        khdpkk7yyjzb: {
          label: {
            en: 'FLOOR_MATS',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        zu3ppcoocclt: {
          label: {
            en: 'ADAPTIVE CRUISE CONTROL',
            ar: 'نظام تثبيت السرعة التكيفي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        by9grbxo4aoa: {
          label: {
            en: 'Hitch View',
            ar: 'عرض موضع القطر'
          },
          image: 'images/2371/TB_Hitch_view.png',
          category: {
            en: 'Performance',
            ar: 'أداء'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'PZ8',
          brandItemCode: 'AC83',
        },
        '4vobyj2julb1': {
          label: {
            en: 'WO Bedliner',
            ar: 'بدون بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        jrk76pk085do: {
          label: {
            en: 'Bedliner',
            ar: 'بطانة الصندوق'
          },
          image: 'images/2371/features/TB/TB_Exhausts.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC78',
        },
        mozank45tss3: {
          label: {
            en: 'Multiflex tailgate without bedliner',
            ar: 'البوابة الخلفية Multi-Flex من دون بطانة الصندوق'
          },
          image: 'images/2371/features/TB/TB_Exhausts.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        qlekkubrc753: {
          label: {
            en: 'Multiflex gate with bedliner',
            ar: 'البوابة الخلفية Multi-Flex مع بطانة الصندوق'
          },
          image: 'images/2371/features/TB/TB_Exhausts.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'QK2',
          brandItemCode: 'AC84',
        },
        beuarz4vde0x: {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        zyg1ajt1tt55: {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '0pphlhk9sfsc': {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        voqklcsa3nah: {
          label: {
            en: 'Suspension_package_acc',
            ar: 'Suspension_package_acc'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        altpyb8zy1ho: {
          label: {
            en: 'HEAD UP DISPLAY1',
            ar: 'نظام العرض على الزجاج الأمامي1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
        },
        nzp13ofwquhk: {
          label: {
            en: 'HEAD UP DISPLAY',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'UV6'
        },
        p9fafesml8k3: {
          label: {
            en: 'IRVM1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        vopa9h4ufomx: {
          label: {
            en: 'IRVM2',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        hf6hrn5vorm4: {
          label: {
            en: 'Power-retractable Assist Steps',
            ar: 'درجات المساعدة على الصعود والنزول قابلة للسحب كهربائياً'
          },
          image: 'images/2371/Acc/TB CC/ACC_Assist_Steps_Chrome_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'BRS',
          brandItemCode: 'AC67',
        },
        '971lnvws25bw': {
          label: {
            en: 'ROOF LIGHT1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        z3atoyjjx1j4: {
          label: {
            en: 'Roof Light',
            ar: 'مصباح السقف'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        lxmkprqdxs62: {
          label: {
            en: 'ROOF LIGHT3',
            ar: 'ضوء السقف 3'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '87ge77v9ftll': {
          label: {
            en: 'SHORT BED TAILAGE2',
            ar: 'SHORT BED TAILAGE2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        mcpnwyf7lsx6: {
          label: {
            en: 'Padel Covers',
            ar: 'أغطية مبدلات السرعة'
          },
          image: 'images/2371/Acc/TB CC/ACC_Pedal Covers_Sport_Stainless_Steel.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        vv66hhabuo0n: {
          label: {
            en: 'Bowtie Logo Puddle Light',
            ar: 'إنارة أرضية أسفل الأبواب بشعار شفروليه'
          },
          image: 'images/2371/Acc/TB CC/ACC_Bowtie_Logo_Puddle_Light.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'S3I',
          excludeFromRecommendation: true,
          brandItemCode: 'AC69',
        },
        bk2f45i70lk6: {
          label: {
            en: 'Bowtie Graphic',
            ar: 'رسم شعار شفروليه'
          },
          image: 'images/2371/Acc/TB CC/ACC_Bowtie_Graphic_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SFZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC70',
        },
        '89kafvbu8vki': {
          label: {
            en: 'Illuiminated Front Bowtie',
            ar: 'شعار شفروليه أمامي مضيء'
          },
          image: 'images/2371/Acc/TB CC/ACC_Illuminated_Front_Bowtie_Except_ZR2.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          excludeFromRecommendation: true,
          brandItemCode: 'AC71',
        },
        ubiyf8t53qzl: {
          label: {
            en: 'Bed Mat Black',
            ar: 'حصيرة الصندوق باللون الأسود'
          },
          image: 'images/2371/Acc/TB CC/ACC_Bed Mat_Black_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VBR',
          excludeFromRecommendation: true,
          brandItemCode: 'AC72',
        },
        '5bs0mlsj81xs': {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صلب قابل للطي لصندوق الحمولة'
          },
          image: 'images/2371/Acc/TB CC/ACC_Hard_Folding_Tonneau_Cover_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VOZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC73',
        },
        ojnsp9mlwvqf: {
          label: {
            en: 'Sports Bar Bed Mounted',
            ar: 'بار رياضي مثبت على الصندوق'
          },
          image: 'images/2371/Acc/TB CC/ACC_Sports_Bar_Bed_Mounted_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        bch105mtur44: {
          label: {
            en: '18in High Gloss Black-Painted Aluminum Standard',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        vaffg14y2l1w: {
          label: {
            en: 'Steering System',
            ar: 'نظام التوجيه'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        roiy440sk7wq: {
          label: {
            en: 'Steering System2',
            ar: 'نظام التوجيه 2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        f78w22e8lpmg: {
          label: {
            en: 'Task lamp1',
            ar: 'مصباح المهام 1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        wuybyhmuok0k: {
          label: {
            en: 'Task lamp2',
            ar: 'مصباح المهام 2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '5a0xpsxa6ued': {
          label: {
            en: 'SHORT BED TAILAGE SYSTEM1',
            ar: 'نظام الباب الخلفي للصندوق القصير1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        jsgv4y4249i6: {
          label: {
            en: 'Short Bed Tailgate System',
            ar: 'نظام الباب الخلفي للصندوق القصير'
          },
          image: 'images/2371/features/TB/TB_Exhausts.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        iybopfjxc5pa: {
          label: {
            en: 'ORVM',
            ar: 'ORVM'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '7kvasj28jy0s': {
          label: {
            en: 'Seat_Storage__Armrest',
            ar: 'مساحة تخزين المقعد_مسند الذراع'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          brandItemCode: 'AC89',
        },
        '1nyebxioseb0': {
          label: {
            en: 'Seat Storage - Armrest',
            ar: 'مساحة تخزين المقعد - مسند الذراع'
          },
          image: 'images/2371/Acc/TB CC/W_STORAGE_CLOTH.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R7O',
          brandItemCode: 'AC89',
        },
        hvo5pzp4rqz3: {
          label: {
            en: 'Door Speaker - BOSE',
            ar: 'مكبر صوت الباب - BOSE'
          },
          image: 'images/2371/Acc/TB CC/DOOR_BOSE_SPEAKER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'UQA',
          brandItemCode: 'AC80',
        },
        gaz4jpsceins: {
          label: {
            en: 'BENCH CONSOLE',
            ar: 'كونسول بمقعد صفي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '1kl47g2ws5um': {
          label: {
            en: 'BUCKET CONSOLE',
            ar: 'كونسول بمقاعد حاضنة'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'D07',
          brandItemCode: 'AC90',
        },
        q8lzyk3t6ge3: {
          label: {
            en: 'Heated Steering - Front seats',
            ar: 'عجلة قيادة قابلة للتدفئة ومقاعد أمامية قابلة للتدفئة'
          },
          image: 'images/2371/Acc/TB CC/HEATED_STEERING_SEATS.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'KI3',
          brandItemCode: 'AC91',
        },
        '42g6mrn15b5v': {
          label: {
            en: 'PILLAR SPEAKER',
            ar: 'مكبر صوت العمود'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '0l48xpuwzt8e': {
          label: {
            en: 'PILLAR SPEAKER',
            ar: 'مكبر صوت العمود'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        t0erixwko2g6: {
          label: {
            en: 'Exhaust1',
            ar: '22 بوصة من الألومنيوم المطلي مع لمسات من الكروم'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        r6ihv55m085t: {
          label: {
            en: 'Exhaust2',
            ar: '22 بوصة من الألومنيوم المطلي مع لمسات من الكروم'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '9v54hle0okz0': {
          label: {
            en: 'Midnight Edition LTTB RC',
            ar: 'إصدار Midnight Edition LTTB RC'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        zmvz8kuaprfi: {
          label: {
            en: 'Dark Appearance LTTB RC',
            ar: 'المظهر الداكن LTTB RC'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        enwcx26f9mkk: {
          label: {
            en: '20" Painted Aluminum with machine face and grazen painted',
            ar: 'عجلات من الألومنيوم المطلي قياس 20 إنش مع واجهة آلية وطبقة من الطلاء المزخرف'
          },
          image: 'images/2371/Acc/RST/Alloy_20in_Painted_Aluminium_Short_Bed.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'NZN',
          brandItemCode: 'AC85',
        },
        aamcmr19ejqi: {
          label: {
            en: 'Fog lamp',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        pq8zmgmxh0v2: {
          label: {
            en: 'Engine, 5.3L EcoTec3 V8',
            ar: 'محرك EcoTec3 V8 سعة 5.3 لتر'
          },
          image: 'images/2371/Acc/LT/GM-5.3L-V-8-L83-Engine.jpg',
          category: {
            en: 'Performance',
            ar: 'أداء'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'L84',
          brandItemCode: 'AC86',
        },
        kdclty1lv6yc: {
          label: {
            en: 'hood logo',
            ar: 'شعار غطاء المحرك'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        x16xwfsr429z: {
          label: {
            en: '6.2L',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        brt3rx24ctz0: {
          label: {
            en: 'Tow hook',
            ar: 'خطاف السحب'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        j9vm0h8qens0: {
          label: {
            en: 'Tow hook',
            ar: 'خطاف السحب'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        gxg2jtly723c: {
          label: {
            en: 'ORVM',
            ar: 'ORVM'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        vp09insg1mki: {
          label: {
            en: 'Redline Decal',
            ar: 'ملصق Redline'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        c9nv2bfy9uu1: {
          label: {
            en: 'Spotter Mirror',
            ar: 'مرآة كشف النقطة العمياء'
          },
          image: 'images/2371/Acc/TB CC/SPOTTER_MIRROR.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        khw0krkakszm: {
          label: {
            en: 'FLOOR_MATS',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        fuwls9gkrtq0: {
          label: {
            en: 'Floor Liners- Front and Rear',
            ar: 'بطانات الأرضية - أمامية وخلفية'
          },
          image: 'images/2371/Acc/RST/FLOOR_LINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AAK',
          brandItemCode: 'AC87',
        },
        y6ntqxq1phyg: {
          label: {
            en: 'ADAPTIVE CRUISE CONTROL',
            ar: 'نظام تثبيت السرعة التكيفي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        v0uslhm8fb90: {
          label: {
            en: 'Hitch View',
            ar: 'عرض موضع القطر'
          },
          image: 'images/2371/Acc/RST/ACC_Assist_Steps_Chrome_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'PZ8',
          brandItemCode: 'AC83',
        },
        '24yy1luzf7j1': {
          label: {
            en: 'WO Bedliner',
            ar: 'بدون بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '225r2u3835jz': {
          label: {
            en: 'Bedliner',
            ar: 'بطانة الصندوق'
          },
          image: 'images/2371/Acc/RST/SHORT_DEFAULT_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC78',
        },
        '5qpvguu2ist8': {
          label: {
            en: 'Multiflex tailgate without bedliner',
            ar: 'البوابة الخلفية Multi-Flex من دون بطانة الصندوق'
          },
          image: 'images/2371/Acc/RST/SHORT_MULTI_FLEX_WO_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '8q49bxzncwev': {
          label: {
            en: 'Multiflex gate with bedliner',
            ar: 'البوابة الخلفية Multi-Flex مع بطانة الصندوق'
          },
          image: 'images/2371/Acc/RST/SHORT_MULTI_FLEX_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'QK2',
          brandItemCode: 'AC84',
        },
        lrv58ij88fde: {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '75bhbffo339e': {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        lsibu5dpt59e: {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '1qbbxch49uhf': {
          label: {
            en: 'Suspension_package_acc',
            ar: 'Suspension_package_acc'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ya6bffpuyjqh: {
          label: {
            en: 'HEAD UP DISPLAY1',
            ar: 'نظام العرض على الزجاج الأمامي1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
        },
        z2irqtn1o9vl: {
          label: {
            en: 'HEAD UP DISPLAY',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'UV6'
        },
        zsgsdtatb4ye: {
          label: {
            en: 'IRVM1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '7nfhymbny0tf': {
          label: {
            en: 'IRVM2',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '33wdst9gysz5': {
          label: {
            en: 'Assist Step  wheel to wheel Chrome',
            ar: 'درجات المساعدة على الصعود والنزول من الكروم تمتد من عجلة إلى عجلة'
          },
          image: 'images/2371/Acc/RST/ACC_Assist_Steps_Chrome_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'BVT',
          brandItemCode: 'AC88',
        },
        hyvbd9wmcrgv: {
          label: {
            en: 'ROOF LIGHT1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ec5gv425ndy7: {
          label: {
            en: 'Roof Light',
            ar: 'مصباح السقف'
          },
          image: 'images/2371/Acc/RST/ROOF_LIGHT_2.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '3kdfoa2ch3dg': {
          label: {
            en: 'ROOF LIGHT3',
            ar: 'ضوء السقف 3'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        jt57wm53sz8l: {
          label: {
            en: 'STD BED TAILAGE WO BEDLINER',
            ar: 'باب الصندوق الخلفي القياسي من دون بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        snvlft6p6hur: {
          label: {
            en: 'STD BED TAILAGE W BEDLINER',
            ar: 'باب الصندوق الخلفي القياسي مع بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        htnp53ovjc9a: {
          label: {
            en: 'Bed Tailgate Without Bedliner',
            ar: 'باب الصندوق الخلفي بدون بطانة'
          },
          image: 'images/2371/Acc/RST/SHORT_MULTI_FLEX_WO_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        dvl30nnebrlo: {
          label: {
            en: 'Bed Tailgate with Bedliner',
            ar: 'باب الصندوق الخلفي مع بطانة الصندوق'
          },
          image: 'images/2371/Acc/RST/SHORT_MULTI_FLEX_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '6b0wxy093zjp': {
          label: {
            en: 'Assist Steps 4 Inch Round Black',
            ar: 'درجات المساعدة على الصعود والنزول باللون الأسود ومستديرة قياس 4 إنش'
          },
          image: 'images/2371/Acc/RST/ACC_Assist_Steps_Black_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        kzm1rcqbiw5g: {
          label: {
            en: 'Assist steps chrome',
            ar: 'درجات المساعدة على الصعود والنزول من الكروم'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        yam3kafate2u: {
          label: {
            en: 'Padel Covers',
            ar: 'أغطية مبدلات السرعة'
          },
          image: 'images/2371/Acc/RST/ACC_Pedal Covers_Sport_Stainless_Steel.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        b5ivj0y9s8sf: {
          label: {
            en: 'Bowtie Logo Puddle Light',
            ar: 'إنارة أرضية أسفل الأبواب بشعار شفروليه'
          },
          image: 'images/2371/Acc/RST/ACC_Bowtie_Logo_Puddle_Light.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'S3I',
          excludeFromRecommendation: true,
          brandItemCode: 'AC69',
        },
        hc5h7z7u47nk: {
          label: {
            en: 'Bowtie Graphic',
            ar: 'رسم شعار شفروليه'
          },
          image: 'images/2371/Acc/RST/ACC_Bowtie_Graphic_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SFZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC70',
        },
        v45l2f9h5anp: {
          label: {
            en: 'Illuiminated Front Bowtie',
            ar: 'شعار شفروليه أمامي مضيء'
          },
          image: 'images/2371/Acc/RST/ACC_Illuminated_Front_Bowtie_Except_ZR2.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          excludeFromRecommendation: true,
          brandItemCode: 'AC71',
        },
        nd4551bx7a8i: {
          label: {
            en: 'Bed Mat Black',
            ar: 'حصيرة الصندوق باللون الأسود'
          },
          image: 'images/2371/Acc/RST/ACC_Bed Mat_Black_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VBR',
          excludeFromRecommendation: true,
          brandItemCode: 'AC72',
        },
        '0lzqlzooomyq': {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صلب قابل للطي لصندوق الحمولة'
          },
          image: 'images/2371/Acc/RST/ACC_Hard_Folding_Tonneau_Cover_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VOZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC73',
        },
        '6vrysp727zx0': {
          label: {
            en: 'Sports Bar Bed Mounted',
            ar: 'بار رياضي مثبت على الصندوق'
          },
          image: 'images/2371/Acc/RST/ACC_Sports_Bar_Bed_Mounted_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        flcdriu7beaw: {
          label: {
            en: '18in Bright Silver Painted Aluminum Standard_Low',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        wihl6kprivy0: {
          label: {
            en: 'Steering System',
            ar: 'نظام التوجيه'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        g2m4yrmyyynz: {
          label: {
            en: 'Steering System2',
            ar: 'نظام التوجيه 2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        bjxt08ns4nof: {
          label: {
            en: 'Task lamp1',
            ar: 'مصباح المهام 1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        hyk5vwlktq1x: {
          label: {
            en: 'Task lamp2',
            ar: 'مصباح المهام 2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        dpt33m49akus: {
          label: {
            en: 'SHORT BED TAILAGE SYSTEM1',
            ar: 'نظام الباب الخلفي للصندوق القصير1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        n94fzze0tvrt: {
          label: {
            en: 'Short Bed Tailgate System',
            ar: 'نظام الباب الخلفي للصندوق القصير'
          },
          image: 'images/2371/Acc/ZR2/SHORT_TAILGATE_POWER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '21zwbuxlcavw': {
          label: {
            en: 'Seat Storage - Armrest',
            ar: 'مساحة تخزين المقعد - مسند الذراع'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R7O',
          brandItemCode: 'AC89',
        },
        rvpnnu0k326r: {
          label: {
            en: 'Seat Storage - Armrest',
            ar: 'مساحة تخزين المقعد - مسند الذراع'
          },
          image: 'images/2371/Acc/RST/W_STORAGE_CLOTH.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R7O',
          brandItemCode: 'AC89',
        },
        hc2waknpvkej: {
          label: {
            en: 'Bucket Console',
            ar: 'كونسول بمقاعد حاضنة'
          },
          image: 'images/2371/Acc/RST/BUCKET_CLOTH.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'D07',
          brandItemCode: 'AC90',
        },
        n7dz0snnwmvo: {
          label: {
            en: 'Bench console',
            ar: 'كونسول بمقعد صفي'
          },
          image: 'images/2371/Acc/RST/BENCH_CLOTH.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        spmqj5ulgz86: {
          label: {
            en: 'Heated Steering - Front seats',
            ar: 'عجلة قيادة قابلة للتدفئة ومقاعد أمامية قابلة للتدفئة'
          },
          image: 'images/2371/Acc/RST/HEATED_STEERING_SEATS.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'KI3',
          brandItemCode: 'AC91',
        },
        '3ggimjowfl66': {
          label: {
            en: 'PILLAR SPEAKER',
            ar: 'مكبر صوت العمود'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '5dferxjng8my': {
          label: {
            en: 'PILLAR SPEAKER',
            ar: 'مكبر صوت العمود'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        bo65zmllt1ve: {
          label: {
            en: 'Fender badge',
            ar: 'شارة الرفرف'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        mzckwh973o1y: {
          label: {
            en: 'Fender badge',
            ar: 'شارة الرفرف'
          },
          image: 'images/2371/Acc/RST/Z71.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '11okflqv5eq9': {
          label: {
            en: 'HILL CONTROL',
            ar: 'التحكم في نزول التلال'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        '6xej21dk4z8n': {
          label: {
            en: 'Hill Control',
            ar: 'التحكم في نزول التلال'
          },
          image: 'images/2371/Acc/RST/W_HILL_CONTROL.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        '2gu367fy93cp': {
          label: {
            en: '4WD_SINGLE_TWO_SPEED',
            ar: 'نظام دفع رباعي بسرعتين'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        l0mv3qhmi42a: {
          label: {
            en: '4WD Single Two Speed',
            ar: 'نظام دفع رباعي بسرعتين'
          },
          image: 'images/2371/Acc/RST/4WD_TWO_SPEED.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        owknxlwm8qx1: {
          label: {
            en: 'STD BED EXHAUST',
            ar: 'عادم الصندوق القياسي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '2qvaqumopcbw': {
          label: {
            en: 'Bed Exhaust',
            ar: 'عادم الصندوق'
          },
          image: 'images/2371/Acc/RST/SHORT_DUAL_EXHAUST_RST.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '46y6qv6omn9t': {
          label: {
            en: '20" Painted Aluminum with machine face and grazen painted',
            ar: 'عجلات من الألومنيوم المطلي قياس 20 إنش مع واجهة آلية وطبقة من الطلاء المزخرف'
          },
          image: 'images/2371/Acc/RST/Alloy_20in_Painted_Aluminium_Short_Bed.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'NZN',
          brandItemCode: 'AC85',
        },
        l2uioho3tg37: {
          label: {
            en: 'Fog lamp',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '1qrv7uy2hn4u': {
          label: {
            en: 'Hood Logo',
            ar: 'شعار غطاء المحرك'
          },
          image: 'images/2371/Acc/RST/CHROME_WHEEL_TO_WHEEL.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        kw2owicdikzj: {
          label: {
            en: '6.2L',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '500b9sjr98qp': {
          label: {
            en: 'Tow hook',
            ar: 'خطاف السحب'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '1pl1livfn0m3': {
          label: {
            en: 'Tow hook',
            ar: 'خطاف السحب'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        fqfe8415hbb4: {
          label: {
            en: 'BLACK HOOK',
            ar: 'خطاف أسود'
          },
          image: 'images/2371/Acc/RST/ROOF_LIGHT_2.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        mn5mv4w5ovv1: {
          label: {
            en: 'ORVM',
            ar: 'ORVM'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ezjt7jvfmb01: {
          label: {
            en: 'Redline Decal',
            ar: 'ملصق Redline'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        e9tntv7n8acw: {
          label: {
            en: 'Spotter Mirror',
            ar: 'مرآة كشف النقطة العمياء'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        y7eynwbblyjx: {
          label: {
            en: 'FLOOR_MATS',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '4ri84tlxzf3i': {
          label: {
            en: 'ADAPTIVE CRUISE CONTROL',
            ar: 'نظام تثبيت السرعة التكيفي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ty7d3to02069: {
          label: {
            en: 'Hitch View',
            ar: 'عرض موضع القطر'
          },
          image: 'images/2371/Acc/RST/ACC_Assist_Steps_Chrome_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'PZ8',
          brandItemCode: 'AC83',
        },
        woxdn43eic08: {
          label: {
            en: 'WO Bedliner',
            ar: 'بدون بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        s73hn6clquce: {
          label: {
            en: 'Bedliner',
            ar: 'بطانة الصندوق'
          },
          image: 'images/2371/Acc/RST/SHORT_DEFAULT_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC78',
        },
        '2qgxzrzd98b7': {
          label: {
            en: 'Multiflex tailgate without bedliner',
            ar: 'البوابة الخلفية Multi-Flex من دون بطانة الصندوق'
          },
          image: 'images/2371/Acc/RST/SHORT_MULTI_FLEX_WO_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        lz8d2rq8l78n: {
          label: {
            en: 'Multiflex gate with bedliner',
            ar: 'البوابة الخلفية Multi-Flex مع بطانة الصندوق'
          },
          image: 'images/2371/Acc/RST/SHORT_MULTI_FLEX_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'QK2',
          brandItemCode: 'AC84',
        },
        '8u1n69n7f3tr': {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '94lak34bsaum': {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '2qh2y5dzlam2': {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '0mbo5g146xof': {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        i7ghu3y0onx1: {
          label: {
            en: 'Suspension_package_acc',
            ar: 'Suspension_package_acc'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        nzc2kkcjvxeg: {
          label: {
            en: 'HEAD UP DISPLAY1',
            ar: 'نظام العرض على الزجاج الأمامي1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
        },
        zcxfm0t5hiiy: {
          label: {
            en: 'HEAD UP DISPLAY',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'UV6'
        },
        '5kjnxltj8pbo': {
          label: {
            en: 'IRVM1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '3njxugdgv4k3': {
          label: {
            en: 'IRVM2',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        r8usax6b64p7: {
          label: {
            en: 'Power-retractable Assist Steps',
            ar: 'درجات المساعدة على الصعود والنزول قابلة للسحب كهربائياً'
          },
          image: 'images/2371/Acc/RST/ACC_Assist_Steps_Chrome_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'BRS',
          brandItemCode: 'AC67',
        },
        '9iia2nsc033z': {
          label: {
            en: 'ROOF LIGHT1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        yjx2x1bvum9k: {
          label: {
            en: 'Roof Light',
            ar: 'مصباح السقف'
          },
          image: 'images/2371/Acc/RST/ROOF_LIGHT_2.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        vjgwyj3p4805: {
          label: {
            en: 'ROOF LIGHT3',
            ar: 'ضوء السقف 3'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ozh8tlqamko3: {
          label: {
            en: 'STD BED TAILAGE WO BEDLINER',
            ar: 'باب الصندوق الخلفي القياسي من دون بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        nc672x9l24tp: {
          label: {
            en: 'STD BED TAILAGE W BEDLINER',
            ar: 'باب الصندوق الخلفي القياسي مع بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '3t7rttr6bx2z': {
          label: {
            en: 'Bed Tailgate Without Bedliner',
            ar: 'باب الصندوق الخلفي بدون بطانة'
          },
          image: 'images/2371/Acc/RST/SHORT_MULTI_FLEX_WO_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        b9dfy0g7ik1m: {
          label: {
            en: 'Bed Tailgate with Bedliner',
            ar: 'باب الصندوق الخلفي مع بطانة الصندوق'
          },
          image: 'images/2371/Acc/RST/SHORT_MULTI_FLEX_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        bfbuwrrfiao3: {
          label: {
            en: 'Assist Steps 4 Inch Round Black',
            ar: 'درجات المساعدة على الصعود والنزول باللون الأسود ومستديرة قياس 4 إنش'
          },
          image: 'images/2371/Acc/RST/ACC_Assist_Steps_Black_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        bxgkx6bmaw4a: {
          label: {
            en: 'Assist steps chrome',
            ar: 'درجات المساعدة على الصعود والنزول من الكروم'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '6y0f764end5z': {
          label: {
            en: 'Padel Covers',
            ar: 'أغطية مبدلات السرعة'
          },
          image: 'images/2371/Acc/RST/ACC_Pedal Covers_Sport_Stainless_Steel.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        wwfk2839r7od: {
          label: {
            en: 'Bowtie Logo Puddle Light',
            ar: 'إنارة أرضية أسفل الأبواب بشعار شفروليه'
          },
          image: 'images/2371/Acc/RST/ACC_Bowtie_Logo_Puddle_Light.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'S3I',
          excludeFromRecommendation: true,
          brandItemCode: 'AC69',
        },
        ipdv50xde60b: {
          label: {
            en: 'Bowtie Graphic',
            ar: 'رسم شعار شفروليه'
          },
          image: 'images/2371/Acc/RST/ACC_Bowtie_Graphic_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SFZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC70',
        },
        '1kio6fr3sbmv': {
          label: {
            en: 'Illuiminated Front Bowtie',
            ar: 'شعار شفروليه أمامي مضيء'
          },
          image: 'images/2371/Acc/RST/ACC_Illuminated_Front_Bowtie_Except_ZR2.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          excludeFromRecommendation: true,
          brandItemCode: 'AC71',
        },
        xyt93f3ss4h8: {
          label: {
            en: 'Bed Mat Black',
            ar: 'حصيرة الصندوق باللون الأسود'
          },
          image: 'images/2371/Acc/RST/ACC_Bed Mat_Black_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VBR',
          excludeFromRecommendation: true,
          brandItemCode: 'AC72',
        },
        '5i7159u1mhw3': {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صلب قابل للطي لصندوق الحمولة'
          },
          image: 'images/2371/Acc/RST/ACC_Hard_Folding_Tonneau_Cover_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VOZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC73',
        },
        rmpop006w5i0: {
          label: {
            en: 'Sports Bar Bed Mounted',
            ar: 'بار رياضي مثبت على الصندوق'
          },
          image: 'images/2371/Acc/RST/ACC_Sports_Bar_Bed_Mounted_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        szwkjiq1besx: {
          label: {
            en: '18in Bright Silver Painted Aluminum Standard',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        s8cxrszuo348: {
          label: {
            en: 'Steering System',
            ar: 'نظام التوجيه'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '7jy59inwr81c': {
          label: {
            en: 'Steering System2',
            ar: 'نظام التوجيه 2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        icxa4nro7vby: {
          label: {
            en: 'Task lamp1',
            ar: 'مصباح المهام 1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        vhux4rln8ytz: {
          label: {
            en: 'Task lamp2',
            ar: 'مصباح المهام 2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        rt9elr84l8kj: {
          label: {
            en: 'SHORT BED TAILAGE SYSTEM1',
            ar: 'نظام الباب الخلفي للصندوق القصير1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        lq0wkwzd2nbv: {
          label: {
            en: 'Short Bed Tailgate System',
            ar: 'نظام الباب الخلفي للصندوق القصير'
          },
          image: 'images/2371/Acc/ZR2/SHORT_TAILGATE_POWER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        aet80k88ue5a: {
          label: {
            en: 'Seat Storage - Armrest',
            ar: 'مساحة تخزين المقعد - مسند الذراع'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R7O',
          brandItemCode: 'AC89',
        },
        '39f8mfcupfp4': {
          label: {
            en: 'Bucket console',
            ar: 'كونسول بمقاعد حاضنة'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'D07',
          brandItemCode: 'AC90',
        },
        wwbecwxqv2md: {
          label: {
            en: 'Heated Steering Front Seats',
            ar: 'عجلة قيادة قابلة للتدفئة ومقاعد أمامية قابلة للتدفئة'
          },
          image: 'images/2371/Acc/RST/HEATED_STEERING_SEATS.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'KA1',
          brandItemCode: 'AC91',
        },
        pqedc214n44x: {
          label: {
            en: 'PILLAR SPEAKER',
            ar: 'مكبر صوت العمود'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        t1xwoo934ln6: {
          label: {
            en: 'Fender badge',
            ar: 'شارة الرفرف'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '69b99z5t9il4': {
          label: {
            en: 'Fender badge',
            ar: 'شارة الرفرف'
          },
          image: 'images/2371/Acc/RST/Z71.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        l34j8pmcdqzg: {
          label: {
            en: 'HILL CONTROL',
            ar: 'التحكم في نزول التلال'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        wtyvo38z3fe1: {
          label: {
            en: 'Hill Control',
            ar: 'التحكم في نزول التلال'
          },
          image: 'images/2371/Acc/RST/W_HILL_CONTROL.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        kt1s36efscg4: {
          label: {
            en: '4WD_SINGLE_TWO_SPEED',
            ar: 'نظام دفع رباعي بسرعتين'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        vwiga2uaok0o: {
          label: {
            en: '4WD Single Two Speed',
            ar: 'نظام دفع رباعي بسرعتين'
          },
          image: 'images/2371/Acc/RST/4WD_TWO_SPEED.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        y0bo6qow8ci9: {
          label: {
            en: 'STD BED EXHAUST',
            ar: 'عادم الصندوق القياسي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '7nvudf5dm6vl': {
          label: {
            en: 'Bed Exhaust',
            ar: 'عادم الصندوق'
          },
          image: 'images/2371/Acc/RST/SHORT_DUAL_EXHAUST_RST.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        aj24okukxiy1: {
          label: {
            en: '20" Painted Aluminum with machine face and grazen painted',
            ar: 'عجلات من الألومنيوم المطلي قياس 20 إنش مع واجهة آلية وطبقة من الطلاء المزخرف'
          },
          image: 'images/2371/Acc/LT/Alloy_20in_Painted_Aluminium_Short_Bed.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'NZN',
          brandItemCode: 'AC85',
        },
        wzz8zk92pz7y: {
          label: {
            en: 'Fog lamp',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        zbss582phyw0: {
          label: {
            en: 'hood logo',
            ar: 'شعار غطاء المحرك'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '7yg1c0lftxiq': {
          label: {
            en: 'Engine, 5.3L EcoTec3 V8',
            ar: 'محرك EcoTec3 V8 سعة 5.3 لتر'
          },
          image: 'images/2371/Acc/LT/GM-5.3L-V-8-L83-Engine.jpg',
          category: {
            en: 'Performance',
            ar: 'أداء'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'L84',
          brandItemCode: 'AC86',
        },
        zzf69gky2znz: {
          label: {
            en: 'Tow hook',
            ar: 'خطاف السحب'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        qbgpy7iuqies: {
          label: {
            en: 'Tow hook',
            ar: 'خطاف السحب'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        tadkb0ery03z: {
          label: {
            en: 'ORVM',
            ar: 'ORVM'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '8dng36kha4c1': {
          label: {
            en: 'Redline Decal',
            ar: 'ملصق Redline'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        rk8q3jqbjbox: {
          label: {
            en: 'Spotter Mirror',
            ar: 'مرآة كشف النقطة العمياء'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        olgrvm8pgxh4: {
          label: {
            en: 'FLOOR_MATS',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ruel9xhclpcx: {
          label: {
            en: 'ADAPTIVE CRUISE CONTROL',
            ar: 'نظام تثبيت السرعة التكيفي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        prwoau4fn32x: {
          label: {
            en: 'Hitch View',
            ar: 'عرض موضع القطر'
          },
          image: 'images/2371/Acc/LT/CHROME_WHEEL_TO_WHEEL.jpg',
          category: {
            en: 'Performance',
            ar: 'أداء'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'PZ8',
          brandItemCode: 'AC83',
        },
        eu5v4taho21m: {
          label: {
            en: 'WO Bedliner',
            ar: 'بدون بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        d3efjusxmmgq: {
          label: {
            en: 'Bedliner',
            ar: 'بطانة الصندوق'
          },
          image: 'images/2371/Acc/LT/SHORT_DEFAULT_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC78',
        },
        ecl9pgc6k8oj: {
          label: {
            en: 'Multiflex tailgate without bedliner',
            ar: 'البوابة الخلفية Multi-Flex من دون بطانة الصندوق'
          },
          image: 'images/2371/Acc/LT/SHORT_MULTI_FLEX_WO_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '098o326v3gqp': {
          label: {
            en: 'Multiflex gate with bedliner',
            ar: 'البوابة الخلفية Multi-Flex مع بطانة الصندوق'
          },
          image: 'images/2371/Acc/LT/SHORT_MULTI_FLEX_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'QK2',
          brandItemCode: 'AC84',
        },
        '7aiecrltj7tk': {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        mu9y84dv1tic: {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '33wdpgx4u2xg': {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '68fzloqy2pln': {
          label: {
            en: 'Suspension_package_acc',
            ar: 'Suspension_package_acc'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        jko5gfx33c1q: {
          label: {
            en: 'HEAD UP DISPLAY1',
            ar: 'نظام العرض على الزجاج الأمامي1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
        },
        '0ugj03jtis6p': {
          label: {
            en: 'HEAD UP DISPLAY',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'UV6'
        },
        '528qxx1fs3ch': {
          label: {
            en: 'IRVM1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        vyyz49o1fuco: {
          label: {
            en: 'IRVM2',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        wd1bgstn1jih: {
          label: {
            en: 'Assist Step  wheel to wheel Chrome',
            ar: 'درجات المساعدة على الصعود والنزول من الكروم تمتد من عجلة إلى عجلة'
          },
          image: 'images/2371/Acc/LT/ACC_Assist_Steps_Chrome_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'BVT',
          brandItemCode: 'AC88',
        },
        j88iwbncrgrf: {
          label: {
            en: 'ROOF LIGHT1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        tploudv07w4z: {
          label: {
            en: 'Roof Light',
            ar: 'مصباح السقف'
          },
          image: 'images/2371/Acc/LT/ROOF_LIGHT_2.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        yp9y0hi01wg6: {
          label: {
            en: 'ROOF LIGHT3',
            ar: 'ضوء السقف 3'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        smygy8byvbp1: {
          label: {
            en: 'STD BED TAILAGE WO BEDLINER',
            ar: 'باب الصندوق الخلفي القياسي من دون بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '53np0q7rtn46': {
          label: {
            en: 'STD BED TAILAGE W BEDLINER',
            ar: 'باب الصندوق الخلفي القياسي مع بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        fp1wk36t4i6h: {
          label: {
            en: 'Bed Tailgate without bedliner',
            ar: 'باب الصندوق الخلفي بدون بطانة'
          },
          image: 'images/2371/Acc/LT/SHORT_MULTI_FLEX_WO_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        o70k784u3bzq: {
          label: {
            en: 'Bed Tailgate with bedliner',
            ar: 'باب الصندوق الخلفي مع بطانة الصندوق'
          },
          image: 'images/2371/Acc/LT/SHORT_MULTI_FLEX_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        e66zxhb8mc6h: {
          label: {
            en: 'Assist Steps 4 Inch Round Black',
            ar: 'درجات المساعدة على الصعود والنزول باللون الأسود ومستديرة قياس 4 إنش'
          },
          image: 'images/2371/Acc/LT/ACC_Assist_Steps_Black_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        aq4ttf5pw2tx: {
          label: {
            en: 'Assist steps chrome',
            ar: 'درجات المساعدة على الصعود والنزول من الكروم'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        uyslmwyjroc2: {
          label: {
            en: 'Padel Covers',
            ar: 'أغطية مبدلات السرعة'
          },
          image: 'images/2371/Acc/LT/ACC_Pedal Covers_Sport_Stainless_Steel.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        zgf4ep9h0dln: {
          label: {
            en: 'Bowtie Logo Puddle Light',
            ar: 'إنارة أرضية أسفل الأبواب بشعار شفروليه'
          },
          image: 'images/2371/Acc/LT/ACC_Bowtie_Logo_Puddle_Light.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'S3I',
          excludeFromRecommendation: true,
          brandItemCode: 'AC69',
        },
        '7swuj7dak6hy': {
          label: {
            en: 'Bowtie Graphic',
            ar: 'رسم شعار شفروليه'
          },
          image: 'images/2371/Acc/LT/ACC_Bowtie_Graphic_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SFZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC70',
        },
        rwpz5ie6e62k: {
          label: {
            en: 'Illuiminated Front Bowtie',
            ar: 'شعار شفروليه أمامي مضيء'
          },
          image: 'images/2371/Acc/LT/ACC_Illuminated_Front_Bowtie_Except_ZR2.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          excludeFromRecommendation: true,
          brandItemCode: 'AC71',
        },
        h6pbcv9f8t1n: {
          label: {
            en: 'Bed Mat Black',
            ar: 'حصيرة الصندوق باللون الأسود'
          },
          image: 'images/2371/Acc/LT/ACC_Bed Mat_Black_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VBR',
          excludeFromRecommendation: true,
          brandItemCode: 'AC72',
        },
        '0p6lt9cbjp4m': {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صلب قابل للطي لصندوق الحمولة'
          },
          image: 'images/2371/Acc/LT/ACC_Hard_Folding_Tonneau_Cover_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VOZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC73',
        },
        '3g0ubv7q5d0s': {
          label: {
            en: 'Sports Bar Bed Mounted',
            ar: 'بار رياضي مثبت على الصندوق'
          },
          image: 'images/2371/Acc/LT/ACC_Sports_Bar_Bed_Mounted_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        w9w3fgevl9ih: {
          label: {
            en: '18in Bright Silver Painted Aluminum Standard_Low',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        o3tzyrq63mdk: {
          label: {
            en: 'Steering System',
            ar: 'نظام التوجيه'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        xv3t34gxllpn: {
          label: {
            en: 'Steering System2',
            ar: 'نظام التوجيه 2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '31vuqrwv8fpy': {
          label: {
            en: 'Task lamp1',
            ar: 'مصباح المهام 1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        i3ccwie2wugj: {
          label: {
            en: 'SHORT BED TAILAGE SYSTEM1',
            ar: 'نظام الباب الخلفي للصندوق القصير1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        rm81gaacdptt: {
          label: {
            en: 'Short Bed Tailgate System',
            ar: 'نظام الباب الخلفي للصندوق القصير'
          },
          image: 'images/2371/Acc/ZR2/SHORT_TAILGATE_POWER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        bw1tbpr57px1: {
          label: {
            en: 'SEAT STORAGE_ARMREST',
            ar: 'مساحة تخزين المقعد ومسند الذراع'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          brandItemCode: 'AC89',
        },
        o74dtmfoe14w: {
          label: {
            en: 'Seat Storage - Armrest',
            ar: 'مساحة تخزين المقعد - مسند الذراع'
          },
          image: 'images/2371/Acc/LT/W_STORAGE_CLOTH.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R7O',
          brandItemCode: 'AC89',
        },
        mmt5fkupfwgm: {
          label: {
            en: 'Front Bucket Seats and Console',
            ar: 'مقاعد أمامية حاضنة وكونسول'
          },
          image: 'images/2371/Acc/LT/LT_BUCKET_CLOTH.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'A95',
          brandItemCode: 'AC93',
        },
        yi923erhdqki: {
          label: {
            en: 'Bench console',
            ar: 'كونسول بمقعد صفي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        dmav4tpowddq: {
          label: {
            en: 'HEATED STEERING FRONT SEATS',
            ar: 'عجلة قيادة قابلة للتدفئة ومقاعد أمامية قابلة للتدفئة'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          brandItemCode: 'AC91',
        },
        '6zeu4yky77ch': {
          label: {
            en: 'PILLAR SPEAKER',
            ar: 'مكبر صوت العمود'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '9wfw66rgxn7x': {
          label: {
            en: 'Bose Sound System, premium 7-speaker system with Richbass woofer',
            ar: 'نظام صوت Bose الفاخر بـ7 مكبرات صوت مع مكبر صوت Richbass'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          brandItemCode: 'AC159',
        },
        '7x9zcgeppbrn': {
          label: {
            en: 'Fender badge',
            ar: 'شارة الرفرف'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        h9cjgpp9xv7e: {
          label: {
            en: 'Fender badge',
            ar: 'شارة الرفرف'
          },
          image: 'images/2371/Acc/LT/Z71.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '1gb7vhwjppqi': {
          label: {
            en: 'HILL CONTROL',
            ar: 'التحكم في نزول التلال'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        n58nvecii5t2: {
          label: {
            en: 'Hill Control',
            ar: 'التحكم في نزول التلال'
          },
          image: 'images/2371/Acc/LT/W_HILL_CONTROL.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        '2gvshcyn14y2': {
          label: {
            en: '4WD_SINGLE_TWO_SPEED',
            ar: 'نظام دفع رباعي بسرعتين'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        '9p8hhjnpsfhp': {
          label: {
            en: '4WD Single Two Speed',
            ar: 'نظام دفع رباعي بسرعتين'
          },
          image: 'images/2371/Acc/LT/4WD_TWO_SPEED.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        u5wbwvw4lwyb: {
          label: {
            en: 'STD BED EXHAUST',
            ar: 'عادم الصندوق القياسي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '7k8pshah93ij': {
          label: {
            en: 'Bed Exhaust',
            ar: 'عادم الصندوق'
          },
          image: 'images/2371/Acc/LT/SHORT_DUAL_EXHAUST_LT.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ppwzigedaacf: {
          label: {
            en: '20" Painted Aluminum with machine face and grazen painted',
            ar: 'عجلات من الألومنيوم المطلي قياس 20 إنش مع واجهة آلية وطبقة من الطلاء المزخرف'
          },
          image: 'images/2371/Acc/LT/Alloy_20in_Painted_Aluminium_Short_Bed.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'NZN',
          brandItemCode: 'AC85',
        },
        ikq01lpmnxig: {
          label: {
            en: 'Fog lamp',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '4l2ch5gt1jei': {
          label: {
            en: 'hood logo',
            ar: 'شعار غطاء المحرك'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        u13ejr7mha46: {
          label: {
            en: '6.2L',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        w8m6jkrjpf1n: {
          label: {
            en: 'Tow hook',
            ar: 'خطاف السحب'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        qflhjgii0zs4: {
          label: {
            en: 'Tow hook',
            ar: 'خطاف السحب'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        tldc3lal50a5: {
          label: {
            en: 'ORVM',
            ar: 'ORVM'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        qh35c8m63fni: {
          label: {
            en: 'Redline Decal',
            ar: 'ملصق Redline'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        m8lvefhp9cjj: {
          label: {
            en: 'Spotter Mirror',
            ar: 'مرآة كشف النقطة العمياء'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        roemf657c1p9: {
          label: {
            en: 'FLOOR_MATS',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        qxnhtszm5ndk: {
          label: {
            en: 'ADAPTIVE CRUISE CONTROL',
            ar: 'نظام تثبيت السرعة التكيفي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        zzq11yb2fvlz: {
          label: {
            en: 'Hitch View',
            ar: 'عرض موضع القطر'
          },
          image: 'images/2371/LT_RWD_HITCH_VIEW.jpg',
          category: {
            en: 'Performance',
            ar: 'أداء'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'PZ8',
          brandItemCode: 'AC83',
        },
        '0r0maueg9zyo': {
          label: {
            en: 'normal wo bedliner',
            ar: 'قياسي من دون بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '9sfws9yqkuc3': {
          label: {
            en: 'Bedliner',
            ar: 'بطانة الصندوق'
          },
          image: 'images/2371/Acc/LT/SHORT_DEFAULT_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC78',
        },
        '7zuqcg1sjgzs': {
          label: {
            en: 'Multiflex tailgate without bedliner',
            ar: 'البوابة الخلفية Multi-Flex من دون بطانة الصندوق'
          },
          image: 'images/2371/Acc/LT/SHORT_MULTI_FLEX_WO_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '1e4rnxy0qlis': {
          label: {
            en: 'Multiflex gate with bedliner',
            ar: 'البوابة الخلفية Multi-Flex مع بطانة الصندوق'
          },
          image: 'images/2371/Acc/LT/SHORT_MULTI_FLEX_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'QK2',
          brandItemCode: 'AC84',
        },
        jo4o2zby05cf: {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '08hmxlpd4m8f': {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '03hkpg6h1hkc': {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        a8cqkrto3nke: {
          label: {
            en: 'Suspension_package_acc',
            ar: 'Suspension_package_acc'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        csmney59ys4x: {
          label: {
            en: 'HEAD UP DISPLAY1',
            ar: 'نظام العرض على الزجاج الأمامي1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
        },
        d4exwpm90nts: {
          label: {
            en: 'HEAD UP DISPLAY',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'UV6'
        },
        fpg8rp26eee3: {
          label: {
            en: 'IRVM1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        mpmwposb1tye: {
          label: {
            en: 'IRVM2',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '2ywwchobf7f5': {
          label: {
            en: 'Power-retractable Assist Steps',
            ar: 'درجات المساعدة على الصعود والنزول قابلة للسحب كهربائياً'
          },
          image: 'images/2371/Acc/LT/ACC_Assist_Steps_Chrome_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'BRS',
          brandItemCode: 'AC67',
        },
        ma8rbv12vcwb: {
          label: {
            en: 'ROOF LIGHT1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '8htt9z21rexy': {
          label: {
            en: 'Roof Light',
            ar: 'مصباح السقف'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ty4mln8g58kr: {
          label: {
            en: 'ROOF LIGHT3',
            ar: 'ضوء السقف 3'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        vvt74t57t45w: {
          label: {
            en: 'STD BED TAILAGE WO BEDLINER',
            ar: 'باب الصندوق الخلفي القياسي من دون بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        x6op3smx7n02: {
          label: {
            en: 'STD BED TAILAGE W BEDLINER',
            ar: 'باب الصندوق الخلفي القياسي مع بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        vxwve40pucxw: {
          label: {
            en: 'Bed Tailgate without bedliner',
            ar: 'باب الصندوق الخلفي بدون بطانة'
          },
          image: 'images/2371/Acc/LT/SHORT_MULTI_FLEX_WO_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        sumurt3l7hh7: {
          label: {
            en: 'Bed Tailgate with bedliner',
            ar: 'باب الصندوق الخلفي مع بطانة الصندوق'
          },
          image: 'images/2371/Acc/LT/SHORT_MULTI_FLEX_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '8i0esom5o4ks': {
          label: {
            en: 'Assist Steps 4 Inch Round Black',
            ar: 'درجات المساعدة على الصعود والنزول باللون الأسود ومستديرة قياس 4 إنش'
          },
          image: 'images/2371/Acc/LT/ACC_Assist_Steps_Black_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        z5r3van9yglu: {
          label: {
            en: 'Assist steps chrome',
            ar: 'درجات المساعدة على الصعود والنزول من الكروم'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        b0ncvdhudq5p: {
          label: {
            en: 'Padel Covers',
            ar: 'أغطية مبدلات السرعة'
          },
          image: 'images/2371/Acc/LT/ACC_Pedal Covers_Sport_Stainless_Steel.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        piwvb7c4q251: {
          label: {
            en: 'Bowtie Logo Puddle Light',
            ar: 'إنارة أرضية أسفل الأبواب بشعار شفروليه'
          },
          image: 'images/2371/Acc/LT/ACC_Bowtie_Logo_Puddle_Light.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'S3I',
          excludeFromRecommendation: true,
          brandItemCode: 'AC69',
        },
        i02sca1ic9ai: {
          label: {
            en: 'Bowtie Graphic',
            ar: 'رسم شعار شفروليه'
          },
          image: 'images/2371/Acc/LT/ACC_Bowtie_Graphic_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SFZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC70',
        },
        '9e4mhz7b5v47': {
          label: {
            en: 'Illuiminated Front Bowtie',
            ar: 'شعار شفروليه أمامي مضيء'
          },
          image: 'images/2371/Acc/LT/ACC_Illuminated_Front_Bowtie_Except_ZR2.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          excludeFromRecommendation: true,
          brandItemCode: 'AC71',
        },
        wmexvu2t7sgn: {
          label: {
            en: 'Bed Mat Black',
            ar: 'حصيرة الصندوق باللون الأسود'
          },
          image: 'images/2371/Acc/LT/ACC_Bed Mat_Black_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VBR',
          excludeFromRecommendation: true,
          brandItemCode: 'AC72',
        },
        xsi63vni3i4w: {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صلب قابل للطي لصندوق الحمولة'
          },
          image: 'images/2371/Acc/LT/ACC_Hard_Folding_Tonneau_Cover_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VOZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC73',
        },
        e2ng81orh764: {
          label: {
            en: 'Sports Bar Bed Mounted',
            ar: 'بار رياضي مثبت على الصندوق'
          },
          image: 'images/2371/Acc/LT/ACC_Sports_Bar_Bed_Mounted_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        xqb4ho3sx176: {
          label: {
            en: '18in Bright Silver Painted Aluminum Standard',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '4bt1ywim06o7': {
          label: {
            en: 'Steering System',
            ar: 'نظام التوجيه'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        n7xm4bdk03yy: {
          label: {
            en: 'Steering System2',
            ar: 'نظام التوجيه 2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        b16sm4roet5l: {
          label: {
            en: 'Task lamp1',
            ar: 'مصباح المهام 1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        emd69mkez0yx: {
          label: {
            en: 'Task lamp2',
            ar: 'مصباح المهام 2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        cvfp1430s1is: {
          label: {
            en: 'SHORT BED TAILAGE SYSTEM1',
            ar: 'نظام الباب الخلفي للصندوق القصير1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ipxrgvwp54iq: {
          label: {
            en: 'Short Bed Tailgate System',
            ar: 'نظام الباب الخلفي للصندوق القصير'
          },
          image: 'images/2371/Acc/ZR2/SHORT_TAILGATE_POWER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '0k97mfhk07bk': {
          label: {
            en: 'SEAT STORAGE_ARMREST',
            ar: 'مساحة تخزين المقعد ومسند الذراع'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          brandItemCode: 'AC89',
        },
        hurcnmxhohxu: {
          label: {
            en: 'Bucket console',
            ar: 'كونسول بمقاعد حاضنة'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'D07',
          brandItemCode: 'AC90',
        },
        '84bjgtdelash': {
          label: {
            en: 'Bench console',
            ar: 'كونسول بمقعد صفي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        '0xpp8n3v94pe': {
          label: {
            en: 'HEATED STEERING FRONT SEATS',
            ar: 'عجلة قيادة قابلة للتدفئة ومقاعد أمامية قابلة للتدفئة'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          brandItemCode: 'AC91',
        },
        wclr4whopkwn: {
          label: {
            en: 'PILLAR SPEAKER',
            ar: 'مكبر صوت العمود'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        j2rynhdwnwk0: {
          label: {
            en: 'Fender badge',
            ar: 'شارة الرفرف'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '0w8gce7si79p': {
          label: {
            en: 'Fender badge',
            ar: 'شارة الرفرف'
          },
          image: 'images/2371/Acc/LT/Z71.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ai8r4op2zjgy: {
          label: {
            en: 'HILL CONTROL',
            ar: 'التحكم في نزول التلال'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '4hj05h5lb75y': {
          label: {
            en: 'Hill Control',
            ar: 'التحكم في نزول التلال'
          },
          image: 'images/2371/Acc/LT/W_HILL_CONTROL.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        jt71de53b4iz: {
          label: {
            en: '4WD_SINGLE_TWO_SPEED',
            ar: 'نظام دفع رباعي بسرعتين'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        edt07rmrzik9: {
          label: {
            en: '4WD Single Two Speed',
            ar: 'نظام دفع رباعي بسرعتين'
          },
          image: 'images/2371/Acc/LT/4WD_TWO_SPEED.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        ai7m6an2ha1o: {
          label: {
            en: 'STD BED EXHAUST',
            ar: 'عادم الصندوق القياسي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '0ln8s93srv7z': {
          label: {
            en: 'Bed Exhaust',
            ar: 'عادم الصندوق'
          },
          image: 'images/2371/Acc/LT/SHORT_DUAL_EXHAUST_LT.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ho29v2z419zo: {
          label: {
            en: '20" Painted Aluminum with machine face and grazen painted',
            ar: 'عجلات من الألومنيوم المطلي قياس 20 إنش مع واجهة آلية وطبقة من الطلاء المزخرف'
          },
          image: 'images/2371/Acc/LT/Alloy_20in_Painted_Aluminium_Short_Bed.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'NZN',
          brandItemCode: 'AC85',
        },
        w7cg8b79m7pz: {
          label: {
            en: 'Fog lamp',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        z1t3kt43hg14: {
          label: {
            en: 'hood logo',
            ar: 'شعار غطاء المحرك'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        symi0bnl8ndg: {
          label: {
            en: '6.2L',
            ar: ''
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        tzs5k4rso8bg: {
          label: {
            en: 'Tow hook',
            ar: 'خطاف السحب'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '28vqgmus9n1x': {
          label: {
            en: 'Tow hook',
            ar: 'خطاف السحب'
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        di75vldkd36j: {
          label: {
            en: 'ORVM',
            ar: 'ORVM'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        qwmzyw8beg99: {
          label: {
            en: 'Redline Decal',
            ar: 'ملصق Redline'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        apnydor0mu4w: {
          label: {
            en: 'Spotter Mirror',
            ar: 'مرآة كشف النقطة العمياء'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '8a3qrlzcwi5d': {
          label: {
            en: 'FLOOR_MATS',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ngkcd58rhw6h: {
          label: {
            en: 'ADAPTIVE CRUISE CONTROL',
            ar: 'نظام تثبيت السرعة التكيفي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '2budg42ejesq': {
          label: {
            en: 'Hitch View',
            ar: 'عرض موضع القطر'
          },
          image: 'images/2371/LT_RWD_HITCH_VIEW.jpg',
          category: {
            en: 'Performance',
            ar: 'أداء'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'PZ8',
          brandItemCode: 'AC83',
        },
        qi6iu757kmgy: {
          label: {
            en: 'WO Bedliner',
            ar: 'بدون بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        maodhxzlje1a: {
          label: {
            en: 'Bedliner',
            ar: 'بطانة الصندوق'
          },
          image: 'images/2371/Acc/LT/SHORT_DEFAULT_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'CGN',
          brandItemCode: 'AC78',
        },
        pxxb7o4i00rd: {
          label: {
            en: 'Multiflex tailgate without bedliner',
            ar: 'البوابة الخلفية Multi-Flex من دون بطانة الصندوق'
          },
          image: 'images/2371/Acc/LT/SHORT_MULTI_FLEX_WO_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '9budfczm63vf': {
          label: {
            en: 'Multiflex gate with bedliner',
            ar: 'البوابة الخلفية Multi-Flex مع بطانة الصندوق'
          },
          image: 'images/2371/Acc/LT/SHORT_MULTI_FLEX_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'QK2',
          brandItemCode: 'AC84',
        },
        b73gxc5vzydw: {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        j2u21hp1iq3m: {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        s86l6qxejbu3: {
          label: {
            en: '',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '723dflxfu4h4': {
          label: {
            en: 'Suspension_package_acc',
            ar: 'Suspension_package_acc'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        s0w2vauwxkzc: {
          label: {
            en: 'HEAD UP DISPLAY1',
            ar: 'نظام العرض على الزجاج الأمامي1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
        },
        mtfze63vc2hh: {
          label: {
            en: 'HEAD UP DISPLAY',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'UV6'
        },
        tziink3u85l9: {
          label: {
            en: 'IRVM1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ht34v61wtf55: {
          label: {
            en: 'IRVM2',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '622lixmwsekz': {
          label: {
            en: 'Power-retractable Assist Steps',
            ar: 'درجات المساعدة على الصعود والنزول قابلة للسحب كهربائياً'
          },
          image: 'images/2371/Acc/LT/ACC_Assist_Steps_Chrome_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'BRS',
          brandItemCode: 'AC67',
        },
        ntizrbsmh1gb: {
          label: {
            en: 'ROOF LIGHT1',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        xhqfq9g8b8ry: {
          label: {
            en: 'Roof Light',
            ar: 'مصباح السقف'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ylarduisiw6g: {
          label: {
            en: 'ROOF LIGHT3',
            ar: 'ضوء السقف 3'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        kp4guhh2w0rf: {
          label: {
            en: 'STD BED TAILAGE WO BEDLINER',
            ar: 'باب الصندوق الخلفي القياسي من دون بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '2tdpg3de5tw5': {
          label: {
            en: 'STD BED TAILAGE W BEDLINER',
            ar: 'باب الصندوق الخلفي القياسي مع بطانة الصندوق'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '3gtxmwncg3li': {
          label: {
            en: 'Bed Tailgate without bedliner',
            ar: 'باب الصندوق الخلفي بدون بطانة'
          },
          image: 'images/2371/Acc/LT/SHORT_MULTI_FLEX_WO_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        j3w8rwxjuhs9: {
          label: {
            en: 'Bed Tailgate with bedliner',
            ar: 'باب الصندوق الخلفي مع بطانة الصندوق'
          },
          image: 'images/2371/Acc/LT/SHORT_MULTI_FLEX_W_BEDLINER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ftzwe1jn4e4i: {
          label: {
            en: 'Assist Steps 4 Inch Round Black',
            ar: 'درجات المساعدة على الصعود والنزول باللون الأسود ومستديرة قياس 4 إنش'
          },
          image: 'images/2371/Acc/LT/ACC_Assist_Steps_Black_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RVS',
          brandItemCode: 'AC68',
        },
        inq0pozyllrv: {
          label: {
            en: 'Assist steps chrome',
            ar: 'درجات المساعدة على الصعود والنزول من الكروم'
          },
          image: 'images/2371/Acc/LT/ACC_Assist_Steps_Chrome_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        rfqovmmh3alm: {
          label: {
            en: 'Padel Covers',
            ar: 'أغطية مبدلات السرعة'
          },
          image: 'images/2371/Acc/LT/ACC_Pedal Covers_Sport_Stainless_Steel.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        g0b9grs14rhs: {
          label: {
            en: 'Bowtie Logo Puddle Light',
            ar: 'إنارة أرضية أسفل الأبواب بشعار شفروليه'
          },
          image: 'images/2371/Acc/LT/ACC_Bowtie_Logo_Puddle_Light.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'S3I',
          excludeFromRecommendation: true,
          brandItemCode: 'AC69',
        },
        '8hq3v6l0hkm3': {
          label: {
            en: 'Bowtie Graphic',
            ar: 'رسم شعار شفروليه'
          },
          image: 'images/2371/Acc/LT/ACC_Bowtie_Graphic_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SFZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC70',
        },
        mzu41xcr9a36: {
          label: {
            en: 'Illuiminated Front Bowtie',
            ar: 'شعار شفروليه أمامي مضيء'
          },
          image: 'images/2371/Acc/LT/ACC_Illuminated_Front_Bowtie_Except_ZR2.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          excludeFromRecommendation: true,
          brandItemCode: 'AC71',
        },
        kvuebey0uhz5: {
          label: {
            en: 'Bed Mat Black',
            ar: 'حصيرة الصندوق باللون الأسود'
          },
          image: 'images/2371/Acc/LT/ACC_Bed Mat_Black_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VBR',
          excludeFromRecommendation: true,
          brandItemCode: 'AC72',
        },
        zqbevqp2jr1l: {
          label: {
            en: 'Hard Folding Tonneau Cover',
            ar: 'غطاء صلب قابل للطي لصندوق الحمولة'
          },
          image: 'images/2371/Acc/LT/ACC_Hard_Folding_Tonneau_Cover_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VOZ',
          excludeFromRecommendation: true,
          brandItemCode: 'AC73',
        },
        hegqbt2xqlo8: {
          label: {
            en: 'Sports Bar Bed Mounted',
            ar: 'بار رياضي مثبت على الصندوق'
          },
          image: 'images/2371/Acc/LT/ACC_Sports_Bar_Bed_Mounted_CC.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SBY',
          brandItemCode: 'AC74',
        },
        '8b23t59k57j1': {
          label: {
            en: '18in Bright Silver Painted Aluminum Standard',
            ar: ''
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        or5gwme29v7n: {
          label: {
            en: 'Steering System',
            ar: 'نظام التوجيه'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        bp09rms79955: {
          label: {
            en: 'Steering System2',
            ar: 'نظام التوجيه 2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        vbx75pxycfex: {
          label: {
            en: 'Task lamp1',
            ar: 'مصباح المهام 1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        p6rrhwl4i2ml: {
          label: {
            en: 'Task lamp2',
            ar: 'مصباح المهام 2'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '1obrn72vzijr': {
          label: {
            en: 'SHORT BED TAILAGE SYSTEM1',
            ar: 'نظام الباب الخلفي للصندوق القصير1'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        iytido69x6ut: {
          label: {
            en: 'Short Bed Tailgate System',
            ar: 'نظام الباب الخلفي للصندوق القصير'
          },
          image: 'images/2371/Acc/ZR2/SHORT_TAILGATE_POWER.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        wt5lpfvsfo72: {
          label: {
            en: 'SEAT STORAGE_ARMREST',
            ar: 'مساحة تخزين المقعد ومسند الذراع'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          brandItemCode: 'AC89',
        },
        gzdwn4sibgmf: {
          label: {
            en: 'Bucket console',
            ar: 'كونسول بمقاعد حاضنة'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'D07',
          brandItemCode: 'AC90',
        },
        w85x6ywvjrg0: {
          label: {
            en: 'Bench console',
            ar: 'كونسول بمقعد صفي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '6kdz9iccj0ao': {
          label: {
            en: 'HEATED STEERING FRONT SEATS',
            ar: 'عجلة قيادة قابلة للتدفئة ومقاعد أمامية قابلة للتدفئة'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          brandItemCode: 'AC91',
        },
        '18ahvekaj1v4': {
          label: {
            en: 'PILLAR SPEAKER',
            ar: 'مكبر صوت العمود'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ba02cdvd1o3m: {
          label: {
            en: 'Fender badge',
            ar: 'شارة الرفرف'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '98e9rbbpf68a': {
          label: {
            en: 'Fender badge',
            ar: 'شارة الرفرف'
          },
          image: 'images/2371/Acc/LT/Z71.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        fkdw5mju9dcs: {
          label: {
            en: 'HILL CONTROL',
            ar: 'التحكم في نزول التلال'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        cm47z7tb0x2p: {
          label: {
            en: 'Hill Control',
            ar: 'التحكم في نزول التلال'
          },
          image: 'images/2371/Acc/LT/W_HILL_CONTROL.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        mn5hw8cgflef: {
          label: {
            en: '4WD_SINGLE_TWO_SPEED',
            ar: 'نظام دفع رباعي بسرعتين'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        ikgkl3ydphv6: {
          label: {
            en: '4WD Single Two Speed',
            ar: 'نظام دفع رباعي بسرعتين'
          },
          image: 'images/2371/Acc/LT/4WD_TWO_SPEED.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        cm1unghtwzn0: {
          label: {
            en: 'STD BED EXHAUST',
            ar: 'عادم الصندوق القياسي'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        gijf0jl9aj8m: {
          label: {
            en: 'Bed Exhaust',
            ar: 'عادم الصندوق'
          },
          image: 'images/2371/Acc/LT/SHORT_DUAL_EXHAUST_LT.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        }
      },
      packages: {
        x7vtyrufcgr0: {
          label: {
            en: 'Z71 Suspension Package',
            ar: 'باقة التعليق Z71'
          },
          image: 'images/2371/features/HC/HC_Side Step.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Z71 Suspension package comes with Z71 Off road suspension, Z71 Fender Badge, All Terrain Tires.',
            ar: 'تتضمن باقة التعليق Z71 نظام تعليق Z71 للطرق الوعرة، وشارة Z71 على الرفرف، وإطارات لجميع التضاريس.'
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'Z71',
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Z71 Off road suspension',
                ar: 'نظام تعليق Z71 للطرق الوعرة'
              },
              accessoryDescription: {
                en: 'Z71 Off road suspension',
                ar: 'نظام تعليق Z71 للطرق الوعرة'
              },
              image: 'images/2371/features/HC/HC_Tailgate.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Z71 Fender Badge',
                ar: 'شارة رفرف Z71'
              },
              accessoryDescription: {
                en: 'Z71 Fender Badge',
                ar: 'شارة رفرف Z71'
              },
              image: 'images/2371/Acc/HC/Z71.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'All Terrain Tires',
                ar: 'إطارات لجميع التضاريس'
              },
              accessoryDescription: {
                en: 'All Terrain Tires',
                ar: 'إطارات لجميع التضاريس'
              },
              image: 'images/2371/features/HC/HC_Tailgate.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            }
          ],
          brandItemCode: 'PA23',
        },
        'x7vtyrufcgr0-my26': {
          label: {
            en: 'Z71 Suspension Package',
            ar: 'باقة التعليق Z71'
          },
          image: 'images/2371/features/HC/HC_Side Step.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Z71 Suspension package comes with Z71 Off road suspension, Z71 Fender Badge, All Terrain Tires.',
            ar: 'تتضمن باقة التعليق Z71 نظام تعليق Z71 للطرق الوعرة، وشارة Z71 على الرفرف، وإطارات لجميع التضاريس.'
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'Z71',
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Z71 Off road suspension',
                ar: 'نظام تعليق Z71 للطرق الوعرة'
              },
              accessoryDescription: {
                en: 'Z71 Off road suspension',
                ar: 'نظام تعليق Z71 للطرق الوعرة'
              },
              image: 'images/2371/features/HC/HC_Tailgate.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Z71 Fender Badge',
                ar: 'شارة رفرف Z71'
              },
              accessoryDescription: {
                en: 'Z71 Fender Badge',
                ar: 'شارة رفرف Z71'
              },
              image: 'images/2371/Acc/HC/Z71.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'All Terrain Tires',
                ar: 'إطارات لجميع التضاريس'
              },
              accessoryDescription: {
                en: 'All Terrain Tires',
                ar: 'إطارات لجميع التضاريس'
              },
              image: 'images/2371/features/HC/HC_Tailgate.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            }
          ],
          brandItemCode: 'PA23',
        },
        jv7fw2fjab9g: {
          label: {
            en: 'Technology Package',
            ar: 'باقة التكنولوجيا'
          },
          image: 'images/2371/features/HC/HC_Side Step.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Technology Package includes Rear Camera Mirror and 15" Head-up Display',
            ar: 'تتضمن باقة التكنولوجيا كاميرا الرؤية الخلفية ونظام العرض على الزجاج الأمامي قياس 15 إنش'
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'CWM',
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Rear Camera Mirror',
                ar: 'مرآة الكاميرا الخلفية'
              },
              accessoryDescription: {
                en: 'Rear Camera Mirror',
                ar: 'مرآة الكاميرا الخلفية'
              },
              image: 'images/2371/Acc/HC/irvm.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: '15" Heads-Up Display',
                ar: 'نظام العرض على الزجاج الأمامي قياس 15 إنش'
              },
              accessoryDescription: {
                en: '15" Heads-Up Display',
                ar: 'نظام العرض على الزجاج الأمامي قياس 15 إنش'
              },
              image: 'images/2371/Acc/HC/W_HUD.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            }
          ],
          brandItemCode: 'PA03',
        },
        aypont1ipdwm: {
          label: {
            en: 'Midnight Edition Package',
            ar: 'باقة Midnight Edition'
          },
          image: 'images/2371/Midnight_Edition_HC.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Midnight Edition Package comes with Dark headlamp bezel, Black Hood Badging, Grille and Lower skid plate, Black Exhaust Tips, Mirror caps, Door handles, Black Body side moldings and DLO moldings, 20" High Gloss Black painted Aluminium wheels and 275/60R20 All Terrain Tyres.',
            ar: 'تتضمن باقة Midnight Edition حواف داكنة للمصابيح الأمامية، وشعارات سوداء على غطاء المحرك، وشبكًا أماميًا ولوح حماية سفليًا باللون الأسود، وأطراف عادم سوداء، وأغطية مرايا ومقابض أبواب سوداء، وقوالب جانبية للهيكل وقوالب DLO باللون الأسود، وعجلات ألومنيوم مطلية باللون الأسود شديد اللمعان قياس 20 إنش، وإطارات لجميع التضاريس قياس 275/60R20.'
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'WJP',
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Dark headlamp bezel',
                ar: 'حواف داكنة للمصابيح الأمامية'
              },
              accessoryDescription: {
                en: 'Dark headlamp bezel',
                ar: 'حواف داكنة للمصابيح الأمامية'
              },
              image: 'images/2371/HC_Healamp_Bezel.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Black hood badging, Grille, Lower skid plate',
                ar: 'شعار غطاء المحرك باللون الأسود، الشبك، لوحة الانزلاق السفلية'
              },
              accessoryDescription: {
                en: 'Black hood badging, Grille, Lower skid plate',
                ar: 'شعار غطاء المحرك باللون الأسود، الشبك، لوحة الانزلاق السفلية'
              },
              image: 'images/2371/HC_Hood_Badging.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Exhaust Tips, Mirror caps, Door handles',
                ar: 'أطراف عادم سوداء، أغطية مرايا، مقابض أبواب'
              },
              accessoryDescription: {
                en: 'Black Exhaust Tips, Mirror caps, Door handles',
                ar: 'أطراف عادم سوداء، أغطية مرايا، مقابض أبواب'
              },
              image: 'images/2371/HC_Mirror_Caps.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Black body side moldings, and DLO moldings',
                ar: 'قوالب جانبية للهيكل وقوالب DLO باللون الأسود'
              },
              accessoryDescription: {
                en: 'Black body side moldings, and DLO moldings',
                ar: 'قوالب جانبية للهيكل وقوالب DLO باللون الأسود'
              },
              image: 'images/2371/HC_DLO_Moldings.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: '20" High gloss Black painted aluminum wheels',
                ar: 'عجلات ألومنيوم مطلية باللون الأسود شديد اللمعان قياس 20 إنش'
              },
              accessoryDescription: {
                en: '20" High gloss Black painted aluminum wheels',
                ar: 'عجلات ألومنيوم مطلية باللون الأسود شديد اللمعان قياس 20 إنش'
              },
              image: 'images/2371/HC_High_Gloss.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: '275/60R20 All Terrain Tyres',
                ar: 'إطارات 275/60R20 لجميع التضاريس'
              },
              accessoryDescription: {
                en: '275/60R20 All Terrain Tyres',
                ar: 'إطارات 275/60R20 لجميع التضاريس'
              },
              image: 'images/2371/HC_High_Gloss.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            }
          ],
          brandItemCode: 'PA21',
        },
        tc5utu4e26lj: {
          label: {
            en: 'Technology Package',
            ar: 'باقة التكنولوجيا'
          },
          image: 'images/2371/Acc/ZR2/ACC_Assist_Steps_Chrome_CC.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Technology Package includes Rear Camera Mirror, 15" Heads-Up Display, Adaptive cruise control, Power Tilt & Telescoping Steering Column.',
            ar: 'تتضمن باقة التكنولوجيا كاميرا الرؤية الخلفية، ونظام العرض على الزجاج الأمامي قياس 15 إنش، ونظام تثبيت السرعة التكيفي، وعمود توجيه قابل للإمالة والامتداد كهربائياً.'
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'CWM',
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Rear Camera Mirror',
                ar: 'مرآة الكاميرا الخلفية'
              },
              accessoryDescription: {
                en: 'Rear Camera Mirror',
                ar: 'مرآة الكاميرا الخلفية'
              },
              image: 'images/2371/Acc/ZR2/AUTO_AND_CAMERA_IRVM.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: '15" Heads-Up Display',
                ar: 'نظام العرض على الزجاج الأمامي قياس 15 إنش'
              },
              accessoryDescription: {
                en: '15" Heads-Up Display',
                ar: 'نظام العرض على الزجاج الأمامي قياس 15 إنش'
              },
              image: 'images/2371/Acc/ZR2/W_HUD.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Adaptive Cruise Control',
                ar: 'نظام تثبيت السرعة التكيفي'
              },
              accessoryDescription: {
                en: 'Adaptive Cruise Control',
                ar: 'نظام تثبيت السرعة التكيفي'
              },
              image: 'images/2371/Acc/ZR2/ADAPTIVE_CRUISE_CONTROL.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Power Tilt & Telescoping Steering Column',
                ar: 'عمود توجيه قابل للإمالة والامتداد كهربائيًا'
              },
              accessoryDescription: {
                en: 'Power Tilt & Telescoping Steering Column',
                ar: 'عمود توجيه قابل للإمالة والامتداد كهربائيًا'
              },
              image: 'images/2371/Acc/ZR2/STEERING_POWER_CLOTH.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            }
          ],
          brandItemCode: 'PA03',
        },
        '35nezp8v8im7': {
          label: {
            en: 'Dark Appearance Package',
            ar: 'باقة المظهر الداكن'
          },
          image: 'images/2371/Dark_Appearance_ZR2.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Dark Appearance Package comes with Black Tow Hooks, Black Badging and Decals, Black Exhaust Tips and 18" Gloss Black Wheels.',
            ar: 'تتضمن باقة المظهر الداكن خطافات سحب وشارات وملصقات وأطراف عادم باللون الأسود، وعجلات سوداء شديدة اللمعان قياس 18 إنش.'
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'CWK',
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Black Tow Hooks',
                ar: 'خطافات سحب سوداء'
              },
              accessoryDescription: {
                en: 'Black Tow Hooks',
                ar: 'خطافات سحب سوداء'
              },
              image: 'images/2371/ZR2_Black_Tow_Hooks.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Badging and Decals',
                ar: 'شارات وملصقات سوداء'
              },
              accessoryDescription: {
                en: 'Black Badging and Decals',
                ar: 'شارات وملصقات سوداء'
              },
              image: 'images/2371/ZR2_Black_Badging.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Dark headlamp bezel',
                ar: 'حواف داكنة للمصابيح الأمامية'
              },
              accessoryDescription: {
                en: 'Dark headlamp bezel',
                ar: 'حواف داكنة للمصابيح الأمامية'
              },
              image: 'images/2371/DA_headlamp_bezel.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: '18" Gloss Black Wheels',
                ar: 'عجلات سوداء شديدة اللمعان قياس 18 إنش'
              },
              accessoryDescription: {
                en: '18" Gloss Black Wheels',
                ar: 'عجلات سوداء شديدة اللمعان قياس 18 إنش'
              },
              image: 'images/2371/DA_Black_Wheels.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            }
          ],
          brandItemCode: 'PA22',
        },
        '6zyk48o23wh4': {
          label: {
            en: 'Midnight Edition Package',
            ar: 'باقة Midnight Edition'
          },
          image: 'images/2371/Midnight_Edition_TB_CC.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Midnight Edition Package comes with Dark headlamp bezel, Black Silverado Badging and Black Midnight Badging.',
            ar: 'تتضمن باقة Midnight Edition حواف داكنة للمصابيح الأمامية، وشعارات سيلفرادو باللون الأسود، وشعارات Midnight باللون الأسود.'
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'WJP',
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Dark headlamp bezel',
                ar: 'حواف داكنة للمصابيح الأمامية'
              },
              accessoryDescription: {
                en: 'Dark headlamp bezel',
                ar: 'حواف داكنة للمصابيح الأمامية'
              },
              image: 'images/2371/TB_CC_MIDNIGHT_Dark_Headlamp_Bezel.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Silverado Badging',
                ar: 'شعارات سيلفرادو باللون الأسود'
              },
              accessoryDescription: {
                en: 'Black Silverado Badging',
                ar: 'شعارات سيلفرادو باللون الأسود'
              },
              image: 'images/2371/TB_CC_MIDNIGHT_Black_Silverado_Badging.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Midnight Badging',
                ar: 'شعارات Midnight باللون الأسود'
              },
              accessoryDescription: {
                en: 'Black Midnight Badging',
                ar: 'شعارات Midnight باللون الأسود'
              },
              image: 'images/2371/TB_CC_MIDNIGHT_Black_Midnight_Badging.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            }
          ],
          brandItemCode: 'PA21',
        },
        '6zyk48o23wh4-my26': {
          label: {
            en: 'Midnight Edition Package',
            ar: 'باقة Midnight Edition'
          },
          image: 'images/2371/Midnight_Edition_TB_CC.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Midnight Edition Package comes with Dark headlamp bezel, Black Silverado Badging and Black Midnight Badging.',
            ar: 'تتضمن باقة Midnight Edition حواف داكنة للمصابيح الأمامية، وشعارات سيلفرادو باللون الأسود، وشعارات Midnight باللون الأسود.'
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'WJP',
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Dark headlamp bezel',
                ar: 'حواف داكنة للمصابيح الأمامية'
              },
              accessoryDescription: {
                en: 'Dark headlamp bezel',
                ar: 'حواف داكنة للمصابيح الأمامية'
              },
              image: 'images/2371/TB_CC_MIDNIGHT_Dark_Headlamp_Bezel.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Silverado Badging',
                ar: 'شعارات سيلفرادو باللون الأسود'
              },
              accessoryDescription: {
                en: 'Black Silverado Badging',
                ar: 'شعارات سيلفرادو باللون الأسود'
              },
              image: 'images/2371/TB_CC_MIDNIGHT_Black_Silverado_Badging.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Midnight Badging',
                ar: 'شعارات Midnight باللون الأسود'
              },
              accessoryDescription: {
                en: 'Black Midnight Badging',
                ar: 'شعارات Midnight باللون الأسود'
              },
              image: 'images/2371/TB_CC_MIDNIGHT_Black_Midnight_Badging.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            }
          ],
          brandItemCode: 'PA21',
        },
        vo9k10vqq8pc: {
          label: {
            en: 'Midnight Edition Package',
            ar: 'باقة Midnight Edition'
          },
          image: 'images/2371/Midnight_Edition_LTTB_RC.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Midnight Edition Package comes with Dark headlamp bezel, Black Silverado Badging and Black Midnight Badging.',
            ar: 'تتضمن باقة Midnight Edition حواف داكنة للمصابيح الأمامية، وشعارات سيلفرادو باللون الأسود، وشعارات Midnight باللون الأسود.'
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'WJP',
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Dark headlamp bezel',
                ar: 'حواف داكنة للمصابيح الأمامية'
              },
              accessoryDescription: {
                en: 'Dark headlamp bezel',
                ar: 'حواف داكنة للمصابيح الأمامية'
              },
              image: 'images/2371/TB_CC_NIGHT_Dark_Headlamp_Bezel.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Silverado Badging',
                ar: 'شعارات سيلفرادو باللون الأسود'
              },
              accessoryDescription: {
                en: 'Black Silverado Badging',
                ar: 'شعارات سيلفرادو باللون الأسود'
              },
              image: 'images/2371/TB_CC_NIGHT_Black_Silverado_Badging.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Midnight Badging',
                ar: 'شعارات Midnight باللون الأسود'
              },
              accessoryDescription: {
                en: 'Black Midnight Badging',
                ar: 'شعارات Midnight باللون الأسود'
              },
              image: 'images/2371/TB_CC_NIGHT_Black_Midnight_Badging.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            }
          ],
          brandItemCode: 'PA21',
        },
        ep1xd71oo073: {
          label: {
            en: 'Dark Appearance Package',
            ar: 'باقة المظهر الداكن'
          },
          image: 'images/2371/Dark_App_TB.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Dark Appearance Package comes with Black Chrome Single Exhaust, Black Tow Hooks and Black Badging and Decals.',
            ar: 'تتضمن باقة المظهر الداكن عادمًا أحاديًا من الكروم الأسود، وخطافات سحب سوداء، وشارات وملصقات سوداء.'
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'CWK',
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Black Chrome Single Exhaust',
                ar: 'عادم أحادي من الكروم الأسود'
              },
              accessoryDescription: {
                en: 'Black Chrome Single Exhaust',
                ar: 'عادم أحادي من الكروم الأسود'
              },
              image: 'images/2371/TB_CC_MID_Black_Single_Exhaust.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Tow Hooks',
                ar: 'خطافات سحب سوداء'
              },
              accessoryDescription: {
                en: 'Black Tow Hooks',
                ar: 'خطافات سحب سوداء'
              },
              image: 'images/2371/TB_CC_MID_Black_Tow_Hooks.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Badging and Decals',
                ar: 'شارات وملصقات سوداء'
              },
              accessoryDescription: {
                en: 'Black Badging and Decals',
                ar: 'شارات وملصقات سوداء'
              },
              image: 'images/2371/TB_CC_MID_Black_Badging.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            }
          ],
          brandItemCode: 'PA22',
        },
        '1hjssljlu8nl': {
          label: {
            en: 'Suspension Package',
            ar: 'حزمة التعليق'
          },
          image: 'images/2371/RST_CC_Z71.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Suspension package comes with Off road suspension, 2-speed transfer case with autotrac, Hill descent control, Dual Exhaust outlets, and Z71 badge.',
            ar: 'تتضمن باقة التعليق نظام تعليق للطرق الوعرة، وعلبة نقل حركة بسرعتين مع نظام Autotrac، ونظام التحكم بنزول المنحدرات، ومخرجَي عادم، وشارة Z71.'
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'PDZ',
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Off road suspension',
                ar: 'نظام تعليق للطرق الوعرة'
              },
              accessoryDescription: {
                en: 'Off road suspension',
                ar: 'نظام تعليق للطرق الوعرة'
              },
              image: 'images/2371/RST_CC_Z71.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: '2-speed transfer case with autotrac',
                ar: 'علبة نقل حركة بسرعتين مع نظام Autotrac'
              },
              accessoryDescription: {
                en: '2-speed transfer case with autotrac',
                ar: 'علبة نقل حركة بسرعتين مع نظام Autotrac'
              },
              image: 'images/2371/RST_CC_4WD_TWO_SPEED.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Hill descent control',
                ar: 'نظام التحكم بنزول المنحدرات'
              },
              accessoryDescription: {
                en: 'Hill descent control',
                ar: 'نظام التحكم بنزول المنحدرات'
              },
              image: 'images/2371/RST_CC_W_HILL_CONTROL.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Dual Exhaust outlets',
                ar: 'منافذ عادم مزدوجة'
              },
              accessoryDescription: {
                en: 'Dual Exhaust outlets',
                ar: 'منافذ عادم مزدوجة'
              },
              image: 'images/2371/RST_CC_SHORT_DUAL_EXHAUST_RST.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Z71 badge',
                ar: 'شارة Z71'
              },
              accessoryDescription: {
                en: 'Z71 badge',
                ar: 'شارة Z71'
              },
              image: 'images/2371/RST_CC_Z71.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            }
          ],
          brandItemCode: 'PA20',
        },
        z6ushx99342q: {
          label: {
            en: 'Suspension Package',
            ar: 'حزمة التعليق'
          },
          image: 'images/2371/RST_RC_Z71.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Suspension package comes with Off road suspension, 2-speed transfer case with autotrac, Hill descent control, Dual Exhaust outlets, and Z71 badge.',
            ar: 'تتضمن باقة التعليق نظام تعليق للطرق الوعرة، وعلبة نقل حركة بسرعتين مع نظام Autotrac، ونظام التحكم بنزول المنحدرات، ومخرجَي عادم، وشارة Z71.'
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'PDZ',
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Off road suspension',
                ar: 'نظام تعليق للطرق الوعرة'
              },
              accessoryDescription: {
                en: 'Off road suspension',
                ar: 'نظام تعليق للطرق الوعرة'
              },
              image: 'images/2371/RST_RC_Z71.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: '2-speed transfer case with autotrac',
                ar: 'علبة نقل حركة بسرعتين مع نظام Autotrac'
              },
              accessoryDescription: {
                en: '2-speed transfer case with autotrac',
                ar: 'علبة نقل حركة بسرعتين مع نظام Autotrac'
              },
              image: 'images/2371/RST_RC_4WD_TWO_SPEED.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Hill descent control',
                ar: 'نظام التحكم بنزول المنحدرات'
              },
              accessoryDescription: {
                en: 'Hill descent control',
                ar: 'نظام التحكم بنزول المنحدرات'
              },
              image: 'images/2371/RST_RC_W_HILL_CONTROL.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Dual Exhaust outlets',
                ar: 'منافذ عادم مزدوجة'
              },
              accessoryDescription: {
                en: 'Dual Exhaust outlets',
                ar: 'منافذ عادم مزدوجة'
              },
              image: 'images/2371/RST_RC_STD_DUAL_EXHAUST_RST.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Z71 badge',
                ar: 'شارة Z71'
              },
              accessoryDescription: {
                en: 'Z71 badge',
                ar: 'شارة Z71'
              },
              image: 'images/2371/RST_RC_Z71.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            }
          ],
          brandItemCode: 'PA20',
        },
        lv8qzqdsgiws: {
          label: {
            en: 'Suspension Package',
            ar: 'حزمة التعليق'
          },
          image: 'images/2371/LT_RC_Z71.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Suspension package comes with Off road suspension, 2-speed transfer case with autotrac, Hill descent control, Dual Exhaust outlets, and Z71 badge.',
            ar: 'تتضمن باقة التعليق نظام تعليق للطرق الوعرة، وعلبة نقل حركة بسرعتين مع نظام Autotrac، ونظام التحكم بنزول المنحدرات، ومخرجَي عادم، وشارة Z71.'
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'PDZ',
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Off road suspension',
                ar: 'نظام تعليق للطرق الوعرة'
              },
              accessoryDescription: {
                en: 'Off road suspension',
                ar: 'نظام تعليق للطرق الوعرة'
              },
              image: 'images/2371/features/LT_CC/LT_CC_Wheels.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: '2-speed transfer case with autotrac',
                ar: 'علبة نقل حركة بسرعتين مع نظام Autotrac'
              },
              accessoryDescription: {
                en: '2-speed transfer case with autotrac',
                ar: 'علبة نقل حركة بسرعتين مع نظام Autotrac'
              },
              image: 'images/2371/features/LT_CC/LT_CC_Wheels.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Hill descent control',
                ar: 'نظام التحكم بنزول المنحدرات'
              },
              accessoryDescription: {
                en: 'Hill descent control',
                ar: 'نظام التحكم بنزول المنحدرات'
              },
              image: 'images/2371/LT_CC_W_HILL_CONTROL.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Dual Exhaust outlets',
                ar: 'منافذ عادم مزدوجة'
              },
              accessoryDescription: {
                en: 'Dual Exhaust outlets',
                ar: 'منافذ عادم مزدوجة'
              },
              image: 'images/2371/LT_CC_SHORT_DUAL_EXHAUST.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Z71 badge',
                ar: 'شارة Z71'
              },
              accessoryDescription: {
                en: 'Z71 badge',
                ar: 'شارة Z71'
              },
              image: 'images/2371/LT_RC_Z71.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            }
          ],
          brandItemCode: 'PA20',
        },
        syyctxz5vsvj: {
          label: {
            en: 'Suspension Package',
            ar: 'حزمة التعليق'
          },
          image: 'images/2371/LT_RC_Suspension.png',
          category: {
            en: 'Packages',
            ar: 'الباقات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Suspension package comes with Off road suspension, 2-speed transfer case with autotrac, Hill descent control, Dual Exhaust outlets, and Z71 badge.',
            ar: 'تتضمن باقة التعليق نظام تعليق للطرق الوعرة، وعلبة نقل حركة بسرعتين مع نظام Autotrac، ونظام التحكم بنزول المنحدرات، ومخرجَي عادم، وشارة Z71.'
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'PDZ',
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Off road suspension',
                ar: 'نظام تعليق للطرق الوعرة'
              },
              accessoryDescription: {
                en: 'Off road suspension',
                ar: 'نظام تعليق للطرق الوعرة'
              },
              image: 'images/2371/LT_RC_Suspension.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: '2-speed transfer case with autotrac',
                ar: 'علبة نقل حركة بسرعتين مع نظام Autotrac'
              },
              accessoryDescription: {
                en: '2-speed transfer case with autotrac',
                ar: 'علبة نقل حركة بسرعتين مع نظام Autotrac'
              },
              image: 'images/2371/features/LT_CC/LT_CC_Wheels.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Hill descent control',
                ar: 'نظام التحكم بنزول المنحدرات'
              },
              accessoryDescription: {
                en: 'Hill descent control',
                ar: 'نظام التحكم بنزول المنحدرات'
              },
              image: 'images/2371/LT_RC_W_HILL_CONTROL.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Dual Exhaust outlets',
                ar: 'منافذ عادم مزدوجة'
              },
              accessoryDescription: {
                en: 'Dual Exhaust outlets',
                ar: 'منافذ عادم مزدوجة'
              },
              image: 'images/2371/LT_RC_STD_DUAL_EXHAUST_LT.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Z71 badge',
                ar: 'شارة Z71'
              },
              accessoryDescription: {
                en: 'Z71 badge',
                ar: 'شارة Z71'
              },
              image: 'images/2371/LT_RC_Z71.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            }
          ],
          brandItemCode: 'PA20',
        }
      },
      interactions: {
        a8jlgfd8evx4: {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        e33wj494id3s: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        auuqx6b61pgx: {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف'
          },
          type: 'interaction'
        },
        rgbpeckqo7cp: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        '9s6k4y4lxbsv': {
          label: {
            en: 'DRL',
            ar: 'DRL'
          },
          type: 'interaction'
        },
        ygh8bahl10yi: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        },
        '7p1u0cgedxiz': {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        '39rwrhudo7hr': {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        de5h612h79it: {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف'
          },
          type: 'interaction'
        },
        '3chny1l2b6r2': {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        nytakn7lnev4: {
          label: {
            en: 'DRL',
            ar: 'DRL'
          },
          type: 'interaction'
        },
        sbo7fpqvwzuh: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        },
        '8dvmfe5qick4': {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        bjq73d6ex5uw: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        '9n0dvsx9qfuz': {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        sx4la8gfy2lv: {
          label: {
            en: 'DRL',
            ar: 'DRL'
          },
          type: 'interaction'
        },
        '4pfn931mbalz': {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        },
        m34u55h0srjx: {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        '4igcs67fpy6q': {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        wvjqyjf609w7: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        byq3h8qvfcna: {
          label: {
            en: 'DRL',
            ar: 'DRL'
          },
          type: 'interaction'
        },
        ylgt9jtql60o: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        },
        sk4ftvul6hgw: {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        vlasgxry3r2e: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        zso5wardx2cd: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        hl5i6bzdfwt5: {
          label: {
            en: 'DRL',
            ar: 'DRL'
          },
          type: 'interaction'
        },
        ied6kma987aw: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        },
        ekb9gx2zypw8: {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        zywf2eknfbem: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        d188lhzd6pv5: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        abs0r5w715lo: {
          label: {
            en: 'DRL',
            ar: 'DRL'
          },
          type: 'interaction'
        },
        f2zs0kah65on: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        },
        '80v66cmn04mn': {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        gdp46rwbpnl4: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        jrqcidowfqam: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        u5q2l78gqsx3: {
          label: {
            en: 'DRL',
            ar: 'DRL'
          },
          type: 'interaction'
        },
        bb19do072c46: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        },
        xbdzchklx04d: {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        '9a4ewd0iqpwk': {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        bkr8amy2tvbs: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        '6146uzbd6kui': {
          label: {
            en: 'DRL',
            ar: 'DRL'
          },
          type: 'interaction'
        },
        iq7p37hq4g0j: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        },
        me1n6qt2sdto: {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        y06f4jy4mex5: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        b5a9285d5gby: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        zndj3hh8v5aq: {
          label: {
            en: 'DRL',
            ar: 'DRL'
          },
          type: 'interaction'
        },
        pcp7lp7oh1rc: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        }
      },
      hotspots: {
        exteriorCamera: {
          title: {
            en: 'Exterior',
            ar: 'الخارج'
          }
        },
        interiorCamera: {
          title: {
            en: 'First Row Seat',
            ar: 'مقعد الصف الأول'
          }
        },
        interiorCamera_1: {
          title: {
            en: 'Second Row Seat',
            ar: 'مقعد الصف الثاني'
          }
        }
      },
      name: {
        en: 'Chevrolet Silverado',
        ar: 'شفروليه سيلفرادو'
      },
      specifications: {
        heading: {
          en: 'Chevrolet Silverado',
          ar: 'شفروليه سيلفرادو'
        },
        trim: {
          en: '',
          ar: ''
        },
        price: {
          en: '',
          ar: ''
        },
        model: 'Chevrolet Silverado',
        seatingCapacity: 'Upto 6',
        dimensions: {
          length: '5915 mm',
          width: '',
          height: '',
          wheelbase: '3748 mm',
          groundClearance: ''
        },
        performance: [
          {
            value: '420',
            unit: 'HP',
            label: {
              en: '',
              ar: ''
            }
          },
          {
            value: '624',
            unit: 'NM',
            label: {
              en: 'Torque',
              ar: 'عزم الدوران'
            }
          },
          {
            value: '6.2L',
            unit: '',
            label: {
              en: 'V8 Engine',
              ar: 'V8 محرك'
            }
          }
        ],
        summary: [
          {
            title: {
              en: 'Model',
              ar: 'الطراز'
            },
            subtitle: {
              en: 'Chevrolet Silverado',
              ar: 'شفروليه سيلفرادو'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Exterior Colors',
              ar: 'الألوان الخارجية'
            },
            subtitle: {
              en: 'Summit White, Sterling Grey Metallic, Radiant Red Tintcoat, Black, Slate Gray Metallic, Red Hot, Riptide Blue, Iridescent Pearl Tricoat, Cypress Grey (As per selected trim)',
              ar: 'أبيض، رمادي معدني، أحمر داكن، أسود، رمادي أردوازي معدني، أحمر، أزرق معدني، لؤلؤي ثلاثي الطبقات، رمادي (بحسب الطراز المحدد)'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Interior',
              ar: 'المقصورة'
            },
            subtitle: {
              en: 'Jet Black Vinyl, Jet Black Cloth, Gideon Cloth, Gideon Leather, Jet Black and Greystone with Strike Yellow accent stitching leather trimmed, Jet Black Leather with exclusive custom perforation and stitching pattern (As per selected trim)',
              ar: 'فينيل باللون الأسود، قماش باللون الأسود، قماش باللون البني، جلد باللون البني، جلد باللونين الأسود والرمادي الداكن مع خياطة بارزة باللون الأصفر، جلد أسود بنمط ثقوب حصري وخياطة مميّزة (بحسب الطراز المحدد)'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Wheelbase',
              ar: 'قاعدة العجلات'
            },
            subtitle: {
              en: '3748 mm',
              ar: '3748 مم'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Length',
              ar: 'الطول'
            },
            subtitle: {
              en: '5915 mm',
              ar: '5915 مم'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Cargo Space',
              ar: 'مساحة التخزين'
            },
            subtitle: {
              en: 'Upto 2030.5 Liters',
              ar: 'حتى 2030.5 لترًا'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Transmission',
              ar: 'ناقل الحركة'
            },
            subtitle: {
              en: '8-speed Automatic / 10-speed Automatic',
              ar: 'ناقل حركة أوتوماتيكي بـ 8 سرعات / ناقل حركة أوتوماتيكي بـ 10 سرعات'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'OnStar Basics',
              ar: 'باقة OnStar الأساسية'
            },
            subtitle: {
              en: '8 Years',
              ar: '8 سنوات'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'OnStar One',
              ar: 'باقة OnStar One'
            },
            subtitle: {
              en: 'Upto 3 Years',
              ar: 'حتى 3 سنوات'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Seating Capacity',
              ar: 'سعة المقاعد'
            },
            subtitle: {
              en: 'Upto 6',
              ar: 'حتى 6'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Infotainment Screen',
              ar: 'شاشة المعلومات والترفيه'
            },
            subtitle: {
              en: '13.4" Premium Infotainment with Google built-in',
              ar: 'شاشة معلومات وترفيه متطورة مقاس 13.4 إنش مع خدمات جوجل المدمجة'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          }
        ]
      },
      accessoryCategories: {
        exterior: [
          'Alloy Wheels'
        ],
        interior: [],
        options: [
          'Options & Accessories',
          'Performance'
        ]
      }
    }
  },
  plugins: {
    deferredAssetLoading: true,
  },
  staticTranslations: {
    specDetails: {
      model: {
        en: 'Model',
        ar: 'نموذج',
      },
      exteriorColours: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interiorAmbience: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      seatingCapacity: {
        en: 'Seating Capacity',
        ar: 'سعة المقاعد',
      },
      length: {
        en: 'Length',
        ar: 'الطول',
      },
      width: {
        en: 'Width',
        ar: 'العرض',
      },
      height: {
        en: 'Height',
        ar: 'الارتفاع',
      },
      wheelbase: {
        en: 'Wheelbase',
        ar: 'قاعدة العجلات',
      },
      groundClearance: {
        en: 'Ground Clearance',
        ar: 'الخلوص الأرضي',
      },
      share: {
        en: 'Share',
        ar: 'يشارك',
      },
    },
    variantSelection: {
      title: {
        en: 'Model',
        ar: 'نموذج',
      },
      select: {
        en: 'Select',
        ar: 'تحديد',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      selectBodyStyle: { en: 'Select Body Style', ar: 'اختر نمط الهيكل' },
      availableTrims: { en: 'Select Trim', ar: 'اختر الفئة' },
      driveType: { en: 'Drive Type', ar: 'نوع القيادة' },
      model: { en: 'Model', ar: 'الموديل' },
    },
    loadingScreen: {
      default: {
        en: 'Preparing the canvas for your dream car. Just a few more seconds to go!',
        ar: 'لحظات ونبدأ رحلة تصميم سيارة أحلامك... باقي ثوانٍ قليلة!',
      },
      enterConfigurator: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      loading: { en: 'Loading', ar: 'جارٍ التحميل' },
      variantLoading: { en: 'Trim is loading', ar: 'نموذج جارٍ التحميل' },
      viewLoading: {
        en: 'Applying final touches',
        ar: 'تطبيق اللمسات النهائية',
      },
      disclaimer: {
        en: 'Disclaimer',
        ar: 'تنصل',
      },
      termsConditions: {
        en: 'Terms & Conditions',
        ar: 'الشروط والأحكام',
      },
      viewSpecDetails: {
        en: 'View Spec Details',
        ar: 'ععرض تفاصيل المواصفات',
      },
      disclaimerContent: {
        en: 'Prices are subject to change without prior notice and may vary based on location, availability, or other factors.',
        ar: 'الأسعار عرضة للتغيير دون إشعار مسبق وقد تختلف بناءً على الموقع والتوافر وعوامل أخرى.',
      },
      termsModal: {
        title: {
          en: 'Configurator Use Terms and Conditions',
          ar: 'شروط وأحكام استخدام المُكوِّن',
        },
        lead: {
          en: 'By using this vehicle configurator and submitting your build, you acknowledge and agree to the following:',
          ar: 'من خلال استخدام مُكوِّن تكوين السيارة هذا وإرسال تصميمك، فإنك تقر وتوافق على ما يلي:',
        },
        sections: {
          item1: {
            title: {
              en: 'Non-Binding Submission',
              ar: 'تقديم غير ملزم',
            },
            content: {
              en: 'Submitting your vehicle configuration does not constitute a formal vehicle order or purchase agreement. No payment or deposit is collected at this stage.',
              ar: 'إرسال مواصفات سيارتك لا يُعد طلبًا رسميًا للسيارة أو اتفاقية شراء. لا يتم تحصيل أي مبلغ أو دفعة مقدمة في هذه المرحلة.',
            },
          },
          item2: {
            title: {
              en: 'Preliminary Estimate',
              ar: 'تقدير أولي',
            },
            content: {
              en: 'All pricing displayed is Manufacturer’s Suggested Retail Price (MSRP)and is provided for informational purposes only. Final pricing, taxes, fees, and applicable offers will be confirmed by the authorized dealer.',
              ar: 'جميع الأسعار المعروضة هي سعر التجزئة المقترح من الشركة المصنعة، وهي لأغراض إعلامية فقط. سيتم تأكيد الأسعار النهائية والضرائب والرسوم والعروض المطبقة من قبل الوكيل المعتمد.',
            },
          },
          item3: {
            title: {
              en: 'Dealer Follow-Up',
              ar: 'متابعة التاجر',
            },
            content: {
              en: 'Your submitted configuration will be shared with a participating dealer, who will contact you to discuss your preferences, availability, and next steps.',
              ar: 'سيتم مشاركة التكوين الذي أرسلته مع أحد التجار المشاركين، والذي سيتصل بك لمناقشة تفضيلاتك وتوافرك والخطوات التالية.',
            },
          },
          item4: {
            title: {
              en: 'Availability and Fulfillment',
              ar: 'التوفر والوفاء',
            },
            content: {
              en: 'We will make every reasonable effort to fulfill your preferences; however, final vehicle and accessories selection is subject to dealer inventory and production availability. Configurations may be limited to in-stock or incoming vehicles.',
              ar: 'سنبذل قصارى جهدنا لتلبية تفضيلاتكم، إلا أن الاختيار النهائي للمركبات والملحقات يخضع لمخزون الوكيل وتوافر الإنتاج. قد تقتصر التكوينات على المركبات المتوفرة في المخزون أو الواردة.',
            },
          },
          item5: {
            title: {
              en: 'No Obligation',
              ar: 'لا يوجد التزام',
            },
            content: {
              en: 'You are under no obligation to purchase the configured vehicle, and submitting your configuration does not create a contractual relationship between you and the manufacturer or dealer.',
              ar: 'لا يوجد أي التزام عليك بشراء السيارة المخصصة، كما أن إرسال التكوين الخاص بك لا يؤدي إلى إنشاء علاقة تعاقدية بينك وبين الشركة المصنعة أو التاجر.',
            },
          },
          item6: {
            title: {
              en: 'Use of Personal Information and Consent',
              ar: 'استخدام المعلومات الشخصية والموافقة',
            },
            content: {
              en: 'By submitting your configuration, you consent to the collection, use, and sharing of your personal information with authorized dealers to facilitate your request, in accordance with our [Privacy Statement].',
              ar: 'من خلال إرسال التكوين الخاص بك، فإنك توافق على جمع معلوماتك الشخصية واستخدامها ومشاركتها مع التجار المعتمدين لتسهيل طلبك، وفقًا لـ [بيان الخصوصية] الخاص بنا.',
            },
          },
          item7: {
            title: {
              en: 'System Limitations',
              ar: 'قيود النظام',
            },
            content: {
              en: 'While we aim to provide accurate and current information, the configurator may not reflect all available features or up-to-date pricing. Errors or omissions may occur.',
              ar: 'مع أننا نهدف إلى توفير معلومات دقيقة وحديثة، إلا أن مُكوِّن الإعدادات قد لا يعكس جميع الميزات المتاحة أو الأسعار المُحدَّثة. قد تحدث أخطاء أو سهو.',
            },
          },
        },
      },
    },
    common: {
      exit: {
        en: 'Exit',
        ar: 'خروج',
      },
      close: {
        en: 'Close',
        ar: 'إغلاق',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      back: {
        en: 'Back',
        ar: 'خلف',
      },
      next: {
        en: 'Next',
        ar: 'التالي',
      },
      done: {
        en: 'Done',
        ar: 'تم',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      enterFullscreen: {
        en: 'Enter Fullscreen',
        ar: 'ملء الشاشة',
      },
      exitFullscreen: {
        en: 'Exit Fullscreen',
        ar: 'إلغاء ملء الشاشة',
      },
      selectTrim: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      viewConfiguration: {
        en: 'View Configuration',
        ar: 'عرض التكوين',
      },
      togglePanelTooltip: {
        en: 'Select Trim and Configuration',
        ar: 'اختر الفئة والتكوين',
      },
      nextStepTooltip: {
        en: 'Click to proceed to the next step',
        ar: 'انقر للانتقال إلى الخطوة التالية',
      },
      heading: {
        en: 'Standard Features',
        ar: 'المميزات  القياسية',
      },
      description: {
        en: 'Chevrolet Arabia',
        ar: 'شيفروليه العربية',
      },
      goBack: { en: 'Go Back', ar: 'العودة' },
      freeRoamView: { en: 'Free Roam View', ar: 'عرض التجوال الحر' },
      featureView: { en: 'Featured View', ar: 'عرض مميز' },
      configurator: { en: 'Configurator', ar: 'المُكوِّن' },
      buildYourRide: { en: 'Build Your Ride', ar: 'ابنِ سيارتك' },
      options: { en: 'Options', ar: 'الخيارات' },
      summary: { en: 'Summary', ar: 'الملخص' },
      saveYourBuild: { en: 'Save Your Build', ar: 'احفظ تكوينك' },
      startingPrice: { en: 'Starting Price', ar: 'السعر الابتدائي' },
      msrp: { en: 'MSRP', ar: 'سعر التجزئة المقترح' },
      proceed: { en: 'Proceed', ar: 'متابعة' },
    },
    tabs: {
      config: { en: 'Config', ar: 'الإعداد' },
      model: { en: 'Model', ar: 'الموديل' },
      exterior: { en: 'Exterior', ar: 'الخارجي' },
      interior: { en: 'Interior', ar: 'الداخلي' },
      options: { en: 'Options', ar: 'الخيارات' },
      summary: { en: 'Summary', ar: 'الملخص' },
    },
    cart: {
      title: {
        en: 'Your Order',
        ar: 'طلبك',
      },
      disclaimer: {
        en: 'Accessory prices and availability will be confirmed at the showroom.',
        ar: 'سيتم تأكيد أسعار الملحقات وتوافرها في صالة العرض.',
      },
      exteriorLabel: {
        en: 'Exterior:',
        ar: ':خارجي',
      },
      interiorLabel: {
        en: 'Interior:',
        ar: ':داخلي',
      },
      addedAccessories: {
        en: 'Added Accessories',
        ar: 'الإكسسوارات المضافة',
      },
      priceEstimate: {
        en: 'Price Estimate',
        ar: 'السعر التقديري',
      },
      priceBreakdown: {
        en: 'Price Breakdown',
        ar: 'تفاصيل السعر',
      },
      reserveNow: {
        en: 'Proceed',
        ar: 'متابعة',
      },
      removeAccessory: {
        en: 'Remove accessory',
        ar: 'إزالة الإكسسوار',
      },
      removeItem: { en: 'Remove', ar: 'إزالة' },
      packageCategory: { en: 'Package', ar: 'حزمة' },
    },
    saveDialog: {
      optionsTitle: {
        en: 'How would you like to proceed?',
        ar: 'كيف تريد المتابعة؟',
      },
      optionWhatsapp: { en: 'Save to WhatsApp', ar: 'حفظ على واتساب' },
      optionEmail: { en: 'Save to Email', ar: 'حفظ على البريد الإلكتروني' },
      optionTestDrive: {
        en: 'Book a Test Drive',
        ar: 'احجز اختبار قيادة',
      },
      optionCallback: {
        en: 'Request a call Back',
        ar: 'طلب معاودة الاتصال',
      },
      labelFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      labelLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      labelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      labelMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      labelPrefix: {
        en: 'Prefix',
        ar: 'البادئة',
      },
      labelModel: {
        en: 'Model',
        ar: 'نموذج',
      },
      labelDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      labelComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      labelTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      labelTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      placeholderTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      placeholderLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      placeholderEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      placeholderMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      placeholderComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      placeholderSelectDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      privacyTitle: {
        en: 'Privacy statement & legal disclaimer',
        ar: 'بيان الخصوصية وإخلاء المسؤولية القانونية',
      },
      privacyText: {
        en: 'I acknowledge and understand that my information will be shared with General Motors, its affiliates, dealer network or other parties are required by law for compliance, safety campaigns, government inquiries, or similar legal process. My information may also be shared for product research and development purposes, and to manage customer relationships to provide support. GM’s Privacy Statement addresses how GM handles the personal information shared with us.',
        ar: 'أقر وأتفهم بأنة سيتم مشاركة معلوماتي مع شركة جنرال موتورز، والشركات التابعة لها، وشبكة الوكلاء أو الأطراف الأخرى بموجب القانون للامتثال وحملات السلامة واستفسارات الحكومية وأي إجراء قانوني مماثل. وقد تتم مشاركة معلوماتي أيضًا لأغراض البحث وتطوير المنتجات ، وإدارة علاقة العملاء لتوفير الدعم اللازم. يوضح بيان خصوصية جنرال موتورز على موقعنا كيفية تعامل جنرال موتورز مع المعلومات الشخصية التي تمت مشاركتها معنا.',
      },
      privacyReadMore: {
        en: 'Read more',
        ar: 'اقرأ المزيد',
      },
      privacyMarketingConsent: {
        en: 'I wish to receive any marketing information or have my information shared with third parties for purposes of providing me with marketing information.',
        ar: 'أرغب في تلقي معلومات تسويقية أو مشاركة معلوماتي مع جهات خارجية لغاية تزويدي بمعلومات تسويقية',
      },
      buttonSend: {
        en: 'Send Configuration',
        ar: 'إرسال التكوين',
      },
      buttonSending: {
        en: 'Submitting...',
        ar: 'تقديم...',
      },
      buttonSent: {
        en: 'Sent!',
        ar: 'مرسل!',
      },
      buttonRetry: {
        en: 'Retry',
        ar: 'إعادة المحاولة',
      },
      buttonClose: {
        en: 'Okay, Got it',
        ar: 'حسنًا، فهمت',
      },
      successTitle: {
        en: 'Saved',
        ar: 'محفوظ',
      },
      successLabelModel: {
        en: 'Model Name',
        ar: 'اسم النموذج',
      },
      successLabelVariant: {
        en: 'Variant Name',
        ar: 'اسم البديل',
      },
      successLabelName: {
        en: 'Name',
        ar: 'اسم',
      },
      successLabelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      successLabelMobile: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      qrTitle: {
        en: 'Your Unique Configuration QR',
        ar: 'رمز الاستجابة السريعة لتكوينك',
      },
      qrDescription: {
        en: 'Show this QR code to your nearest dealer to access your saved configuration.',
        ar: 'أظهر رمز الاستجابة السريعة هذا لتاجر التجزئة الأقرب للوصول إلى التكوين المحفوظ.',
      },
      qrNoteLabel: {
        en: 'Note:',
        ar: 'ملاحظة:',
      },
      qrNote: {
        en: 'Only authorized dealers can view this configuration.',
        ar: 'يمكن للوكلاء المعتمدين فقط عرض هذا التكوين.',
      },
      qrDownload: {
        en: 'Download QR',
        ar: 'تحميل QR',
      },
      qrSave: {
        en: 'Save QR',
        ar: 'حفظ QR',
      },
      errorTitle: {
        en: 'Submission Failed',
        ar: 'فشل التقديم',
      },
      errorMessage: {
        en: 'We encountered an error, please try again.',
        ar: 'واجهنا خطأ، يرجى المحاولة مرة أخرى.',
      },
    },
    featureGroups: {
      exteriorDesign: {
        en: 'Exterior Design & Styling',
        ar: 'التصميم الخارجي والتصميم',
      },
      interiorComfort: {
        en: 'Interior Comfort & Features',
        ar: 'الراحة والميزات الداخلية',
      },
      technologyConvenience: {
        en: 'Technology & Convenience',
        ar: 'التكنولوجيا والراحة',
      },
      safetyAssistance: {
        en: 'Safety & Driver Assistance',
        ar: 'السلامة ومساعدة السائق',
      },
    },
    sidePanel: {
      menu: { en: 'Main Menu', ar: 'القائمة الرئيسية' },
      search: { en: 'Search', ar: 'بحث' },
      settings: { en: 'Settings', ar: 'الإعدادات' },
      profile: { en: 'Profile', ar: 'الملف الشخصي' },
      notifications: { en: 'Notifications', ar: 'الإشعارات' },
      help: { en: 'Help & Support', ar: 'المساعدة والدعم' },
      filter: { en: 'Filter Options', ar: 'خيارات التصفية' },
      favorites: { en: 'Favorites', ar: 'المفضلة' },
      history: { en: 'History', ar: 'التاريخ' },
      tools: { en: 'Tools', ar: 'الأدوات' },
      variants: { en: 'Variants', ar: 'الأنواع' },
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'ألوان المقصورة' },
      features: { en: 'Features', ar: 'الميزات' },
      step: { en: 'Step', ar: 'خطوة' },
      openPanel: { en: 'Open Panel', ar: 'فتح اللوحة' },
      closePanel: { en: 'Close Panel', ar: 'إغلاق اللوحة' },
    },
    landscape: {
      message: {
        en: 'Please rotate your device to portrait mode',
        ar: 'يرجى تدوير جهازك إلى الوضع الرأسي',
      },
      subtitle: {
        en: 'For the best experience, please turn your device upright.',
        ar: 'للحصول على أفضل تجربة، يرجى إمساك جهازك بشكل رأسي.',
      },
    },
    cameraViewToggle: {
      ext: { en: 'Ext', ar: 'خارجي' },
      int: { en: 'Int', ar: 'داخلي' },
      dualTone: { en: 'Dual-tone', ar: 'لون مزدوج' },
    },
    bottomDrawer: {
      variants: { en: 'Variants', ar: 'الأنواع' },
      interactions: { en: 'Interactions', ar: 'التفاعلات' },
      accessories: { en: 'Options & Accessories', ar: 'الإكسسوارات' },
      features: { en: 'Features', ar: 'الميزات' },
      highlights: { en: 'Highlights', ar: 'الميزات البارزة' },
      moreOptions: { en: 'More Options', ar: 'المزيد من الخيارات' },
      colorSelector: { en: 'Color Selector', ar: 'محدد اللون' },
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'لون المقصورة' },
      wheels: { en: 'Wheels', ar: 'العجلات' },
      roofs: { en: 'Roofs', ar: 'الأسقف' },
      performance: { en: 'Performance', ar: 'الأداء' },
      packages: { en: 'Packages', ar: 'الحزم' },
      chargingOptions: { en: 'Charging Options', ar: 'خيارات الشحن' },
      safetyServices: { en: 'Safety & Services', ar: 'السلامة والخدمات' },
      expand: { en: 'Expand', ar: 'توسيع' },
      minimize: { en: 'Minimize', ar: 'تصغير' },
      freeRoamView: { en: 'Free Roam View', ar: 'عرض التجوال الحر' },
      featureView: { en: 'Featured View', ar: 'العرض المميز' },
    },
    qr: {
      scan: { en: 'Scan QR', ar: 'امسح رمز الاستجابة السريعة' },
      scanWithPhone: {
        en: 'Scan with your phone to view in your Space',
        ar: 'امسح باستخدام هاتفك لعرضه في مساحتك',
      },
    },
    share: {
      title: { en: 'Share Configuration', ar: 'مشاركة التكوين' },
      description: {
        en: 'Share your vehicle configuration with others',
        ar: 'شارك تكوين سيارتك مع الآخرين',
      },
      twitterTitle: {
        en: 'Check out my Chevrolet Silverado configuration!',
        ar: 'شاهد تكوين سيارتي Chevrolet Silverado!',
      },
      whatsappTitle: {
        en: 'Check out my Chevrolet Silverado configuration!',
        ar: 'شاهد تكوين سيارتي Chevrolet Silverado!',
      },
      linkedinTitle: {
        en: 'My Chevrolet Silverado Configuration',
        ar: 'تكويني لسيارة Chevrolet Silverado',
      },
      linkedinSummary: {
        en: 'Check out my custom Chevrolet Silverado configuration!',
        ar: 'شاهد تكويني المخصص لسيارة Chevrolet Silverado!',
      },
      emailSubject: {
        en: 'Check out my Chevrolet Silverado Configuration',
        ar: 'شاهد تكوين سيارتي Chevrolet Silverado',
      },
      emailBody: {
        en: 'I configured my dream Chevrolet Silverado. Take a look:',
        ar: 'قمت بتكوين سيارة Chevrolet Silverado المثالية. ألق نظرة:',
      },
      linkLabel: { en: 'Or copy link:', ar: 'أو انسخ الرابط:' },
      copied: {
        en: 'Copied',
        ar: 'تم نسخ الرابط إلى الحافظة!',
      },
      copy: { en: 'Copy', ar: 'نسخ' },
      saveAndShare: { en: 'Save & Share', ar: 'حفظ ومشاركة' },
      copyLink: { en: 'Copy link', ar: 'نسخ الرابط' },
      facebook: { en: 'Facebook', ar: 'Facebook' },
      twitter: { en: 'Twitter', ar: 'Twitter' },
      gmail: { en: 'Gmail', ar: 'Gmail' },
      whatsapp: { en: 'Whatsapp', ar: 'Whatsapp' },
    },
    topBar: {
      matchingVehiclesFound: {
        en: 'Matching Vehicles Found',
        ar: 'تم العثور على المركبات المطابقة',
      },
    },
    bottomBar: {
      priceLabel: { en: 'ex showroom price', ar: 'سعر صالة العرض' },
      leads: { en: 'Save', ar: 'حفظ' },
      findLocalMatches: {
        en: 'Find Local Matches',
        ar: 'اعثر على التطابقات المحلية',
      },
      save: { en: 'Save', ar: 'حفظ' },
      item: { en: 'Item', ar: 'العنصر' },
      items: { en: 'Items', ar: 'عناصر' },
      totalToPay: { en: 'Total to pay', ar: 'الإجمالي للدفع' },
      bookTestDrive: { en: 'Save', ar: 'حفظ' },
      requestQuote: { en: 'Request a Quote', ar: 'طلب عرض سعر' },
      downloadBrochure: { en: 'Download Brochure', ar: 'تحميل الكتيب' },
    },
    priceSummary: {
      title: { en: 'Price Summary', ar: 'ملخص السعر' },
      enterLocation: { en: 'Enter Location', ar: 'أدخل الموقع' },
      selectCountry: { en: 'Location', ar: 'الموقع' },
      selectCity: { en: 'Location', ar: 'الموقع' },
      note: {
        en: 'Note : Prices might vary by state and all prices listed are on road price, T&C apply.',
        ar: 'ملاحظة: قد تختلف الأسعار حسب الولاية وجميع الأسعار المذكورة هي الأسعار على الطريق، تطبق الشروط والأحكام.',
      },
      breakdown: { en: 'Price Breakdown', ar: 'تفصيل السعر' },
      color: { en: 'Color', ar: 'اللون' },
      variant: { en: 'Variant', ar: 'الفئة' },
      included: { en: 'Included', ar: 'مشمول' },
      total: { en: 'Total', ar: 'الإجمالي' },
    },
    featureDialog: {
      playPause: { en: 'Play-Pause', ar: 'تشغيل-إيقاف' },
    },
    selectDealership: {
      title: { en: 'Select a Dealer', ar: 'اختر وكيلاً' },
      subtitle: {
        en: 'Please select a dealer to find local matches similar to your configuration',
        ar: 'يرجى اختيار وكيل للعثور على سيارات مطابقة لتكوينك في منطقتك',
      },
      city: { en: 'UAE', ar: 'الإمارات' },
      dealersIn: { en: 'Dealers in', ar: 'وكلاء في' },
      dealersInCity: { en: 'Dealers in {{city}}', ar: 'وكلاء في {{city}}' },
      availableInLocations: {
        en: 'Available in {{count}} locations of {{city}}',
        ar: 'متوفرة في {{count}} مواقع في {{city}}',
      },
      proceed: {
        en: 'Select Dealer and Proceed to Configurator',
        ar: 'اختر الوكيل وتابع إلى المتكون',
      },
    },
    countryNames: {
      UAE: { en: 'UAE', ar: 'الإمارات' },
      KSA: { en: 'KSA', ar: 'السعودية' },
      KW: { en: 'Kuwait', ar: 'الكويت' },
      default: { en: 'UAE', ar: 'الإمارات' },
    },
    localMatches: {
      exterior: { en: 'Exterior', ar: 'خارجي' },
      interior: { en: 'Interior', ar: 'داخلي' },
      dualTone: { en: 'Dual-tone', ar: 'لون مزدوج' },
      yourConfiguration: { en: 'Current Configuration', ar: 'التكوين الحالي' },
      viewConfiguration: {
        en: 'View your configuration details',
        ar: 'عرض تفاصيل تكوينك',
      },
      yourBuildDetails: { en: 'Your Build Details', ar: 'تفاصيل بنائك' },
      options: { en: 'Options', ar: 'الخيارات' },
      save: { en: 'Save', ar: 'حفظ' },
      dealersIn: { en: 'Dealers in', ar: 'وكلاء في' },
      changeDealer: { en: 'Change Dealer', ar: 'تغيير الوكيل' },
      carsAvailable: {
        en: 'Cars Available at the Selected Dealership',
        ar: 'السيارات المتوفرة لدى الوكيل المختار',
      },
      matchesFound: { en: 'Matches found', ar: 'تطابق تم العثور عليه' },
      startingFrom: { en: 'Starting from', ar: 'يبدأ من' },
      getQuote: { en: 'Proceed', ar: 'احصل على عرض سعر' },
      viewDifferences: { en: 'View Differences', ar: 'عرض الاختلافات' },
      match: { en: 'Match', ar: 'تطابق' },
      closestMatchFound: {
        en: 'Closest Match Found',
        ar: 'أقرب تطابق تم العثور عليه',
      },
      buildDetails: { en: 'Build Details', ar: 'تفاصيل التكوين' },
      trim: { en: 'Trim', ar: 'الفئة' },
      matchScoreTooltip: {
        en: 'This score indicates how closely this vehicle matches your configuration.',
        ar: 'يشير هذا النتيجة إلى مدى تطابق هذه السيارة مع تكوينك.',
      },
      exactMatch: { en: 'Exact Match', ar: 'تطابق تام' },
      similarMatch: { en: 'Close Match', ar: 'تطابق قريب' },
      availableNow: { en: 'Available Now', ar: 'متوفر الآن' },
      whatYouConfigured: { en: 'What you configured', ar: 'ما قمت بتكوينه' },
      matchingInStock: { en: 'Matching vehicle available in stock', ar: 'مركبة مطابقة متوفرة في المخزون' },
      noMatches: {
        en: 'No matching vehicles found in your area.',
        ar: 'لم يتم العثور على سيارات مطابقة في منطقتك.',
      },
      closeMatchDetails: { en: 'Close Match Details', ar: 'تفاصيل التطابق القريب' },
      matchesYourSelection: { en: 'Matches your selection', ar: 'يطابق اختيارك' },
      sameAsWhatYouSelected: { en: 'Same as what you selected', ar: 'نفس ما اخترته' },
      replacesYourSelection: { en: 'Replaces your selection', ar: 'يستبدل اختيارك' },
      selectionNotIncluded: { en: 'Selection not included', ar: 'الاختيار غير مشمول' },
      includedInThisVehicle: { en: 'Included in this vehicle', ar: 'مشمول في هذه المركبة' },
      additionalFeatureAlreadyIncluded: { en: 'Additional feature already included', ar: 'ميزة إضافية مشمولة بالفعل' },
      stockDisclaimer: { en: 'Available stock may include extra features not selected in your original configuration.', ar: 'قد تشمل المركبات المتاحة ميزات إضافية لم تختارها في تكوينك الأصلي.' },
      additionalInstalledOptionsLegend: { en: 'Included in this vehicle', ar: 'مشمول في هذه المركبة' },
      vehicleDetails: { en: 'Vehicle Details', ar: 'تفاصيل المركبة' },
      installedOptionsTitle: { en: 'Comes with vehicle', ar: 'يأتي مع المركبة' },
      additionalInstalledOptionsSubtitle: { en: 'Additional Installed Options:', ar: 'الخيارات المركبة الإضافية:' },
      specTrim: { en: 'Trim', ar: 'الفئة' },
      specExteriorColor: { en: 'Exterior Color', ar: 'اللون الخارجي' },
      specInteriorColor: { en: 'Interior Color', ar: 'اللون الداخلي' },
      specWheels: { en: 'Wheels', ar: 'العجلات' },
      specPackage: { en: 'Package', ar: 'الحزمة' },
      title: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
      message: {
        en: 'We Found a few local matches similar to your current configuration. Click to view Matches',
        ar: 'وجدنا بعض التطابقات المحلية المشابهة لتكوينك الحالي. انقر لعرض التطابقات',
      },
    },
    trim: {
      selection: { en: 'Trim Selection', ar: 'اختيار الفئة' },
    },
    paymentPage: {
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'لون المقصورة' },
      accessory: { en: 'Accessory', ar: 'ملحق' },
      closePayment: { en: 'Close', ar: 'إغلاق' },
    },
    packageModal: {
      includes: { en: 'Includes:', ar: 'يشمل:' },
      of: { en: 'of', ar: 'من' },
      totalPriceChange: {
        en: 'Total Price Change:',
        ar: 'إجمالي تغيير السعر:',
      },
      item: { en: 'Item', ar: 'غرض' },
      items: { en: 'Items', ar: 'أغراض' },
      addPackage: { en: 'Add Package', ar: 'إضافة الحزمة' },
      noThanks: { en: 'No Thanks', ar: 'لا شكراً' },
      alreadyApplied: { en: 'Already Applied', ar: 'مُطبّق بالفعل' },
    },
    changesRequired: { en: 'Changes Required', ar: 'التغييرات المطلوبة' },
    changesRequiredDescription: {
      en: 'The package you selected conflicts with your current selections. Please review the changes below.',
      ar: 'الحزمة التي اخترتها تتعارض مع اختياراتك الحالية. يرجى مراجعة التغييرات أدناه.',
    },
    itemsThatWillBeRemoved: {
      en: 'Items that will be removed',
      ar: 'العناصر التي سيتم إزالتها',
    },
    itemsThatWillBeAdded: {
      en: 'Items that will be added',
      ar: 'العناصر التي سيتم إضافتها',
    },
    applyChanges: { en: 'Apply Changes', ar: 'تطبيق التغييرات' },
    previewFeature: { en: 'Preview Feature', ar: 'معاينة الميزة' },
    exploreFeatures: { en: 'Explore Features', ar: 'استكشف الميزات' },
    buildAndPrice: { en: 'Build & Price', ar: 'البناء والسعر' },
    features: {
      preview: { en: 'Preview Feature', ar: 'معاينة المميزات' },
      playAll: { en: 'PLAY ALL', ar: 'تشغيل الكل' },
      title: { en: 'Features', ar: 'المميزات' },
    },
    header: {
      localMatches: { en: 'Local\nMatches', ar: 'التطابقات\nالمحلية' },
      matchingVehicles: { en: 'Matching vehicles', ar: 'مركبات مطابقة' },
      configurationsFound: { en: 'Configurations Found', ar: 'تكوينات تم العثور عليها' },
    },
    configPanel: {
      playAll: { en: 'Play All', ar: 'تشغيل الكل' },
      pause: { en: 'Pause', ar: 'إيقاف مؤقت' },
      exteriorColors: { en: 'Exterior Colors', ar: 'ألوان الهيكل الخارجي' },
      interiorColors: { en: 'Interior Colors', ar: 'ألوان الداخلية' },
      roofOptions: { en: 'Roof Options', ar: 'خيارات السقف' },
      interiorOptions: { en: 'Interior Options', ar: 'خيارات الداخلية' },
      exteriorAccessories: { en: 'Exterior Accessories', ar: 'إكسسوارات خارجية' },
      noAdditionalOptions: { en: 'No additional options available for the selected trim.', ar: 'لا توجد خيارات إضافية متاحة للفئة المختارة.' },
      noFilters: { en: 'No filters available.', ar: 'لا توجد فلاتر متاحة.' },
      noVariantsMatch: { en: 'No trims match the selected filters.', ar: 'لا توجد فئات تطابق الفلاتر المختارة.' },
      configurationBreakdown: { en: 'Configuration Breakdown', ar: 'تفاصيل التكوين' },
      yourConfiguration: { en: 'Your Configuration', ar: 'تكوينك' },
      trim: { en: 'Trim', ar: 'الفئة' },
      total: { en: 'Total', ar: 'الإجمالي' },
    },
    conflictDialog: {
      description: { en: 'In order to apply this, the following item(s) will need to be removed due to compatibility constraints. Please review the item(s) below and proceed with your change.', ar: 'لتطبيق هذا، يجب إزالة العناصر التالية بسبب قيود التوافق. يرجى مراجعة العناصر أدناه والمتابعة بتغييرك.' },
    },
    viewport: {
      changeBackground: { en: 'Change Background', ar: 'تغيير الخلفية' },
      selectLanguage: { en: 'Switch Language (عربي)', ar: 'تبديل اللغة (EN)' },
      menu: { en: 'Menu', ar: 'القائمة' },
      exploreFeatures: { en: 'Explore Features', ar: 'استكشف الميزات' },
    },
  },
  dealers: {
    UAE: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
    KSA: [
      {
        id: 0,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307261',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Aziziyah Madina Road, Jeddah',
          ar: 'الجميح للسيارات - العزيزية طريق المدينة، جدة',
        },
      },
      {
        id: 1,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164034',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Hail City King Abdullah Road. Hail',
          ar: 'الجميح للسيارات - مدينة حائل طريق الملك عبدالله. يشيد',
        },
      },
      {
        id: 2,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164035',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Alshlhoub King Abdullah Road, Al Jouf',
          ar: 'الجميح للسيارات - الشلهوب طريق الملك عبدالله، الجوف',
        },
      },
      {
        id: 3,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307271',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Qassim King Abdulaziz Road, Buraidah',
          ar: 'الجميح للسيارات - القصيم طريق الملك عبد العزيز، بريدة',
        },
      },
      {
        id: 4,
        country: 'KSA',
        value: 'KSA',
        dealerId: '260921',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Jededah Road, Arar',
          ar: 'الجميح للسيارات - طريق الجديدة، عرعر',
        },
      },
    ],
    KW: [
      {
        id: 0,
        country: 'KW',
        value: 'KW',
        dealerId: '122620',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Al Rai 4th Ring Road, Al Rai',
          ar: 'الامانة للسيارات - الري الطريق الدائري الرابع، الري',
        },
      },
      {
        id: 1,
        country: 'KW',
        value: 'KW',
        dealerId: '174979',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Fahaheel Industrial Area Dabbous Street, Fahaheel',
          ar: 'الامانة للسيارات - الفحيحيل الصناعية شارع الدبوس، الفحيحيل',
        },
      },
    ],
    BHD: [
      {
        id: 0,
        country: 'BHD',
        value: 'BHD',
        dealerId: '122215',
        dealerName: 'National Motor Company W.L.L',
        label: {
          en: 'National Motor Company W.L.L, Al Qarya 475 Shk. Jaber Al Subah Hwy',
          ar: 'الشركة الوطنية للسيارات ذ.م.م، القرية 475 شارع الشيخ. طريق جابر الصباح',
        },
      },
    ],
    IQD: [
      {
        id: 0,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295876',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Al-Karada Sadoun Street',
          ar: 'شركة ماك الدولية (منطقة حرة)، شارع الكرادة سعدون',
        },
      },
      {
        id: 1,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295879',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Kurdistan District 100 m Street',
          ar: 'ماك إنترناشونال إف زي إي، منطقة كردستان، شارع ١٠٠ متر',
        },
      },
    ],
    LBP: [
      {
        id: 0,
        country: 'LBP',
        value: 'LBP',
        dealerId: '122226',
        dealerName: 'IMPEX',
        label: {
          en: 'IMPEX -  Badaro, Near the National Museum , Beirut',
          ar: 'إمبكس - بدارو، بالقرب من المتحف الوطني، بيروت',
        },
      },
    ],
    QAR: [
      {
        id: 0,
        country: 'QAR',
        value: 'QAR',
        dealerId: '274766',
        dealerName: 'Jaidah',
        label: {
          en: 'Jaidah Square Showroom - Al Matar Street Umm Ghuwallina, Doha',
          ar: 'صالة عرض "جايدة سكوير" - شارع المطار، أم غويلينة، الدوحة',
        },
      },
    ],
    OMR: [
      {
        id: 0,
        country: 'OMR',
        value: 'OMR',
        dealerId: '122219',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Ghalla Al Maardh Street',
          ar: 'مؤسسة عمان التجارية ش.م.م، شارع غلا المعرض',
        },
      },
      {
        id: 1,
        country: 'OMR',
        value: 'OMR',
        dealerId: '234217',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Al Farq Street',
          ar: 'مؤسسة عمان التجارية ذ.م.م، شارع الفرق',
        },
      },
      {
        id: 2,
        country: 'OMR',
        value: 'OMR',
        dealerId: '260896',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Hail Al Shumaliah Street',
          ar: 'مؤسسة عمان التجارية ذ.م.م، شارع حيل الشمالية',
        },
      },
    ],
    JOD: [
      {
        id: 0,
        country: 'JOD',
        value: 'JOD',
        dealerId: '189129',
        dealerName: 'Leading Vehicles',
        label: {
          en: 'The Leading Vehicles Co. Ltd, Al Rawabi',
          ar: 'شركة المركبات الرائدة المحدودة، الروابي',
        },
      },
    ],
    default: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
  },
  featureFlags: {
    showPriceInPresentation: false,
    showLogo: true,
    showSpecDetailsButton: true,
    useChevroletFont: true,
    showLocalMatches: true,
    showDisclaimerInPresentation: false,
    // Per-item pricing (card / MODEL / SUMMARY price lines, colours in the total)
    // is opt-in; this project keeps the header-only pricing it has always had.
    showPrices: true, // per-item prices on cards + summary breakdown, like Corvette
    showDisplayCarPrice: true,
    showCart: false
  },
  config: {
    // Display currency + USD conversion factor per region, keyed by the
    // `country` URL parameter (local = usd * fx). Prices were re-derived
    // from the AED figure: usd = AED / 3.68, then usd * fx per country.
    regions: {
      KSA: { label: 'KSA', currency: 'SAR', fx: 3.75 },
      UAE: { label: 'UAE', currency: 'AED', fx: 3.68 },
      OMR: { label: 'Oman', currency: 'OMR', fx: 0.39 },
      BHD: { label: 'Bahrain', currency: 'BHD', fx: 0.38 },
      QAR: { label: 'Qatar', currency: 'QAR', fx: 3.64 },
      JOD: { label: 'Jordan', currency: 'JOD', fx: 0.71 },
      KW: { label: 'Kuwait', currency: 'KWD', fx: 0.31 },
      LBP: { label: 'Lebanon', currency: 'USD', fx: 1 }, // GM quotes Lebanon in USD
      IQD: { label: 'Iraq', currency: 'USD', fx: 1 }, // priced in USD, like Lebanon
      default: { label: 'UAE', currency: 'AED', fx: 3.68 },
    },
    // Lead-side model name — 1.0's `config.model`, which is a lead label
    // distinct from 2.0's `config.model` (the models[] key).
    leadModel: 'silverado ld',
    // Exact model name for the lead file's Model column.
    leadModelName: 'Silverado LD',
    // Dial code (prefixed onto mobile_number) and Salesforce lead country
    // code (metadome_country_code), keyed by the `country` URL parameter.
    countryCodes: {
      UAE: {
        code: '+971',
        leadCode: 'AE',
      },
      KSA: {
        code: '+966',
        leadCode: 'SA',
      },
      KW: {
        code: '+965',
        leadCode: 'KW',
      },
      BHD: {
        code: '+973',
        leadCode: 'BH',
      },
      IQD: {
        code: '+964',
        leadCode: 'IQ',
      },
      LBP: {
        code: '+961',
        leadCode: 'LB',
      },
      OMR: {
        code: '+968',
        leadCode: 'OM',
      },
      QAR: {
        code: '+974',
        leadCode: 'QA',
      },
      JOD: {
        code: '+962',
        leadCode: 'JO',
      },
      default: {
        code: '+971',
        leadCode: 'AE',
      },
    },
    model: 'Silverado',
    modelName: 'Silverado',
    pageTitle: 'Silverado Configurator',
    client: 'GM',
    brand: 'chevrolet',
    modelId: 'CHSILT1MCM',
    configurationId: 'l25zrwex1u93',
    externalAnalytics: {
      vehicleType: 'truck',
      brand: 'chevrolet',
      provider: 'adobe',
      modelName: 'silverado ld'
    },
    presentation: {
      video: 'videos/2371/presentation_video_1.webm',
      loadingImage: 'images/2371/sliderImage.webp',
      transitionImage: 'images/2371/transition_logo.png',
      headerLogoKeepColor: true,
      keyHighlights: [
        {
          index: 0,
          show: true,
          title: {
            en: '',
            ar: ''
          },
          value: {
            en: '420 HP',
            ar: '420 حصان'
          },
          unit: ''
        },
        {
          index: 1,
          show: true,
          title: {
            en: 'Torque',
            ar: 'عزم الدوران'
          },
          value: {
            en: '624 NM',
            ar: '624 نيوتن متر'
          },
          unit: ''
        },
        {
          index: 2,
          show: true,
          title: {
            en: 'V8 Engine',
            ar: 'V8 محرك'
          },
          value: {
            en: '6.2L',
            ar: '6.2 لتر'
          },
          unit: ''
        }
      ]
    }
  },
  saveFormConfig: {
    fields: [
      {
        id: 'firstName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelFirstName',
        placeholderKey: 'saveDialog.placeholderFirstName',
      },
      {
        id: 'lastName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelLastName',
        placeholderKey: 'saveDialog.placeholderLastName',
      },
      {
        id: 'email',
        type: 'email',
        required: true,
        labelKey: 'saveDialog.labelEmail',
        placeholderKey: 'saveDialog.placeholderEmail',
        visibility: ['email', 'test-drive', 'callback'],
      },
      {
        id: 'phone',
        type: 'phone',
        required: true,
        labelKey: 'saveDialog.labelMobileNumber',
        placeholderKey: 'saveDialog.placeholderMobileNumber',
        visibility: ['whatsapp', 'test-drive', 'callback'],
      },
      {
        id: 'model',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelModel',
        placeholderKey: 'saveDialog.labelModel',
        optionsSource: 'models',
        visibility: ['test-drive', 'callback'],
      },
      {
        id: 'dealer',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelDealership',
        placeholderKey: 'saveDialog.placeholderSelectDealership',
        optionsSource: 'dealers',
        visibility: [
          'test-drive',
          'callback',
          'whatsapp',
          'email',
        ],
        // 1.0's save forms show the dealership dropdown but do not gate
        // submission on it; only the booking forms require it.
        requiredFor: [
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'testDriveDate',
        type: 'date',
        required: true,
        labelKey: 'saveDialog.labelTestDriveDate',
        placeholderKey: 'saveDialog.placeholderTestDriveDate',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'testDriveTime',
        type: 'time',
        required: true,
        labelKey: 'saveDialog.labelTestDriveTime',
        placeholderKey: 'saveDialog.placeholderTestDriveTime',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'comments',
        type: 'textarea',
        required: false,
        labelKey: 'saveDialog.labelComments',
        placeholderKey: 'saveDialog.placeholderComments',
        visibility: ['test-drive', 'callback'],
      },
    ],
  },
  externalScript: {
    dev: {
      head: {
        links: [
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true
          },
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-bea0833819b4-development.min.js',
            iframe: true,
            standalone: true
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true
          }
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true
          }
        ]
      },
      body: {
        snippets: [
          {
            snippet: '<script type="text/javascript">_satellite.pageBottom();</script>',
            iframe: true,
            standalone: true,
            async: false
          }
        ]
      }
    },
    staging: {
      head: {
        links: [
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true
          },
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
            async: false
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true
          }
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true
          }
        ]
      },
      body: {
        snippets: [
          {
            snippet: '<script type="text/javascript">_satellite.pageBottom();</script>',
            iframe: true,
            standalone: true,
            async: false
          }
        ]
      }
    },
    main: {
      head: {
        links: [
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true
          },
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
            async: false
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true
          }
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true
          }
        ]
      },
      body: {
        snippets: [
          {
            snippet: '<script type="text/javascript">_satellite.pageBottom();</script>',
            iframe: true,
            standalone: true,
            async: false
          }
        ]
      }
    }
  },
  filterOptions: [
    {
      id: 'variant',
      // 1.0's own Cab Type filter uses id:'variant' as just the filter-group's
      // own identifier - the actual field it matches on each variant is named
      // by `parent: 'engine'` on every one of its options (metadata-2371.ts,
      // ~line 73065), not `id`/`key`. The migration copied key:'variant' as if
      // it were that field name, but no variant has ever had a field literally
      // called `variant`, so this filter silently matched everything.
      key: 'engine',
      label: {
        en: 'Cab Type',
        ar: ''
      },
      disabled: false,
      type: 'chip',
      options: [
        {
          id: 'Crew Cab',
          label: {
            en: 'Crew Cab',
            ar: 'طاقم الكابينة'
          },
          value: 'Crew Cab',
          checked: false
        },
        {
          id: 'Regular Cab',
          label: {
            en: 'Regular Cab',
            ar: 'الكابينة العادية'
          },
          value: 'Regular Cab',
          checked: false
        }
      ]
    },
    {
      id: 'modelyear',
      key: 'modelyear',
      label: {
        en: 'Model Year',
        ar: 'سنة الطراز'
      },
      disabled: false,
      type: 'chip',
      options: [
        {
          id: '2025',
          label: {
            en: '2025',
            ar: '2025'
          },
          value: '2025',
          checked: false
        },
        {
          id: '2026',
          label: {
            en: '2026',
            ar: '2026'
          },
          value: '2026',
          checked: true
        }
      ]
    }
  ],
  styleConfig: {
    cssVariables: {
      '--default-font-family': 'Chevy Sans',
      '--bold-font-family': 'Chevy Sans Medium',
    },
  },
} as unknown as UIMetadataComplete;
